import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { supabase } from './supabase';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ComposedChart, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Treemap
} from "recharts";
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ─── GLOBAL STYLES ───────────────────────────────────────────────────────────
const GLOBAL_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  :root {
    --color-bg-base: #F0F2F7;
    --color-bg-card: #FFFFFF;
    --color-bg-sidebar: #0F1117;
    --color-bg-hover: #F0F4F9;
    --color-bg-subtle: #F5F7FC;
    --color-bg-input: #F9FAFB;
    --color-border: #E4E8F0;
    --color-border-subtle: #EEF1F7;
    --color-text-primary: #0F1117;
    --color-text-secondary: #5A6478;
    --color-text-tertiary: #9BA5B8;
    --color-sidebar-text: rgba(255,255,255,0.55);
    --color-sidebar-active: #FFFFFF;
    --color-sidebar-active-bg: rgba(255,255,255,0.09);
    --color-accent: #2563EB;
    --color-accent-hover: #1D4ED8;
    --color-accent-subtle: #EFF6FF;
    --color-success: #059669;
    --color-success-subtle: #ECFDF5;
    --color-danger: #DC2626;
    --color-danger-subtle: #FEF2F2;
    --color-warning: #D97706;
    --color-warning-subtle: #FFFBEB;
    --color-purple: #7C3AED;
    --color-purple-subtle: #F5F3FF;
    --color-orange: #EA580C;
    --color-orange-subtle: #FFF7ED;
    --gradient-emerald: linear-gradient(135deg, #34D399 0%, #059669 100%);
    --gradient-blue: linear-gradient(135deg, #60A5FA 0%, #1D4ED8 100%);
    --gradient-rose: linear-gradient(135deg, #FB7185 0%, #BE123C 100%);
    --gradient-violet: linear-gradient(135deg, #C4B5FD 0%, #6D28D9 100%);
    --chart-1: #2563EB;
    --chart-2: #059669;
    --chart-3: #D97706;
    --chart-4: #7C3AED;
    --chart-5: #EA580C;
    --chart-6: #DC2626;
    --chart-7: #0EA5E9;
    --chart-8: #DB2777;
    --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    --font-mono: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace;
    --radius-sm: 7px;
    --radius-md: 12px;
    --radius-lg: 18px;
    --radius-xl: 22px;
    --shadow-sm: 0 1px 4px rgba(15,17,23,0.06), 0 1px 2px rgba(15,17,23,0.04);
    --shadow-md: 0 4px 16px rgba(15,17,23,0.08), 0 2px 6px rgba(15,17,23,0.04);
    --shadow-lg: 0 16px 48px rgba(15,17,23,0.12), 0 6px 16px rgba(15,17,23,0.06);
    --transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
  }
  :root[data-theme="dark"] {
    --color-bg-base: #14161D;
    --color-bg-card: #1B1E27;
    --color-bg-hover: #232631;
    --color-bg-subtle: #1F222B;
    --color-bg-input: #20232C;
    --color-border: #2C303B;
    --color-border-subtle: #262933;
    --color-text-primary: #F0F2F7;
    --color-text-secondary: #A7AEBD;
    --color-text-tertiary: #6B7280;
    --color-accent: #3B82F6;
    --color-accent-hover: #60A5FA;
    --color-accent-subtle: rgba(59,130,246,0.16);
    --color-success: #10B981;
    --color-success-subtle: rgba(16,185,129,0.16);
    --color-danger: #F87171;
    --color-danger-subtle: rgba(248,113,113,0.16);
    --color-warning: #FBBF24;
    --color-warning-subtle: rgba(251,191,36,0.16);
    --color-purple: #A78BFA;
    --color-purple-subtle: rgba(167,139,250,0.16);
    --color-orange: #FB923C;
    --color-orange-subtle: rgba(251,146,60,0.16);
    --chart-1: #3B7DDD;
    --chart-2: #189E6E;
    --chart-3: #C08A16;
    --chart-4: #8B6EE8;
    --chart-5: #DD6A2A;
    --chart-6: #E4574F;
    --chart-7: #1789A6;
    --chart-8: #CE4A87;
    --shadow-sm: 0 1px 4px rgba(0,0,0,0.28), 0 1px 2px rgba(0,0,0,0.22);
    --shadow-md: 0 4px 16px rgba(0,0,0,0.36), 0 2px 6px rgba(0,0,0,0.24);
    --shadow-lg: 0 16px 48px rgba(0,0,0,0.5), 0 6px 16px rgba(0,0,0,0.3);
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: var(--font-sans); background: var(--color-bg-base); color: var(--color-text-primary); -webkit-font-smoothing: antialiased; }
  input, select, textarea {
    font-family: var(--font-sans);
    font-size: 13px;
    padding: 9px 12px;
    border: 1.5px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg-input);
    color: var(--color-text-primary);
    outline: none;
    width: 100%;
    transition: var(--transition);
  }
  input:focus, select:focus, textarea:focus {
    border-color: var(--color-accent);
    background: var(--color-bg-card);
    box-shadow: 0 0 0 3px rgba(37,99,235,0.10);
  }
  input::placeholder { color: var(--color-text-tertiary); }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--color-border); border-radius: 99px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--color-text-tertiary); }
  .recharts-default-tooltip { border-radius: 12px !important; border: 1px solid #E4E8F0 !important; background: #fff !important; box-shadow: 0 8px 32px rgba(15,17,23,0.12) !important; padding: 10px 14px !important; }
  [data-theme="dark"] .recharts-default-tooltip { border: 1px solid #2C303B !important; background: #1B1E27 !important; box-shadow: 0 8px 32px rgba(0,0,0,0.45) !important; color: #F0F2F7 !important; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes scaleIn { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }
  .chart-card { animation: fadeUp 0.3s ease; }
`;

// ─── SEED DATA ───────────────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const now = new Date();
const thisMonth = MONTHS[now.getMonth()];
const thisYear = now.getFullYear();

const ACCOUNT_TYPES = [
  {value:"checking", label:"Checking Account", icon:"🏦"},
  {value:"savings", label:"Savings Account", icon:"💰"},
  {value:"credit_card", label:"Credit Card", icon:"💳"},
  {value:"ewallet_gcash", label:"GCash", icon:"📱"},
  {value:"ewallet_maya", label:"Maya", icon:"🌐"},
  {value:"ewallet_paypal", label:"PayPal", icon:"🅿️"},
  {value:"cash", label:"Cash Wallet", icon:"💵"},
  {value:"crypto", label:"Crypto Wallet", icon:"🔷"},
  {value:"ewallet", label:"E-Wallet", icon:"📲"},
];

const initAccounts = [
  { id:"a1", name:"BDO Checking", type:"checking", balance:85400, currency:"PHP", color:"#2563EB", institution:"BDO Unibank", accountNo:"****4821", reconciled:true },
  { id:"a2", name:"BPI Savings", type:"savings", balance:210000, currency:"PHP", color:"#059669", institution:"BPI", accountNo:"****9032", reconciled:true },
  { id:"a3", name:"GCash", type:"ewallet_gcash", balance:12500, currency:"PHP", color:"#0EA5E9", institution:"GCash", accountNo:"0917-xxx-xxxx", reconciled:false },
  { id:"a4", name:"Cash Wallet", type:"cash", balance:8300, currency:"PHP", color:"#D97706", institution:"Personal", accountNo:"-", reconciled:true },
  { id:"a5", name:"Maya", type:"ewallet_maya", balance:4200, currency:"PHP", color:"#7C3AED", institution:"Maya / Paymaya", accountNo:"0928-xxx-xxxx", reconciled:false },
  { id:"a6", name:"Emergency Fund", type:"savings", balance:150000, currency:"PHP", color:"#059669", institution:"BPI", accountNo:"****7761", reconciled:true },
];

const initTransactions = [
  { id:"t1", date:"2025-06-01", desc:"Monthly Salary", category:"Salary Income", account:"a1", amount:75000, type:"income", notes:"", tags:["salary"], cleared:true, reconciled:true },
  { id:"t2", date:"2025-06-02", desc:"Grocery SM", category:"Groceries", account:"a4", amount:3200, type:"expense", notes:"", tags:["food"], cleared:true, reconciled:true },
  { id:"t3", date:"2025-06-03", desc:"Meralco Bill", category:"Electricity", account:"a1", amount:2800, type:"expense", notes:"", tags:["bills","utilities"], cleared:true, reconciled:true },
  { id:"t4", date:"2025-06-04", desc:"Netflix", category:"Subscriptions", account:"a3", amount:549, type:"expense", notes:"", tags:["subscription","streaming"], cleared:true, reconciled:false },
  { id:"t5", date:"2025-06-05", desc:"Fuel - Shell", category:"Fuel", account:"a4", amount:1800, type:"expense", notes:"Full tank", tags:["transport","car"], cleared:true, reconciled:true },
  { id:"t6", date:"2025-06-06", desc:"Freelance Project", category:"Side Hustle Income", account:"a1", amount:15000, type:"income", notes:"Web dev project — client X", tags:["freelance","business"], cleared:true, reconciled:true },
  { id:"t7", date:"2025-06-07", desc:"Dining - Mang Inasal", category:"Dining Out", account:"a3", amount:450, type:"expense", notes:"", tags:["food","dining"], cleared:true, reconciled:false },
  { id:"t8", date:"2025-06-08", desc:"PLDT Internet", category:"Internet", account:"a1", amount:1800, type:"expense", notes:"", tags:["bills","utilities"], cleared:true, reconciled:true },
  { id:"t9", date:"2025-06-09", desc:"Spotify", category:"Subscriptions", account:"a3", amount:179, type:"expense", notes:"", tags:["subscription","music"], cleared:false, reconciled:false },
  { id:"t10", date:"2025-06-10", desc:"Interest from Jomar", category:"Interest Income from Loans", account:"a1", amount:2500, type:"income", notes:"Monthly interest collection", tags:["interest","income"], cleared:true, reconciled:true },
  { id:"t11", date:"2025-06-11", desc:"Groceries Puregold", category:"Groceries", account:"a4", amount:2100, type:"expense", notes:"", tags:["food"], cleared:true, reconciled:true },
  { id:"t12", date:"2025-06-12", desc:"Car Insurance", category:"Insurance", account:"a1", amount:4500, type:"expense", notes:"Annual renewal", tags:["insurance","car"], cleared:true, reconciled:true },
  { id:"t13", date:"2025-06-13", desc:"Coffee Starbucks", category:"Coffee", account:"a3", amount:320, type:"expense", notes:"", tags:["food","coffee"], cleared:false, reconciled:false },
  { id:"t14", date:"2025-06-14", desc:"Bonus Q2", category:"Bonus Income", account:"a1", amount:20000, type:"income", notes:"Q2 performance bonus", tags:["bonus","salary"], cleared:true, reconciled:true },
  { id:"t15", date:"2025-06-15", desc:"Water Bill", category:"Water", account:"a1", amount:650, type:"expense", notes:"", tags:["bills","utilities"], cleared:true, reconciled:true },
  { id:"t16", date:"2025-06-16", desc:"Globe Mobile", category:"Mobile Phone", account:"a5", amount:999, type:"expense", notes:"Postpaid plan", tags:["bills","telecom"], cleared:false, reconciled:false },
  { id:"t17", date:"2025-06-17", desc:"Adobe Creative Cloud", category:"Subscriptions", account:"a3", amount:1199, type:"expense", notes:"", tags:["subscription","design"], cleared:false, reconciled:false },
  { id:"t18", date:"2025-06-18", desc:"Investment Dividend", category:"Dividend Income", account:"a1", amount:3200, type:"income", notes:"COL stocks quarterly dividend", tags:["investment","dividend"], cleared:true, reconciled:false },
];

const initReceivables = [
  {
    id:"r1", borrower:"Jomar Santos", contact:"09171234567", loanDate:"2025-01-10",
    principal:50000, rate:3, dueDate:"2025-12-10", paid:15000, interestPaid:4500,
    notes:"Old friend — reliable payer", paymentMethod:"GCash", accountId:"a3",
    installments:[
      {id:"ins1", dueDate:"2025-02-10", amount:5000, paid:true, paidDate:"2025-02-08"},
      {id:"ins2", dueDate:"2025-03-10", amount:5000, paid:true, paidDate:"2025-03-10"},
      {id:"ins3", dueDate:"2025-04-10", amount:5000, paid:true, paidDate:"2025-04-11"},
      {id:"ins4", dueDate:"2025-05-10", amount:5000, paid:false, paidDate:null},
      {id:"ins5", dueDate:"2025-06-10", amount:5000, paid:false, paidDate:null},
    ]
  },
  {
    id:"r2", borrower:"Maria Cruz", contact:"09281234567", loanDate:"2025-03-05",
    principal:20000, rate:2.5, dueDate:"2025-09-05", paid:5000, interestPaid:500,
    notes:"Colleague, paying on time so far", paymentMethod:"BDO", accountId:"a1",
    installments:[
      {id:"ins6", dueDate:"2025-04-05", amount:5000, paid:true, paidDate:"2025-04-04"},
      {id:"ins7", dueDate:"2025-05-05", amount:5000, paid:false, paidDate:null},
      {id:"ins8", dueDate:"2025-06-05", amount:5000, paid:false, paidDate:null},
      {id:"ins9", dueDate:"2025-07-05", amount:5000, paid:false, paidDate:null},
    ]
  },
  {
    id:"r3", borrower:"Pedro Reyes", contact:"09391234567", loanDate:"2024-11-20",
    principal:30000, rate:4, dueDate:"2025-05-20", paid:30000, interestPaid:7200,
    notes:"Fully paid — closed", paymentMethod:"Cash", accountId:"a4",
    installments:[
      {id:"ins10", dueDate:"2024-12-20", amount:10000, paid:true, paidDate:"2024-12-19"},
      {id:"ins11", dueDate:"2025-01-20", amount:10000, paid:true, paidDate:"2025-01-20"},
      {id:"ins12", dueDate:"2025-02-20", amount:10000, paid:true, paidDate:"2025-02-20"},
    ]
  },
];

const initDebts = [
  { id:"d1", lender:"BPI Credit Card", amount:45000, rate:3.5, dueDate:"2025-07-15", paid:10000, type:"credit_card", monthlyPayment:2000, notes:"Avoid minimum payment trap" },
  { id:"d2", lender:"SSS Salary Loan", amount:24000, rate:10, dueDate:"2026-06-01", paid:8000, type:"personal_loan", monthlyPayment:1200, notes:"Auto-deducted from salary" },
  { id:"d3", lender:"Toyota Financial", amount:350000, rate:5.5, dueDate:"2028-06-01", paid:120000, type:"car_loan", monthlyPayment:6800, notes:"2022 Vios — 48 months" },
];

const initBills = [
  { id:"b1", name:"Meralco", category:"Electricity", amount:2800, dueDay:3, status:"paid", account:"a1", recurring:true },
  { id:"b2", name:"PLDT Internet", category:"Internet", amount:1800, dueDay:8, status:"paid", account:"a1", recurring:true },
  { id:"b3", name:"Netflix", category:"Subscriptions", amount:549, dueDay:4, status:"paid", account:"a3", recurring:true },
  { id:"b4", name:"Spotify", category:"Subscriptions", amount:179, dueDay:9, status:"paid", account:"a3", recurring:true },
  { id:"b5", name:"Globe Mobile", category:"Mobile Phone", amount:999, dueDay:20, status:"unpaid", account:"a5", recurring:true },
  { id:"b6", name:"Maynilad Water", category:"Water", amount:650, dueDay:15, status:"unpaid", account:"a1", recurring:true },
  { id:"b7", name:"Condo Dues", category:"Rent", amount:8000, dueDay:5, status:"paid", account:"a1", recurring:true },
  { id:"b8", name:"Adobe Creative", category:"Subscriptions", amount:1199, dueDay:25, status:"unpaid", account:"a3", recurring:true },
];

const initBudgets = [
  { id:"bg1", category:"Groceries", budget:8000 },
  { id:"bg2", category:"Dining Out", budget:3000 },
  { id:"bg3", category:"Fuel", budget:4000 },
  { id:"bg4", category:"Entertainment", budget:2000 },
  { id:"bg5", category:"Subscriptions", budget:1500 },
  { id:"bg6", category:"Shopping", budget:5000 },
  { id:"bg7", category:"Coffee", budget:1000 },
];

const initAssets = [
  { id:"as1", name:"Toyota Vios 2022", category:"Vehicle", value:680000, notes:"Depreciates ~10% yearly" },
  { id:"as2", name:"MacBook Pro M3", category:"Laptop", value:85000, notes:"Purchased Dec 2023" },
  { id:"as3", name:"iPhone 16 Pro", category:"Phone", value:65000, notes:"" },
  { id:"as4", name:"UITF BPI", category:"Mutual Funds", value:45000, notes:"Low-risk bond fund" },
  { id:"as5", name:"COL Financial Stocks", category:"Stocks", value:92000, notes:"Dividend stocks" },
];

const initGoals = [
  { id:"g1", name:"Emergency Fund (6 months)", targetAmount:200000, savedAmount:150000, targetDate:"2025-12-31", icon:"🛡️", color:"#059669" },
  { id:"g2", name:"MacBook Pro Upgrade", targetAmount:120000, savedAmount:45000, targetDate:"2025-09-30", icon:"💻", color:"#2563EB" },
  { id:"g3", name:"Vacation to Japan", targetAmount:80000, savedAmount:18000, targetDate:"2026-03-31", icon:"✈️", color:"#7C3AED" },
];

const initReconciliations = [
  { id:"rec1", accountId:"a1", date:"2025-05-31", openingBalance:72000, closingBalance:85400, clearedCount:12, status:"completed" },
  { id:"rec2", accountId:"a2", date:"2025-05-31", openingBalance:200000, closingBalance:210000, clearedCount:5, status:"completed" },
];

const incomeCategories = [
  "Salary Income","Bonus Income","Commission Income","Overtime Income",
  "Interest Income from Loans","Interest Income from Banks","Investment Income",
  "Dividend Income","Rental Income","Business Income","Side Hustle Income",
  "Refunds","Miscellaneous Income"
];
const expenseCategories = [
  "Rent","Utilities","Electricity","Water","Internet","Mobile Phone",
  "Fuel","Parking","Toll Fees","Vehicle Maintenance","Public Transportation",
  "Groceries","Dining Out","Coffee","Food Delivery",
  "Medical","Medicines","Insurance",
  "Entertainment","Travel","Shopping","Subscriptions",
  "Bank Charges","Interest Expense","Taxes","Accounting Services",
  "Books","Courses","Certifications",
  "Gifts","Donations","Miscellaneous Expenses"
];

const PALETTE = ["#2563EB","#059669","#DC2626","#D97706","#7C3AED","#EA580C","#0EA5E9","#DB2777","#16A34A","#4F46E5","#B45309","#0891B2"];

// ─── IMPORT / EXPORT ENTITY CONFIG ────────────────────────────────────────────
// Drives both CSV import (column expectations) and PDF export (column headers).
// `dbKey` is only set where the Supabase column name differs from the JS field name.
const NEEDS_SCHEDULE = ["receivables","debts","bills"];
const ENTITY_CONFIGS = [
  { key:"accounts", table:"accounts", label:"Accounts", required:["name","balance"], fields:[
    {key:"name",type:"string"}, {key:"type",type:"string"}, {key:"balance",type:"number"},
    {key:"institution",type:"string"}, {key:"accountNo",type:"string",dbKey:"account_no"}, {key:"color",type:"string"},
  ]},
  { key:"transactions", table:"transactions", label:"Transactions", required:["date","desc","amount","account"], fields:[
    {key:"date",type:"string"}, {key:"desc",type:"string",dbKey:"description"}, {key:"category",type:"string"},
    {key:"account",type:"string"}, {key:"amount",type:"number"}, {key:"type",type:"string"},
    {key:"notes",type:"string"}, {key:"tags",type:"tags"}, {key:"cleared",type:"boolean"},
  ]},
  { key:"receivables", table:"receivables", label:"Receivables", required:["borrower","principal"], fields:[
    {key:"borrower",type:"string"}, {key:"contact",type:"string"}, {key:"loanDate",type:"string",dbKey:"loan_date"},
    {key:"principal",type:"number"}, {key:"rate",type:"number"}, {key:"dueDate",type:"string",dbKey:"due_date"},
    {key:"paid",type:"number"}, {key:"interestPaid",type:"number",dbKey:"interest_paid"}, {key:"notes",type:"string"},
    {key:"paymentMethod",type:"string",dbKey:"payment_method"}, {key:"accountId",type:"string",dbKey:"account_id"},
  ]},
  { key:"debts", table:"debts", label:"Debts (Loans)", required:["lender","amount"], fields:[
    {key:"lender",type:"string"}, {key:"amount",type:"number"}, {key:"rate",type:"number"},
    {key:"dueDate",type:"string",dbKey:"due_date"}, {key:"paid",type:"number"}, {key:"type",type:"string"},
    {key:"monthlyPayment",type:"number",dbKey:"monthly_payment"}, {key:"notes",type:"string"}, {key:"termMonths",type:"number",dbKey:"term_months"},
  ]},
  { key:"bills", table:"bills", label:"Bills", required:["name","amount"], fields:[
    {key:"name",type:"string"}, {key:"category",type:"string"}, {key:"amount",type:"number"},
    {key:"dueDay",type:"number"}, {key:"status",type:"string"}, {key:"account",type:"string"}, {key:"recurring",type:"boolean"},
  ]},
  { key:"budgets", table:"budgets", label:"Budgets", required:["category","budget"], fields:[
    {key:"category",type:"string"}, {key:"budget",type:"number"},
  ]},
  { key:"goals", table:"goals", label:"Goals", required:["name","targetAmount"], fields:[
    {key:"name",type:"string"}, {key:"targetAmount",type:"number",dbKey:"target_amount"}, {key:"savedAmount",type:"number",dbKey:"saved_amount"},
    {key:"targetDate",type:"string",dbKey:"target_date"}, {key:"icon",type:"string"}, {key:"color",type:"string"},
  ]},
  { key:"assets", table:"assets", label:"Assets", required:["name","value"], fields:[
    {key:"name",type:"string"}, {key:"category",type:"string"}, {key:"value",type:"number"}, {key:"notes",type:"string"},
  ]},
];

const coerceField = (rawVal, type) => {
  const v = (rawVal ?? "").toString().trim();
  if (type === "number") return parseFloat(v) || 0;
  if (type === "boolean") return ["true","1","yes","y"].includes(v.toLowerCase());
  if (type === "tags") return v ? v.split(";").map(s=>s.trim()).filter(Boolean) : [];
  return v;
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmt = (n) => "₱" + (Math.round(n||0)).toLocaleString();
const fmtShort = (n) => {
  const abs = Math.abs(n||0);
  const sign = (n||0) < 0 ? "-" : "";
  if (abs >= 1000000) return sign + "₱" + (abs/1000000).toFixed(1) + "M";
  if (abs >= 1000) return sign + "₱" + (abs/1000).toFixed(0) + "K";
  return sign + "₱" + abs;
};
const today = () => new Date().toISOString().split("T")[0];
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2,6);
// Whether a transaction is money arriving into its account — true income, or the receiving leg of a transfer.
const isInflow = (t) => t.type==="income" || t.type==="transfer_in";

const getAccountIcon = (type) => {
  const t = ACCOUNT_TYPES.find(a => a.value === type);
  return t ? t.icon : "🏦";
};

const getDaysOverdue = (dueDate) => {
  const diff = (new Date() - new Date(dueDate)) / 86400000;
  return Math.floor(diff);
};

const getAgingBucket = (r) => {
  const days = getDaysOverdue(r.dueDate);
  if (r.paid >= r.principal) return "paid";
  if (days <= 0) return "current";
  if (days <= 30) return "1-30";
  if (days <= 60) return "31-60";
  if (days <= 90) return "61-90";
  if (days <= 120) return "91-120";
  return "120+";
};

// ─── SCHEDULE GENERATION ─────────────────────────────────────────────────────
const addInterval = (dateStr, n, unit) => {
  const d = new Date(dateStr);
  if (unit === "biweekly") d.setDate(d.getDate() + n * 14);
  else if (unit === "weekly") d.setDate(d.getDate() + n * 7);
  else d.setMonth(d.getMonth() + n); // monthly (default)
  return d.toISOString().split("T")[0];
};

// Declining-balance amortization schedule (real loan math) — used by Debts.
const generateAmortizationSchedule = ({ principal, annualRatePct, termMonths, startDate }) => {
  const months = Math.max(1, parseInt(termMonths) || 1);
  const monthlyRate = (parseFloat(annualRatePct) || 0) / 100 / 12;
  const payment = monthlyRate > 0
    ? principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -months))
    : principal / months;
  let balance = principal;
  const rows = [];
  for (let i = 0; i < months; i++) {
    const interest = balance * monthlyRate;
    let principalPortion = payment - interest;
    balance = Math.max(0, balance - principalPortion);
    if (i === months - 1 && balance > 0) { principalPortion += balance; balance = 0; }
    rows.push({
      id: uid(), month: i + 1, dueDate: addInterval(startDate, i + 1, "monthly"),
      amount: payment, principal: principalPortion, interest, balance,
      paid: false, paidDate: null
    });
  }
  return rows;
};

// Recompute interest/principal/balance from user-edited installment amounts, in row order — used by Debts.
const recalcDebtInstallments = (installments, principal, annualRatePct) => {
  const monthlyRate = (parseFloat(annualRatePct) || 0) / 100 / 12;
  let balance = principal;
  return installments.map((ins, i) => {
    const amount = parseFloat(ins.amount) || 0;
    const interest = balance * monthlyRate;
    const principalPortion = Math.min(amount - interest, balance);
    balance = Math.max(0, balance - principalPortion);
    return { ...ins, month: i + 1, principal: principalPortion, interest, balance };
  });
};

// Equal-split installment plan (flat, no interest breakdown) — used by Receivables.
const generateInstallmentPlan = ({ principal, count, frequency = "monthly", startDate }) => {
  const n = Math.max(1, parseInt(count) || 1);
  const base = Math.floor((principal / n) * 100) / 100;
  const rows = [];
  let allocated = 0;
  for (let i = 0; i < n; i++) {
    const isLast = i === n - 1;
    const amount = isLast ? Math.round((principal - allocated) * 100) / 100 : base;
    allocated += amount;
    rows.push({ id: uid(), dueDate: addInterval(startDate, i + 1, frequency), amount, paid: false, paidDate: null });
  }
  return rows;
};

// Rolling recurring-bill schedule (next N monthly due dates) — used by Bills.
const generateBillSchedule = ({ dueDay, amount, monthsAhead = 12, startDate }) => {
  const base = new Date(startDate || today());
  const rows = [];
  for (let i = 0; i < monthsAhead; i++) {
    const y = base.getFullYear();
    const m = base.getMonth() + i;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const day = Math.min(dueDay, daysInMonth);
    const d = new Date(y, m, day);
    rows.push({ id: uid(), dueDate: d.toISOString().split("T")[0], amount, paid: false, paidDate: null });
  }
  return rows;
};

// ─── STYLE INJECTION ─────────────────────────────────────────────────────────
function StyleInjector() {
  useEffect(() => {
    const tag = document.createElement("style");
    tag.innerHTML = GLOBAL_STYLE;
    document.head.prepend(tag);
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const icons = document.createElement("link");
    icons.href = "https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css";
    icons.rel = "stylesheet";
    document.head.appendChild(icons);
    return () => { document.head.removeChild(tag); };
  }, []);
  return null;
}

// ─── REUSABLE UI PRIMITIVES ───────────────────────────────────────────────────

function Card({ children, style={}, onClick, hover=false }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--color-bg-card)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-lg)",
        padding: "20px 24px",
        boxShadow: "var(--shadow-sm)",
        transition: hover ? "var(--transition)" : undefined,
        cursor: onClick ? "pointer" : undefined,
        ...style
      }}
      onMouseEnter={hover && onClick ? e => { e.currentTarget.style.boxShadow = "var(--shadow-md)"; e.currentTarget.style.borderColor = "var(--color-accent)"; } : undefined}
      onMouseLeave={hover && onClick ? e => { e.currentTarget.style.boxShadow = "var(--shadow-sm)"; e.currentTarget.style.borderColor = "var(--color-border)"; } : undefined}
    >
      {children}
    </div>
  );
}

function Page({ title, subtitle, action, children, noPad=false }) {
  return (
    <div style={{ padding: noPad ? 0 : "28px 32px", maxWidth: 1400 }}>
      {(title || action) && (
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom: 24 }}>
          <div>
            <h1 style={{ margin:0, fontSize:20, fontWeight:600, color:"var(--color-text-primary)", letterSpacing:"-0.02em" }}>{title}</h1>
            {subtitle && <p style={{ margin:"4px 0 0", fontSize:13, color:"var(--color-text-secondary)" }}>{subtitle}</p>}
          </div>
          {action && <div style={{ display:"flex", gap:8, alignItems:"center" }}>{action}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function MetricCard({ label, value, sub, color="var(--color-accent)", icon, trend, trendValue, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--color-bg-card)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-lg)",
        padding: "18px 20px",
        boxShadow: "var(--shadow-sm)",
        cursor: onClick ? "pointer" : undefined,
        transition: "var(--transition)",
        position: "relative",
        overflow: "hidden"
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "var(--shadow-md)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "var(--shadow-sm)"; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      {/* accent bar */}
      <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${color},color-mix(in srgb, ${color} 55%, transparent))`, borderRadius:"var(--radius-lg) var(--radius-lg) 0 0" }} />
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
        <div style={{ fontSize:10, fontWeight:600, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.10em" }}>{label}</div>
        {icon && (
          <div style={{ width:34, height:34, borderRadius:11, background: `color-mix(in srgb, ${color} 14%, transparent)`, display:"flex", alignItems:"center", justifyContent:"center", border:`1px solid color-mix(in srgb, ${color} 22%, transparent)` }}>
            <i className={`ti ${icon}`} style={{ fontSize:16, color }} aria-hidden="true" />
          </div>
        )}
      </div>
      <div style={{ fontSize:24, fontWeight:800, color:"var(--color-text-primary)", letterSpacing:"-0.04em", lineHeight:1 }}>{value}</div>
      {(sub || trendValue !== undefined) && (
        <div style={{ marginTop:9, display:"flex", alignItems:"center", gap:6 }}>
          {trendValue !== undefined && (
            <span style={{ fontSize:11, fontWeight:700, color: trendValue >= 0 ? "var(--color-success)" : "var(--color-danger)", background: trendValue >= 0 ? "var(--color-success-subtle)" : "var(--color-danger-subtle)", padding:"2px 7px", borderRadius:20 }}>
              {trendValue >= 0 ? "▲" : "▼"} {Math.abs(trendValue)}%
            </span>
          )}
          {sub && <div style={{ fontSize:11, color:"var(--color-text-tertiary)", fontWeight:500 }}>{sub}</div>}
        </div>
      )}
    </div>
  );
}

function GradientStatCard({ label, value, sub, icon, gradient="var(--gradient-blue)", trendValue, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: gradient,
        borderRadius: "var(--radius-xl)",
        padding: "20px 22px",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
        cursor: onClick ? "pointer" : undefined,
        boxShadow: "var(--shadow-md)",
        transition: "var(--transition)",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-lg)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
    >
      {/* decorative glow */}
      <div style={{ position:"absolute", top:-30, right:-30, width:120, height:120, borderRadius:"50%", background:"rgba(255,255,255,0.12)" }} />
      <div style={{ position:"relative", display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
        <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.82)", textTransform:"uppercase", letterSpacing:"0.10em" }}>{label}</div>
        {icon && (
          <div style={{ width:34, height:34, borderRadius:11, background:"rgba(255,255,255,0.18)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <i className={`ti ${icon}`} style={{ fontSize:16, color:"#fff" }} aria-hidden="true" />
          </div>
        )}
      </div>
      <div style={{ position:"relative", fontSize:26, fontWeight:800, letterSpacing:"-0.04em", lineHeight:1 }}>{value}</div>
      {(sub || trendValue !== undefined) && (
        <div style={{ position:"relative", marginTop:10, display:"flex", alignItems:"center", gap:6 }}>
          {trendValue !== undefined && (
            <span style={{ fontSize:11, fontWeight:700, background:"rgba(255,255,255,0.20)", padding:"2px 7px", borderRadius:20 }}>
              {trendValue >= 0 ? "▲" : "▼"} {Math.abs(trendValue)}%
            </span>
          )}
          {sub && <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.82)", fontWeight:500 }}>{sub}</div>}
        </div>
      )}
    </div>
  );
}

function Badge({ children, color="blue", size="sm" }) {
  const schemes = {
    blue: { bg:"var(--color-accent-subtle)", text:"var(--color-accent)" },
    green: { bg:"var(--color-success-subtle)", text:"var(--color-success)" },
    red: { bg:"var(--color-danger-subtle)", text:"var(--color-danger)" },
    amber: { bg:"var(--color-warning-subtle)", text:"var(--color-warning)" },
    purple: { bg:"var(--color-purple-subtle)", text:"var(--color-purple)" },
    orange: { bg:"var(--color-orange-subtle)", text:"var(--color-orange)" },
    gray: { bg:"var(--color-bg-subtle)", text:"var(--color-text-secondary)" },
  };
  const { bg, text } = schemes[color] || schemes.blue;
  return (
    <span style={{
      background: bg, color: text,
      fontSize: size === "xs" ? 10 : 11,
      padding: size === "xs" ? "1px 5px" : "2px 8px",
      borderRadius: 99, fontWeight: 600, whiteSpace: "nowrap"
    }}>{children}</span>
  );
}

function SectionTitle({ children, action }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
      <h3 style={{ fontSize:11, fontWeight:600, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.08em" }}>{children}</h3>
      {action}
    </div>
  );
}

function Btn({ children, onClick, variant="primary", size="md", icon, disabled=false, style:s={} }) {
  const base = {
    display:"flex", alignItems:"center", gap:6,
    border:"none", cursor: disabled ? "not-allowed" : "pointer",
    fontFamily:"var(--font-sans)", fontWeight:500,
    transition:"var(--transition)", opacity: disabled ? 0.6 : 1,
    borderRadius:"var(--radius-sm)",
    fontSize: size === "sm" ? 12 : 13,
    padding: size === "sm" ? "5px 12px" : size === "lg" ? "10px 20px" : "8px 16px",
  };
  const variants = {
    primary: { background:"var(--color-accent)", color:"#fff", boxShadow:"0 2px 10px color-mix(in srgb, var(--color-accent) 35%, transparent)" },
    secondary: { background:"var(--color-bg-subtle)", color:"var(--color-text-primary)", border:"1px solid var(--color-border)" },
    danger: { background:"var(--color-danger-subtle)", color:"var(--color-danger)" },
    success: { background:"var(--color-success-subtle)", color:"var(--color-success)" },
    ghost: { background:"transparent", color:"var(--color-text-secondary)" },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      style={{ ...base, ...variants[variant]||variants.primary, ...s }}
      onMouseEnter={!disabled ? e => {
        if (variant==="primary") e.currentTarget.style.background = "var(--color-accent-hover)";
        else if (variant==="secondary") e.currentTarget.style.background = "var(--color-bg-hover)";
      } : undefined}
      onMouseLeave={!disabled ? e => {
        if (variant==="primary") e.currentTarget.style.background = "var(--color-accent)";
        else if (variant==="secondary") e.currentTarget.style.background = "var(--color-bg-subtle)";
      } : undefined}
    >
      {icon && <i className={`ti ${icon}`} style={{ fontSize:14 }} aria-hidden="true" />}
      {children}
    </button>
  );
}

function Input({ label, type="text", value, onChange, placeholder, style:s={}, ...rest }) {
  return (
    <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", display:"block" }}>
      {label}
      <input
        type={type} value={value} onChange={onChange}
        placeholder={placeholder}
        style={{ display:"block", marginTop:4, ...s }}
        {...rest}
      />
    </label>
  );
}

function Select({ label, value, onChange, children, style:s={} }) {
  return (
    <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", display:"block" }}>
      {label}
      <select value={value} onChange={onChange} style={{ display:"block", marginTop:4, ...s }}>
        {children}
      </select>
    </label>
  );
}

function ProgressBar({ value, max=100, color="var(--color-accent)", height=6, showLabel=false }) {
  const pct = Math.min(100, max > 0 ? (value / max) * 100 : 0);
  return (
    <div>
      {showLabel && (
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:4, color:"var(--color-text-secondary)" }}>
          <span>{Math.round(pct)}%</span>
          <span>{fmt(value)} / {fmt(max)}</span>
        </div>
      )}
      <div style={{ height, borderRadius:99, background:"var(--color-border)", overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:99, transition:"width 0.4s ease" }} />
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, width=520 }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:1000,
      background:"rgba(0,0,0,0.35)", display:"flex",
      alignItems:"center", justifyContent:"center", padding:20
    }}>
      <div style={{
        background:"var(--color-bg-card)", borderRadius:"var(--radius-xl)",
        boxShadow:"var(--shadow-lg)", width:"100%", maxWidth:width,
        maxHeight:"90vh", overflow:"auto"
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 24px", borderBottom:"1px solid var(--color-border)" }}>
          <h2 style={{ fontSize:16, fontWeight:600, margin:0 }}>{title}</h2>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--color-text-secondary)", display:"flex", alignItems:"center" }}>
            <i className="ti ti-x" style={{ fontSize:18 }} aria-hidden="true" />
          </button>
        </div>
        <div style={{ padding:"24px" }}>{children}</div>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, desc, action }) {
  return (
    <div style={{ textAlign:"center", padding:"48px 24px" }}>
      <div style={{ fontSize:40, marginBottom:12 }}>{icon}</div>
      <div style={{ fontSize:15, fontWeight:600, color:"var(--color-text-primary)", marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:13, color:"var(--color-text-secondary)", marginBottom:20, maxWidth:320, margin:"0 auto 20px" }}>{desc}</div>
      {action}
    </div>
  );
}

function Divider({ style:s={} }) {
  return <div style={{ height:1, background:"var(--color-border-subtle)", margin:"16px 0", ...s }} />;
}

function Tag({ label, onRemove }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:"var(--color-accent-subtle)", color:"var(--color-accent)", fontSize:11, fontWeight:500, padding:"2px 8px", borderRadius:99 }}>
      {label}
      {onRemove && <button onClick={onRemove} style={{ background:"none", border:"none", cursor:"pointer", color:"inherit", display:"flex", alignItems:"center", padding:0, lineHeight:1 }}>×</button>}
    </span>
  );
}

// ─── IMPORT / EXPORT ──────────────────────────────────────────────────────────
function ImportModal({ config, session, onClose, onImported }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);

  const isRowValid = (r) => config.required.every(k => {
    const f = config.fields.find(x=>x.key===k);
    if (!f || f.type !== "string") return true;
    return !!r[k];
  });

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setError("");
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (results) => {
        const parsed = results.data.map(raw => {
          const rec = {};
          config.fields.forEach(f => { rec[f.key] = coerceField(raw[f.key], f.type); });
          return rec;
        });
        setRows(parsed);
      },
      error: (err) => setError(err.message),
    });
  };

  const validRows = rows.filter(isRowValid);

  const doImport = async () => {
    if (validRows.length===0) return;
    setImporting(true);
    const localRecords = validRows.map(r => ({
      ...r, id: uid(),
      ...(NEEDS_SCHEDULE.includes(config.key) ? {installments:[]} : {}),
      ...(config.key==="transactions" ? {reconciled:false} : {}),
    }));
    const dbRecords = localRecords.map(r => {
      const dbRec = { user_id: session.user.id, id: r.id };
      config.fields.forEach(f => { dbRec[f.dbKey || f.key] = r[f.key]; });
      if (NEEDS_SCHEDULE.includes(config.key)) dbRec.installments = [];
      if (config.key==="transactions") dbRec.reconciled = false;
      return dbRec;
    });
    const { error: insertError } = await supabase.from(config.table).insert(dbRecords);
    setImporting(false);
    if (insertError) { setError(insertError.message); return; }
    onImported(config.key, localRecords);
    onClose();
  };

  return (
    <Modal title={`Import ${config.label} (CSV)`} onClose={onClose} width={760}>
      <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:12 }}>
        CSV headers should match: <code>{config.fields.map(f=>f.key).join(", ")}</code>. Payment schedules aren't imported — new records start with none.
      </div>
      <input type="file" accept=".csv" onChange={handleFile} style={{ marginBottom:12 }} />
      {fileName && <div style={{ fontSize:12, marginBottom:12, color:"var(--color-text-secondary)" }}>{rows.length} row(s) parsed from {fileName}</div>}
      {error && <div style={{ color:"var(--color-danger)", fontSize:12, marginBottom:12 }}>{error}</div>}
      {rows.length > 0 && (
        <div style={{ overflow:"auto", maxHeight:260, marginBottom:16, border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
            <thead>
              <tr style={{ background:"var(--color-bg-subtle)" }}>
                {config.fields.map(f=><th key={f.key} style={{ padding:"6px 8px", textAlign:"left", whiteSpace:"nowrap", color:"var(--color-text-tertiary)" }}>{f.key}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0,10).map((r,i)=>(
                <tr key={i} style={{ borderTop:"1px solid var(--color-border-subtle)" }}>
                  {config.fields.map(f=><td key={f.key} style={{ padding:"6px 8px", whiteSpace:"nowrap" }}>{Array.isArray(r[f.key])?r[f.key].join(", "):String(r[f.key])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length>10 && <div style={{ padding:8, fontSize:11, color:"var(--color-text-tertiary)" }}>+ {rows.length-10} more row(s)…</div>}
        </div>
      )}
      {rows.length - validRows.length > 0 && (
        <div style={{ color:"var(--color-warning)", fontSize:12, marginBottom:12 }}>
          {rows.length - validRows.length} row(s) are missing required fields ({config.required.join(", ")}) and will be skipped.
        </div>
      )}
      <div style={{ display:"flex", gap:8 }}>
        <Btn onClick={doImport} icon="ti-upload" disabled={validRows.length===0||importing}>{importing?"Importing…":`Import ${validRows.length} Row(s)`}</Btn>
        <Btn onClick={onClose} variant="secondary">Cancel</Btn>
      </div>
    </Modal>
  );
}

function DataManager({ accounts, setAccounts, transactions, setTransactions, receivables, setReceivables, debts, setDebts, bills, setBills, budgets, setBudgets, goals, setGoals, assets, setAssets, session }) {
  const [importEntity, setImportEntity] = useState(null);

  const SETTERS = { accounts:setAccounts, transactions:setTransactions, receivables:setReceivables, debts:setDebts, bills:setBills, budgets:setBudgets, goals:setGoals, assets:setAssets };
  const DATA = { accounts, transactions, receivables, debts, bills, budgets, goals, assets };

  const handleImported = (key, records) => {
    SETTERS[key](prev => [...prev, ...records]);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    let wroteAny = false;
    ENTITY_CONFIGS.forEach(cfg => {
      const rows = DATA[cfg.key];
      if (!rows || rows.length===0) return;
      if (wroteAny) doc.addPage();
      wroteAny = true;
      doc.setFontSize(14);
      doc.text(cfg.label, 14, 16);
      autoTable(doc, {
        startY: 22,
        head: [cfg.fields.map(f=>f.key)],
        body: rows.map(r => cfg.fields.map(f => {
          const v = r[f.key];
          if (Array.isArray(v)) return v.join(", ");
          return (v===undefined||v===null) ? "" : String(v);
        })),
        styles: { fontSize:7 },
        headStyles: { fillColor:[37,99,235] },
      });
    });
    if (!wroteAny) { window.alert("No data to export yet."); return; }
    doc.save(`finance-backup-${today()}.pdf`);
  };

  return (
    <Page title="Import & Export" subtitle="Bring data in from CSV, or export a full backup as PDF">
      <div style={{ display:"grid", gridTemplateColumns: "1fr 1fr", gap:16 }}>
        <Card>
          <SectionTitle>Import from CSV</SectionTitle>
          <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:14 }}>Pick what you're importing, then upload a CSV file matching that entity's columns.</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {ENTITY_CONFIGS.map(cfg => (
              <div key={cfg.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 12px", background:"var(--color-bg-subtle)", borderRadius:"var(--radius-sm)" }}>
                <span style={{ fontSize:13, fontWeight:500 }}>{cfg.label}</span>
                <Btn onClick={()=>setImportEntity(cfg.key)} variant="secondary" size="sm" icon="ti-file-import">Import</Btn>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <SectionTitle>Export Full Backup</SectionTitle>
          <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:14 }}>Download every entity — accounts, transactions, receivables, debts, bills, budgets, goals, and assets — as one PDF.</div>
          <Btn onClick={exportPDF} icon="ti-file-download">Export Full Backup (PDF)</Btn>
        </Card>
      </div>

      {importEntity && (
        <ImportModal config={ENTITY_CONFIGS.find(c=>c.key===importEntity)} session={session} onClose={()=>setImportEntity(null)} onImported={handleImported} />
      )}
    </Page>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App({ session }) {
  const [tab, setTab] = useState("dashboard");
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [debts, setDebts] = useState([]);
  const [bills, setBills] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [goals, setGoals] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => session.user.user_metadata?.theme || (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? "dark" : "light"));

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const remoteTheme = session.user.user_metadata?.theme;
    if (remoteTheme && remoteTheme !== theme) setTheme(remoteTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.user.user_metadata?.theme]);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    supabase.auth.updateUser({ data: { theme: next } });
  };

  const isMobile = windowWidth <= 768;
  
  useEffect(() => {
   const loadAll = async () => {
      setLoading(true);
      const userId = session.user.id;
      const [
        { data: acc }, { data: txn }, { data: rec },
        { data: dbt }, { data: bll }, { data: bdg },
        { data: ast }, { data: gls }
      ] = await Promise.all([
        supabase.from('accounts').select('*').eq('user_id', userId),
        supabase.from('transactions').select('*').eq('user_id', userId),
        supabase.from('receivables').select('*').eq('user_id', userId),
        supabase.from('debts').select('*').eq('user_id', userId),
        supabase.from('bills').select('*').eq('user_id', userId),
        supabase.from('budgets').select('*').eq('user_id', userId),
        supabase.from('assets').select('*').eq('user_id', userId),
        supabase.from('goals').select('*').eq('user_id', userId),
      ]);
      if (acc) setAccounts(acc.map(a => ({...a, accountNo: a.account_no})));
      if (txn) setTransactions(txn.map(t => ({...t, desc: t.description, transferId: t.transfer_id})));
      if (rec) setReceivables(rec.map(r => ({...r, loanDate: r.loan_date, dueDate: r.due_date, interestPaid: r.interest_paid, paymentMethod: r.payment_method, accountId: r.account_id, installments: r.installments || []})));
      if (dbt) setDebts(dbt.map(d => ({...d, dueDate: d.due_date, monthlyPayment: d.monthly_payment, termMonths: d.term_months, installments: d.installments || []})));
      if (bll) setBills(bll.map(b => ({...b, installments: b.installments || []})));
      if (bdg) setBudgets(bdg);
      if (ast) setAssets(ast);
      if (gls) setGoals(gls.map(g => ({...g, targetAmount: g.target_amount, savedAmount: g.saved_amount, targetDate: g.target_date})));
      setLoading(false);
    };
    loadAll();
  }, []);

  

  // ── derived metrics ──
  const totalLiquid = useMemo(() => accounts.reduce((s,a) => s+a.balance, 0), [accounts]);
  const totalCash = useMemo(() => accounts.filter(a => ["cash","ewallet","ewallet_gcash","ewallet_maya","ewallet_paypal"].includes(a.type)).reduce((s,a) => s+a.balance, 0), [accounts]);
  const totalBank = useMemo(() => accounts.filter(a => ["checking","savings"].includes(a.type)).reduce((s,a) => s+a.balance, 0), [accounts]);
  const totalReceivables = useMemo(() => receivables.reduce((s,r) => s+(r.principal-r.paid), 0), [receivables]);
  const totalDebts = useMemo(() => debts.reduce((s,d) => s+(d.amount-d.paid), 0), [debts]);
  const totalAssetValues = useMemo(() => assets.reduce((s,a) => s+a.value, 0), [assets]);
  const totalAssets = useMemo(() => totalAssetValues + totalLiquid + totalReceivables, [totalAssetValues, totalLiquid, totalReceivables]);
  const netWorth = totalAssets - totalDebts;

  const monthlyIncome = useMemo(() => transactions.filter(t => t.type==="income" && t.category).reduce((s,t) => s+t.amount, 0), [transactions]);
  const monthlyExpense = useMemo(() => transactions.filter(t => t.type==="expense" && t.category).reduce((s,t) => s+t.amount, 0), [transactions]);
  const monthlySavings = monthlyIncome - monthlyExpense;

  // ── account-aware transaction balance updater ──
  // A transaction only affects its account's balance once it's categorized ("Posted");
  // uncategorized ("Pending") transactions are recorded but left out of the ledger until categorized.
  const addTransaction = useCallback(async (txn) => {
   await supabase.from('transactions').insert([{
      id: txn.id, date: txn.date, description: txn.desc,
      category: txn.category, account: txn.account,
      amount: txn.amount, type: txn.type, notes: txn.notes,
      tags: txn.tags, cleared: txn.cleared, reconciled: txn.reconciled,
      user_id: session.user.id
    }]);
    if (txn.category) {
      const newBalance = txn.type === "income"
        ? accounts.find(a => a.id === txn.account)?.balance + txn.amount
        : accounts.find(a => a.id === txn.account)?.balance - txn.amount;
      await supabase.from('accounts').update({ balance: newBalance }).eq('id', txn.account);
      setAccounts(as => as.map(a => {
        if (a.id !== txn.account) return a;
        return { ...a, balance: newBalance };
      }));
    }
    setTransactions(ts => [txn, ...ts]);
  }, [accounts]);

  // Transfers move money between two of the user's own accounts — always posted immediately
  // (no pending/review step, since both accounts are explicit at entry time) and excluded from
  // income/expense totals via their distinct "transfer_out"/"transfer_in" type.
  const addTransfer = useCallback(async ({ date, desc, fromAccount, toAccount, amount }) => {
    const transferId = uid();
    const fromName = accounts.find(a => a.id === fromAccount)?.name || "Account";
    const toName = accounts.find(a => a.id === toAccount)?.name || "Account";
    const outDesc = desc || `Transfer to ${toName}`;
    const inDesc = desc || `Transfer from ${fromName}`;
    const outId = uid(), inId = uid();
    await supabase.from('transactions').insert([
      { id:outId, date, description:outDesc, category:"Transfer", account:fromAccount, amount, type:"transfer_out", notes:"", tags:[], cleared:false, transfer_id:transferId, user_id: session.user.id },
      { id:inId, date, description:inDesc, category:"Transfer", account:toAccount, amount, type:"transfer_in", notes:"", tags:[], cleared:false, transfer_id:transferId, user_id: session.user.id },
    ]);
    const newFromBalance = (accounts.find(a => a.id === fromAccount)?.balance || 0) - amount;
    const newToBalance = (accounts.find(a => a.id === toAccount)?.balance || 0) + amount;
    await supabase.from('accounts').update({ balance: newFromBalance }).eq('id', fromAccount);
    await supabase.from('accounts').update({ balance: newToBalance }).eq('id', toAccount);
    setAccounts(as => as.map(a => {
      if (a.id === fromAccount) return { ...a, balance:newFromBalance };
      if (a.id === toAccount) return { ...a, balance:newToBalance };
      return a;
    }));
    setTransactions(ts => [
      { id:outId, date, desc:outDesc, category:"Transfer", account:fromAccount, amount, type:"transfer_out", notes:"", tags:[], cleared:false, transferId },
      { id:inId, date, desc:inDesc, category:"Transfer", account:toAccount, amount, type:"transfer_in", notes:"", tags:[], cleared:false, transferId },
      ...ts
    ]);
  }, [accounts]);

  const deleteTransaction = useCallback(async (txId) => {
    const txn = transactions.find(t => t.id === txId);
    if (!txn) return;
    if (txn.type === "transfer_out" || txn.type === "transfer_in") {
      const sibling = transactions.find(t => t.transferId === txn.transferId && t.id !== txId);
      await supabase.from('transactions').delete().in('id', sibling ? [txn.id, sibling.id] : [txn.id]);
      const legs = sibling ? [txn, sibling] : [txn];
      const balanceUpdates = [];
      legs.forEach(leg => {
        const acc = accounts.find(a => a.id === leg.account);
        if (!acc) return;
        const newBalance = leg.type === "transfer_in" ? acc.balance - leg.amount : acc.balance + leg.amount;
        balanceUpdates.push({ id: acc.id, balance: newBalance });
      });
      for (const u of balanceUpdates) {
        await supabase.from('accounts').update({ balance: u.balance }).eq('id', u.id);
      }
      setAccounts(as => as.map(a => {
        const u = balanceUpdates.find(x => x.id === a.id);
        return u ? { ...a, balance: u.balance } : a;
      }));
      setTransactions(ts => ts.filter(t => t.id !== txId && (!sibling || t.id !== sibling.id)));
      return;
    }
    await supabase.from('transactions').delete().eq('id', txId);
    if (txn.category) {
      const newBalance = txn.type === "income"
        ? accounts.find(a => a.id === txn.account)?.balance - txn.amount
        : accounts.find(a => a.id === txn.account)?.balance + txn.amount;
      await supabase.from('accounts').update({ balance: newBalance }).eq('id', txn.account);
      setAccounts(as => as.map(a => {
        if (a.id !== txn.account) return a;
        return { ...a, balance: newBalance };
      }));
    }
    setTransactions(ts => ts.filter(t => t.id !== txId));
  }, [transactions, accounts]);

  const navItems = [
    { id:"dashboard", icon:"ti-dashboard", label:"Dashboard" },
    { id:"accounts", icon:"ti-building-bank", label:"Accounts" },
    { id:"transactions", icon:"ti-transfer", label:"Transactions" },
    { id:"receivables", icon:"ti-receipt", label:"Receivables" },
    { id:"debts", icon:"ti-credit-card", label:"Debts" },
    { id:"bills", icon:"ti-calendar-due", label:"Bills" },
    { id:"budgets", icon:"ti-target", label:"Budgets" },
    { id:"goals", icon:"ti-flag", label:"Goals" },
    { id:"reports", icon:"ti-report-analytics", label:"Reports" },
    { id:"assets", icon:"ti-home-2", label:"Assets" },
    { id:"data", icon:"ti-file-import", label:"Import & Export" },
  ];

if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'Inter, sans-serif', fontSize:16, color:'#5A6478' }}>
      Loading your finances...
    </div>
  );

  return (
    <>
      <StyleInjector />
      <div style={{ display:"flex", minHeight:"100vh", background:"var(--color-bg-base)", fontFamily:"var(--font-sans)" }}>
        {/* SIDEBAR — dark premium */}
        <aside style={{
          width: isMobile ? 260 : sidebarCollapsed ? 64 : 228,
          background:"#0F1117",
          borderRight:"none",
          display:"flex", flexDirection:"column", flexShrink:0,
          position: isMobile ? "fixed" : "sticky",
          top:0,
          left: isMobile ? (mobileMenuOpen ? 0 : -260) : 0,
          height:"100vh", overflow:"hidden",
          transition:"left 0.25s cubic-bezier(0.4,0,0.2,1), width 0.22s cubic-bezier(0.4,0,0.2,1)",
          zIndex: isMobile ? 100 : 10,
          boxShadow: isMobile && mobileMenuOpen ? "4px 0 24px rgba(0,0,0,0.4)" : "4px 0 24px rgba(0,0,0,0.18)"
        }}>
          {/* Logo */}
          <div style={{ padding:"22px 16px 18px", borderBottom:"1px solid rgba(255,255,255,0.07)", display:"flex", alignItems:"center", gap:10, justifyContent: sidebarCollapsed ? "center" : undefined }}>
            <div style={{ width:36, height:36, borderRadius:10, background:"linear-gradient(135deg,#2563EB,#7C3AED)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, boxShadow:"0 4px 12px rgba(37,99,235,0.4)" }}>
              <i className="ti ti-chart-pie" style={{ color:"#fff", fontSize:17 }} aria-hidden="true" />
            </div>
            {!sidebarCollapsed && (
              <div>
                <div style={{ fontWeight:800, fontSize:14.5, color:"#FFFFFF", letterSpacing:"-0.03em" }}>BEEBAH</div>
                <div style={{ fontSize:9.5, color:"rgba(255,255,255,0.35)", fontWeight:600, letterSpacing:"0.10em", marginTop:1 }}>PERSONAL FINANCE TRACKER</div>
              </div>
            )}
          </div>
          {/* Nav */}
          <nav style={{ flex:1, padding:"12px 8px", overflow:"auto" }}>
            {navItems.map(n => (
              <button key={n.id} onClick={() => setTab(n.id)} title={sidebarCollapsed ? n.label : undefined}
                style={{
                  display:"flex", alignItems:"center", gap:10,
                  width:"100%", padding:"9px 12px", borderRadius:"var(--radius-md)",
                  border:"none",
                  background: tab===n.id ? "linear-gradient(135deg,#2563EB,#7C3AED)" : "transparent",
                  color: tab===n.id ? "#FFFFFF" : "rgba(255,255,255,0.48)",
                  cursor:"pointer", fontSize:13, fontWeight: tab===n.id ? 600 : 400,
                  marginBottom:2, textAlign:"left", transition:"var(--transition)",
                  justifyContent: sidebarCollapsed ? "center" : undefined,
                  whiteSpace:"nowrap", overflow:"hidden",
                  boxShadow: tab===n.id ? "0 4px 14px rgba(37,99,235,0.35)" : "none",
                }}>
                <i className={`ti ${n.icon}`} style={{ fontSize:17, flexShrink:0, opacity: tab===n.id ? 1 : 0.6 }} aria-hidden="true" />
                {!sidebarCollapsed && n.label}
              </button>
            ))}
          </nav>
          {/* Net Worth Footer */}
          {!sidebarCollapsed && (
            <div style={{ padding:"16px", borderTop:"1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ fontSize:9.5, color:"rgba(255,255,255,0.3)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.10em", marginBottom:5 }}>Net Worth</div>
              <div style={{ fontSize:20, fontWeight:800, color: netWorth>=0 ? "#34D399" : "#F87171", letterSpacing:"-0.03em", lineHeight:1 }}>{fmt(netWorth)}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.25)", marginTop:4 }}>as of {thisMonth} {thisYear}</div>
            </div>
          )}
          {/* Collapse toggle */}
          {!sidebarCollapsed && (
            <div style={{ padding:"8px 16px", borderTop:"1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginBottom:4 }}>{session?.user?.email}</div>
              <button onClick={() => supabase.auth.signOut()} style={{ background:"rgba(220,38,38,0.15)", border:"1px solid rgba(220,38,38,0.3)", borderRadius:8, padding:"6px 12px", color:"#F87171", fontSize:12, cursor:"pointer", width:"100%", fontWeight:500 }}>
                Sign Out
              </button>
            </div>
          )}
          <button onClick={toggleTheme} title={theme==="dark" ? "Switch to light mode" : "Switch to dark mode"}
            style={{ border:"none", background:"transparent", padding:"12px", cursor:"pointer", color:"rgba(255,255,255,0.5)", display:"flex", alignItems:"center", justifyContent: sidebarCollapsed ? "center" : "flex-start", gap:10, borderTop:"1px solid rgba(255,255,255,0.07)", transition:"var(--transition)", width:"100%" }}
            onMouseEnter={e=>e.currentTarget.style.color="#fff"}
            onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,0.5)"}>
            <i className={`ti ${theme==="dark" ? "ti-sun" : "ti-moon"}`} style={{ fontSize:16 }} aria-hidden="true" />
            {!sidebarCollapsed && <span style={{ fontSize:12.5, fontWeight:500 }}>{theme==="dark" ? "Light Mode" : "Dark Mode"}</span>}
          </button>
          <button onClick={() => setSidebarCollapsed(c=>!c)}
            style={{ border:"none", background:"transparent", padding:"12px", cursor:"pointer", color:"rgba(255,255,255,0.3)", display:"flex", alignItems:"center", justifyContent:"center", borderTop:"1px solid rgba(255,255,255,0.07)", transition:"var(--transition)" }}
            onMouseEnter={e=>e.currentTarget.style.color="rgba(255,255,255,0.7)"}
            onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,0.3)"}>
            <i className={`ti ${sidebarCollapsed ? "ti-chevron-right" : "ti-chevron-left"}`} style={{ fontSize:15 }} aria-hidden="true" />
          </button>
        </aside>

        {/* MAIN CONTENT */}
        {isMobile && mobileMenuOpen && (
          <div onClick={() => setMobileMenuOpen(false)} style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:99
          }} />
        )}

        {isMobile && (
          <div style={{
            position:"fixed", top:0, left:0, right:0, height:52,
            background:"#0F1117", zIndex:98, display:"flex",
            alignItems:"center", padding:"0 16px", gap:12,
            boxShadow:"0 2px 12px rgba(0,0,0,0.3)"
          }}>
            <button onClick={() => setMobileMenuOpen(o => !o)} style={{
              background:"none", border:"none", cursor:"pointer",
              color:"#fff", display:"flex", alignItems:"center", padding:4
            }}>
              <i className="ti ti-menu-2" style={{ fontSize:22 }} />
            </button>
            <div style={{ fontWeight:800, fontSize:15, color:"#fff", letterSpacing:"-0.02em" }}>FinanceOS</div>
          </div>
        )}

        <main style={{ flex:1, overflow:"auto", minWidth:0, paddingTop: isMobile ? 52 : 0 }}>
          {tab==="dashboard" && <Dashboard accounts={accounts} transactions={transactions} receivables={receivables} debts={debts} bills={bills} goals={goals} totalCash={totalCash} totalBank={totalBank} totalLiquid={totalLiquid} totalAssets={totalAssets} totalDebts={totalDebts} totalReceivables={totalReceivables} netWorth={netWorth} monthlyIncome={monthlyIncome} monthlyExpense={monthlyExpense} monthlySavings={monthlySavings} setTab={setTab} isMobile={isMobile} />}
         {tab==="accounts" && <AccountsPage accounts={accounts} setAccounts={setAccounts} transactions={transactions} reconciliations={reconciliations} setReconciliations={setReconciliations} selectedAccount={selectedAccount} setSelectedAccount={setSelectedAccount} session={session} />}
          {tab==="transactions" && <Transactions transactions={transactions} setTransactions={setTransactions} accounts={accounts} setAccounts={setAccounts} addTransaction={addTransaction} addTransfer={addTransfer} deleteTransaction={deleteTransaction} session={session} />}
          {tab==="receivables" && <Receivables receivables={receivables} setReceivables={setReceivables} accounts={accounts} setAccounts={setAccounts} session={session} />}
          {tab==="debts" && <Debts debts={debts} setDebts={setDebts} session={session} />}
          {tab==="bills" && <Bills bills={bills} setBills={setBills} accounts={accounts} session={session} />}
          {tab==="budgets" && <Budgets budgets={budgets} setBudgets={setBudgets} transactions={transactions} session={session} />}
          {tab==="goals" && <Goals goals={goals} setGoals={setGoals} monthlySavings={monthlySavings} session={session} />}
          {tab==="reports" && <Reports transactions={transactions} receivables={receivables} debts={debts} accounts={accounts} assets={assets} totalAssets={totalAssets} totalDebts={totalDebts} netWorth={netWorth} totalLiquid={totalLiquid} totalReceivables={totalReceivables} totalAssetValues={totalAssetValues} monthlyIncome={monthlyIncome} monthlyExpense={monthlyExpense} />}
          {tab==="assets" && <Assets assets={assets} setAssets={setAssets} session={session} />}
          {tab==="data" && <DataManager accounts={accounts} setAccounts={setAccounts} transactions={transactions} setTransactions={setTransactions} receivables={receivables} setReceivables={setReceivables} debts={debts} setDebts={setDebts} bills={bills} setBills={setBills} budgets={budgets} setBudgets={setBudgets} goals={goals} setGoals={setGoals} assets={assets} setAssets={setAssets} session={session} />}
        </main>
      </div>
    </>
  );
}

// ─── PREMIUM EXPENSE PIE ─────────────────────────────────────────────────────
function PremiumExpensePie({ expByCategory, totalExpense }) {
  const [activeIdx, setActiveIdx] = useState(null);
  const data = expByCategory.slice(0, 7);
  const RICH_PALETTE = ["var(--chart-1)","var(--chart-2)","var(--chart-3)","var(--chart-4)","var(--chart-5)","var(--chart-6)","var(--chart-7)","var(--chart-8)"];

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, index }) => {
    if (activeIdx !== null && activeIdx !== index) return null;
    const RADIAN = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    const pct = totalExpense > 0 ? ((data[index]?.value||0)/totalExpense*100).toFixed(0) : 0;
    if (pct < 8) return null;
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700}>
        {pct}%
      </text>
    );
  };

  const activeEntry = activeIdx !== null ? data[activeIdx] : null;
  const activePct = activeEntry && totalExpense > 0 ? ((activeEntry.value/totalExpense)*100).toFixed(1) : null;

  return (
    <Card className="chart-card" style={{ display:"flex", flexDirection:"column" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
        <SectionTitle>Expense Breakdown</SectionTitle>
        <span style={{ fontSize:11, color:"var(--color-text-tertiary)", fontWeight:500 }}>This period</span>
      </div>
      <div style={{ position:"relative" }}>
        <ResponsiveContainer width="100%" height={190}>
          <PieChart>
            <defs>
              {RICH_PALETTE.map((c,i) => (
                <filter key={i} id={`glow${i}`} x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
              ))}
            </defs>
            <Pie
              data={data}
              cx="50%" cy="50%"
              innerRadius={58} outerRadius={82}
              dataKey="value"
              paddingAngle={3}
              onMouseEnter={(_,i) => setActiveIdx(i)}
              onMouseLeave={() => setActiveIdx(null)}
              labelLine={false}
              label={renderCustomLabel}
            >
              {data.map((e,i) => (
                <Cell
                  key={i}
                  fill={RICH_PALETTE[i % RICH_PALETTE.length]}
                  opacity={activeIdx === null || activeIdx === i ? 1 : 0.4}
                  stroke={activeIdx === i ? "#fff" : "none"}
                  strokeWidth={activeIdx === i ? 2 : 0}
                  style={{ cursor:"pointer", transition:"opacity 0.2s, transform 0.2s", filter: activeIdx === i ? `url(#glow${i})` : "none" }}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(v,n) => [fmt(v), n]}
              contentStyle={{ borderRadius:12, border:"1px solid var(--color-border)", background:"var(--color-bg-card)", color:"var(--color-text-primary)", fontSize:12, boxShadow:"var(--shadow-md)", padding:"10px 14px" }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Center label */}
        <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", textAlign:"center", pointerEvents:"none" }}>
          {activeEntry ? (
            <>
              <div style={{ fontSize:9, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:2 }}>
                {activeEntry.name.length > 10 ? activeEntry.name.slice(0,10)+"…" : activeEntry.name}
              </div>
              <div style={{ fontSize:16, fontWeight:800, color:"var(--color-text-primary)", letterSpacing:"-0.03em", lineHeight:1 }}>{activePct}%</div>
              <div style={{ fontSize:10, color:"var(--color-text-secondary)", marginTop:2, fontWeight:500 }}>{fmt(activeEntry.value)}</div>
            </>
          ) : (
            <>
              <div style={{ fontSize:9, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:2 }}>Total</div>
              <div style={{ fontSize:16, fontWeight:800, color:"var(--color-danger)", letterSpacing:"-0.03em", lineHeight:1 }}>{fmt(totalExpense)}</div>
              <div style={{ fontSize:9, color:"var(--color-text-tertiary)", marginTop:2 }}>expenses</div>
            </>
          )}
        </div>
      </div>
      {/* Legend */}
      <div style={{ display:"flex", flexDirection:"column", gap:0, marginTop:4, flex:1 }}>
        {data.slice(0,6).map((e,i) => {
          const pct = totalExpense > 0 ? ((e.value/totalExpense)*100).toFixed(1) : 0;
          const isActive = activeIdx === i;
          return (
            <div key={i}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseLeave={() => setActiveIdx(null)}
              style={{
                display:"flex", alignItems:"center", gap:8, padding:"5px 8px",
                borderRadius:8, cursor:"pointer",
                background: isActive ? `color-mix(in srgb, ${RICH_PALETTE[i]} 8%, transparent)` : "transparent",
                transition:"background 0.15s"
              }}>
              <div style={{ width:9, height:9, borderRadius:"50%", background:RICH_PALETTE[i], flexShrink:0, opacity: activeIdx===null||isActive ? 1 : 0.4 }} />
              <span style={{ fontSize:11, flex:1, color: isActive ? "var(--color-text-primary)" : "var(--color-text-secondary)", fontWeight: isActive ? 600 : 400, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{e.name}</span>
              <span style={{ fontSize:11, fontWeight:700, color: isActive ? RICH_PALETTE[i] : "var(--color-text-secondary)", flexShrink:0 }}>{fmt(e.value)}</span>
              <span style={{ fontSize:10, color:"var(--color-text-tertiary)", flexShrink:0, minWidth:32, textAlign:"right" }}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ accounts, transactions, receivables, debts, bills, goals, totalCash, totalBank, totalLiquid, totalAssets, totalDebts, totalReceivables, netWorth, monthlyIncome, monthlyExpense, monthlySavings, setTab, isMobile }) {
  const expByCategory = useMemo(() => {
    const m = {};
    transactions.filter(t => t.type==="expense" && t.category).forEach(t => { m[t.category]=(m[t.category]||0)+t.amount; });
    return Object.entries(m).map(([name,value]) => ({name,value})).sort((a,b)=>b.value-a.value);
  }, [transactions]);
  const totalExpense = useMemo(() => expByCategory.reduce((s,e)=>s+e.value,0), [expByCategory]);

  const dailyTrend = useMemo(() => {
    const m = {};
    transactions.forEach(t => {
      if (!m[t.date]) m[t.date] = { date:t.date, income:0, expense:0 };
      if (t.type==="income") m[t.date].income += t.amount; else m[t.date].expense += t.amount;
    });
    return Object.values(m).sort((a,b)=>a.date.localeCompare(b.date)).slice(-31)
      .map(d => ({ ...d, label:d.date.slice(5), savings:d.income-d.expense }));
  }, [transactions]);

  const monthlyTrend = useMemo(() => {
    const m = {};
    transactions.forEach(t => {
      const key = t.date.slice(0,7);
      if (!m[key]) m[key] = { key, income:0, expense:0 };
      if (t.type==="income") m[key].income += t.amount; else m[key].expense += t.amount;
    });
    return Object.values(m).sort((a,b)=>a.key.localeCompare(b.key)).slice(-12)
      .map(d => ({ ...d, label: MONTHS[parseInt(d.key.slice(5,7),10)-1] }));
  }, [transactions]);

  const upcomingBills = bills.filter(b => b.status==="unpaid");
  const overdueReceivables = receivables.filter(r => getDaysOverdue(r.dueDate) > 0 && r.paid < r.principal);
  const savingsRate = monthlyIncome > 0 ? ((monthlySavings/monthlyIncome)*100).toFixed(1) : 0;
  const dtiRatio = monthlyIncome > 0 ? ((totalDebts/(monthlyIncome*12))*100).toFixed(1) : 0;
  const recentTx = [...transactions].sort((a,b)=>b.date.localeCompare(a.date)).slice(0,5);

  // Financial Health Score
  const healthScore = useMemo(() => {
    let score = 0;
    if (parseFloat(savingsRate) >= 20) score += 25; else if (parseFloat(savingsRate) >= 10) score += 15; else score += 5;
    if (parseFloat(dtiRatio) <= 30) score += 25; else if (parseFloat(dtiRatio) <= 50) score += 15; else score += 5;
    if (totalCash >= monthlyExpense * 3) score += 25; else if (totalCash >= monthlyExpense) score += 15; else score += 5;
    if (overdueReceivables.length === 0) score += 15; else score += 5;
    if (upcomingBills.length === 0) score += 10; else score += 5;
    return score;
  }, [savingsRate, dtiRatio, totalCash, monthlyExpense, overdueReceivables, upcomingBills]);

  const healthColor = healthScore >= 75 ? "var(--color-success)" : healthScore >= 50 ? "var(--color-warning)" : "var(--color-danger)";
  const healthLabel = healthScore >= 75 ? "Excellent" : healthScore >= 50 ? "Good" : "Needs Attention";

  // SVG ring params
  const hR = 32, hC = 2*Math.PI*hR;
  const hFilled = hC * (healthScore/100);

  return (
    <div style={{ padding:"28px 32px" }}>
      {/* Header */}
      <div style={{ marginBottom:24, display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div>
          <div style={{ fontSize:11, fontWeight:600, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.12em", marginBottom:6 }}>{thisMonth} {thisYear}</div>
          <h1 style={{ fontSize:26, fontWeight:800, margin:0, letterSpacing:"-0.04em" }}>
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"} 👋
          </h1>
          <p style={{ margin:"5px 0 0", fontSize:13, color:"var(--color-text-secondary)" }}>Here's your financial overview at a glance</p>
        </div>
        {/* Health Score with SVG ring */}
        <div style={{ background:"var(--color-bg-card)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-xl)", padding:"16px 24px", display:"flex", alignItems:"center", gap:16, boxShadow:"var(--shadow-md)" }}>
          <div style={{ position:"relative", width:80, height:80 }}>
            <svg width={80} height={80} style={{ transform:"rotate(-90deg)" }}>
              <circle cx={40} cy={40} r={hR} fill="none" stroke={healthColor+"22"} strokeWidth={8} />
              <circle cx={40} cy={40} r={hR} fill="none" stroke={healthColor} strokeWidth={8}
                strokeDasharray={`${hFilled} ${hC}`} strokeLinecap="round" />
            </svg>
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
              <div style={{ fontSize:18, fontWeight:900, color:healthColor, lineHeight:1, letterSpacing:"-0.04em" }}>{healthScore}</div>
            </div>
          </div>
          <div>
            <div style={{ fontSize:10, fontWeight:600, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.10em", marginBottom:4 }}>Financial Health</div>
            <div style={{ fontSize:18, fontWeight:800, color:healthColor, letterSpacing:"-0.02em" }}>{healthLabel}</div>
            <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:3 }}>Score out of 100</div>
          </div>
        </div>
      </div>

      {/* Hero KPI row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:14, marginBottom:14 }}>
        <GradientStatCard label="Net Savings" value={fmtShort(monthlySavings)} sub={`${savingsRate}% savings rate`} icon="ti-piggy-bank" gradient={monthlySavings>=0?"var(--gradient-emerald)":"var(--gradient-rose)"} />
        <GradientStatCard label="Monthly Income" value={fmtShort(monthlyIncome)} icon="ti-cash" gradient="var(--gradient-blue)" trendValue={8.3} />
        <GradientStatCard label="Monthly Expense" value={fmtShort(monthlyExpense)} icon="ti-receipt" gradient="var(--gradient-rose)" />
      </div>

      {/* Secondary KPI row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Net Worth" value={fmtShort(netWorth)} color="var(--color-success)" icon="ti-trending-up" />
        <MetricCard label="Total Assets" value={fmtShort(totalAssets)} color="var(--color-accent)" icon="ti-building-bank" />
        <MetricCard label="Total Liabilities" value={fmtShort(totalDebts)} color="var(--color-danger)" icon="ti-credit-card" />
        <MetricCard label="Receivables" value={fmtShort(totalReceivables)} color="var(--color-purple)" icon="ti-users" />
        <MetricCard label="Cash & Wallets" value={fmtShort(totalCash)} color="var(--color-warning)" icon="ti-wallet" />
      </div>

      {/* Charts Row 1 */}
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:16 }}>
        <Card className="chart-card" style={{ background:"linear-gradient(160deg,#0F1117 0%,#1A1F2E 100%)", border:"none", boxShadow:"0 20px 60px rgba(0,0,0,0.18)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.10em", marginBottom:4 }}>Cash Flow</div>
              <SectionTitle style={{ color:"#FFFFFF", margin:0 }}>Daily Cash Flow</SectionTitle>
            </div>
            <div style={{ display:"flex", gap:16 }}>
              {[{label:"Income",color:"#34D399"},{label:"Expense",color:"#F87171"},{label:"Net",color:"#60A5FA"}].map(l=>(
                <div key={l.label} style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:l.color }} />
                  <span style={{ fontSize:11, color:"rgba(255,255,255,0.5)", fontWeight:500 }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={dailyTrend} margin={{top:4,right:4,left:0,bottom:0}}>
              <defs>
                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34D399" stopOpacity={0.25}/>
                  <stop offset="100%" stopColor="#34D399" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F87171" stopOpacity={0.20}/>
                  <stop offset="100%" stopColor="#F87171" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 6" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{fontSize:11, fill:"rgba(255,255,255,0.35)", fontWeight:500}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize:10, fill:"rgba(255,255,255,0.28)"}} tickFormatter={v=>"₱"+v/1000+"K"} axisLine={false} tickLine={false} width={48} />
              <Tooltip
                contentStyle={{ background:"#1E2435", border:"1px solid rgba(255,255,255,0.10)", borderRadius:12, fontSize:12, color:"#fff", padding:"10px 14px" }}
                formatter={(v,n)=>[fmt(v),n]}
                labelStyle={{ color:"rgba(255,255,255,0.6)", fontWeight:600, marginBottom:4 }}
                cursor={{ fill:"rgba(255,255,255,0.04)" }}
              />
              <Area type="monotone" dataKey="income" stroke="#34D399" fill="url(#incomeGrad)" name="Income" strokeWidth={2.5} dot={false} activeDot={{ r:5, fill:"#34D399", stroke:"#0F1117", strokeWidth:2 }} />
              <Area type="monotone" dataKey="expense" stroke="#F87171" fill="url(#expenseGrad)" name="Expense" strokeWidth={2.5} dot={false} activeDot={{ r:5, fill:"#F87171", stroke:"#0F1117", strokeWidth:2 }} />
              <Bar dataKey="savings" name="Net" fill="#60A5FA" opacity={0.85} radius={[4,4,0,0]} barSize={10} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>

        <PremiumExpensePie expByCategory={expByCategory} totalExpense={totalExpense} />
      </div>

      {/* Charts Row 2 */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:16 }}>
        <Card className="chart-card">
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, fontWeight:600, color:"var(--color-accent)", textTransform:"uppercase", letterSpacing:"0.10em", marginBottom:2 }}>Monthly Trend</div>
            <div style={{ fontSize:22, fontWeight:800, color:"var(--color-text-primary)", letterSpacing:"-0.03em" }}>{fmt(monthlySavings)}</div>
            <div style={{ fontSize:11, color:"var(--color-text-secondary)", marginTop:2, fontWeight:500 }}>net this month</div>
          </div>
          <ResponsiveContainer width="100%" height={110}>
            <BarChart data={monthlyTrend} margin={{top:4,right:0,left:0,bottom:0}} barGap={2}>
              <XAxis dataKey="label" tick={{fontSize:9.5, fill:"var(--color-text-tertiary)"}} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip formatter={(v)=>fmt(v)} contentStyle={{ borderRadius:10, border:"1px solid var(--color-border)", fontSize:11, boxShadow:"0 4px 16px rgba(15,17,23,0.10)" }} labelStyle={{ fontWeight:600, color:"var(--color-text-primary)" }} />
              <Bar dataKey="income" name="Income" fill="var(--color-success)" radius={[3,3,0,0]} barSize={8} />
              <Bar dataKey="expense" name="Expense" fill="var(--color-danger)" radius={[3,3,0,0]} barSize={8} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <SectionTitle>Financial Ratios</SectionTitle>
            <span style={{ fontSize:10, color:"var(--color-text-tertiary)" }}>vs benchmarks</span>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {[
              { label:"Savings Rate", value:parseFloat(savingsRate), target:20, color:"var(--color-success)", unit:"%", tip:"Target ≥ 20%" },
              { label:"Expense Ratio", value:monthlyIncome>0?parseFloat(((monthlyExpense/monthlyIncome)*100).toFixed(1)):0, target:80, color:"var(--color-danger)", unit:"%", tip:"Target < 80%" },
              { label:"Debt-to-Income", value:parseFloat(dtiRatio), target:30, color:"var(--color-warning)", unit:"%", tip:"Target < 30%" },
            ].map(r => {
              const capped = Math.min(100, r.value);
              const ok = r.label==="Savings Rate" ? r.value >= r.target : r.value <= r.target;
              return (
                <div key={r.label}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                    <div>
                      <span style={{ fontSize:12, fontWeight:500, color:"var(--color-text-primary)" }}>{r.label}</span>
                      <span style={{ fontSize:10, color:"var(--color-text-tertiary)", marginLeft:6 }}>{r.tip}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:13, fontWeight:800, color:r.color, fontFamily:"var(--font-mono)" }}>{r.value}{r.unit}</span>
                      <span style={{ fontSize:9, fontWeight:700, color: ok?"var(--color-success)":"var(--color-danger)", background: ok?"var(--color-success-subtle)":"var(--color-danger-subtle)", padding:"2px 5px", borderRadius:99 }}>{ok?"✓":"!"}</span>
                    </div>
                  </div>
                  <div style={{ height:7, background:"var(--color-bg-subtle)", borderRadius:99, overflow:"hidden", position:"relative" }}>
                    <div style={{ position:"absolute", left:`${Math.min(99,r.target)}%`, top:0, bottom:0, width:2, background:`color-mix(in srgb, ${r.color} 55%, transparent)`, zIndex:1 }} />
                    <div style={{ height:"100%", width:`${capped}%`, background:`linear-gradient(90deg, color-mix(in srgb, ${r.color} 88%, transparent), ${r.color})`, borderRadius:99, transition:"width 0.6s ease" }} />
                  </div>
                </div>
              );
            })}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 10px", background:"var(--color-accent-subtle)", borderRadius:"var(--radius-sm)", marginTop:2 }}>
              <span style={{ fontSize:11, color:"var(--color-accent)", fontWeight:500 }}>Emergency Fund</span>
              <span style={{ fontSize:13, fontWeight:800, color:"var(--color-accent)", fontFamily:"var(--font-mono)" }}>{monthlyExpense>0?(150000/monthlyExpense).toFixed(1):0} mo.</span>
            </div>
          </div>
        </Card>

        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <Card style={{ flex:1 }}>
            <SectionTitle>Upcoming Bills</SectionTitle>
            {upcomingBills.length === 0
              ? <p style={{ fontSize:12, color:"var(--color-text-secondary)" }}>All bills paid ✓</p>
              : upcomingBills.slice(0,3).map(b => (
                <div key={b.id} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid var(--color-border-subtle)" }}>
                  <div style={{ fontSize:12, fontWeight:500 }}>{b.name}</div>
                  <div style={{ fontSize:12, fontWeight:600, color:"var(--color-warning)" }}>{fmt(b.amount)}</div>
                </div>
              ))
            }
          </Card>
          <Card style={{ flex:1 }}>
            <SectionTitle>Alerts</SectionTitle>
            {upcomingBills.length > 0 && <AlertRow icon="ti-bell" text={`${upcomingBills.length} unpaid bills`} color="var(--color-warning)" />}
            {overdueReceivables.length > 0 && <AlertRow icon="ti-alert-triangle" text={`${overdueReceivables.length} overdue receivables`} color="var(--color-danger)" />}
            {monthlySavings < 0 && <AlertRow icon="ti-trending-down" text="Expenses exceed income" color="var(--color-danger)" />}
            {totalCash < 5000 && <AlertRow icon="ti-cash" text="Low cash balance" color="var(--color-orange)" />}
            {upcomingBills.length===0 && overdueReceivables.length===0 && monthlySavings>=0 && totalCash>=5000 && <p style={{ fontSize:12, color:"var(--color-text-secondary)" }}>No alerts — you're on track 🎉</p>}
          </Card>
        </div>
      </div>

      {/* Bottom Row */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr 1fr", gap:16 }}>
        <Card>
          <SectionTitle>Account Balances</SectionTitle>
          <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
            {accounts.map(a => {
              const pct = (a.balance / totalLiquid) * 100;
              return (
                <div key={a.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:"1px solid var(--color-border-subtle)" }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:a.color+"22", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>
                    {getAccountIcon(a.type)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500 }}>{a.name}</div>
                    <div style={{ height:4, borderRadius:99, background:"var(--color-border)", marginTop:5, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:a.color, borderRadius:99 }} />
                    </div>
                  </div>
                  <div style={{ fontSize:14, fontWeight:700, flexShrink:0, letterSpacing:"-0.02em" }}>{fmt(a.balance)}</div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card>
          <SectionTitle>Top Expense Categories</SectionTitle>
          <div style={{ display:"flex", flexDirection:"column", gap:11, marginTop:4 }}>
            {expByCategory.slice(0,6).map((e,i) => {
              const maxVal = expByCategory[0]?.value || 1;
              const pct = (e.value/maxVal)*100;
              return (
                <div key={e.name}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:5 }}>
                    <span style={{ fontWeight:500, color:"var(--color-text-primary)" }}>{e.name}</span>
                    <span style={{ fontWeight:700, color:`var(--chart-${(i%8)+1})` }}>{fmtShort(e.value)}</span>
                  </div>
                  <div style={{ height:7, borderRadius:99, background:"var(--color-bg-subtle)", overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct}%`, background:`var(--chart-${(i%8)+1})`, borderRadius:99, transition:"width 0.6s ease" }} />
                  </div>
                </div>
              );
            })}
            {expByCategory.length===0 && <p style={{ fontSize:12, color:"var(--color-text-secondary)" }}>No expenses recorded yet.</p>}
          </div>
        </Card>

        <Card>
          <SectionTitle>Recent Transactions</SectionTitle>
          <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
            {recentTx.map(t => (
              <div key={t.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 0", borderBottom:"1px solid var(--color-border-subtle)" }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:150 }}>{t.desc}</div>
                  <div style={{ fontSize:10, color:"var(--color-text-tertiary)", marginTop:1 }}>{t.date} · {t.category}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:700, color: isInflow(t)?"var(--color-success)":"var(--color-danger)", flexShrink:0 }}>
                  {isInflow(t)?"+":"-"}{fmt(t.amount)}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function AlertRow({ icon, text, color }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid var(--color-border-subtle)" }}>
      <i className={`ti ${icon}`} style={{ fontSize:13, color, flexShrink:0 }} aria-hidden="true" />
      <span style={{ fontSize:12 }}>{text}</span>
    </div>
  );
}

// ─── ACCOUNTS PAGE ────────────────────────────────────────────────────────────
function AccountsPage({ accounts, setAccounts, transactions, reconciliations, setReconciliations, selectedAccount, setSelectedAccount, session }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview | reconcile
  const [reconcileAccount, setReconcileAccount] = useState(null);
  const [statementBalance, setStatementBalance] = useState("");
  const [clearedTxIds, setClearedTxIds] = useState(new Set());
  const [reconcileDate, setReconcileDate] = useState(today());
  const [reconcileNotes, setReconcileNotes] = useState("");

  const [form, setForm] = useState({name:"", type:"checking", balance:"", institution:"", accountNo:"", color:"#2563EB"});
  const blankForm = {name:"", type:"checking", balance:"", institution:"", accountNo:"", color:"#2563EB"};

  const openNew = () => { setForm(blankForm); setEditing(null); setShowForm(true); };
  const openEdit = (a) => { setForm({name:a.name, type:a.type, balance:String(a.balance), institution:a.institution||"", accountNo:a.accountNo||"", color:a.color||"#2563EB"}); setEditing(a.id); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); };

  const save = async () => {
    if (!form.name || !form.balance) return;
    if (editing) {
      await supabase.from('accounts').update({
        name: form.name, type: form.type, balance: parseFloat(form.balance),
        institution: form.institution, account_no: form.accountNo, color: form.color
      }).eq('id', editing);
      setAccounts(as => as.map(a => a.id===editing ? {...a, ...form, balance:parseFloat(form.balance)} : a));
    } else {
      const newAccount = {id:uid(), ...form, balance:parseFloat(form.balance), currency:"PHP", reconciled:false};
     await supabase.from('accounts').insert([{
        id: newAccount.id, name: newAccount.name, type: newAccount.type,
        balance: newAccount.balance, currency: "PHP", color: newAccount.color,
        institution: newAccount.institution, account_no: newAccount.accountNo, 
        reconciled: false, user_id: session.user.id
      }]);
      setAccounts(as => [...as, newAccount]);
    }
    cancel();
  };

 const del = async (id) => { 
    if (window.confirm("Delete this account? This cannot be undone.")) {
      await supabase.from('accounts').delete().eq('id', id);
      setAccounts(as => as.filter(a => a.id!==id)); 
    }
  };

  const startReconcile = (account) => {
    setReconcileAccount(account);
    setStatementBalance("");
    setReconcileDate(today());
    setReconcileNotes("");
    const existingCleared = new Set(transactions.filter(t => t.account===account.id && t.cleared).map(t=>t.id));
    setClearedTxIds(existingCleared);
    setActiveTab("reconcile");
  };

  const finishReconcile = (force=false) => {
    const stmtBal = parseFloat(statementBalance);
    if (isNaN(stmtBal)) { alert("Please enter a statement balance."); return; }
    if (!reconcileDate) { alert("Please select a reconciliation date."); return; }
    const diff = stmtBal - reconcileAccount.balance;
    const lastRec = reconciliations.filter(r=>r.accountId===reconcileAccount.id).sort((a,b)=>b.date.localeCompare(a.date))[0];
    if (!force && Math.abs(diff) > 0.01) {
      // still let them save with a warning — don't block
      if (!window.confirm(`⚠️ Difference of ${fmt(Math.abs(diff))} remains. Save anyway?`)) return;
    }
    setReconciliations(rs => [...rs, {
      id: uid(),
      accountId: reconcileAccount.id,
      date: reconcileDate,
      openingBalance: lastRec ? lastRec.closingBalance : reconcileAccount.balance,
      closingBalance: stmtBal,
      clearedCount: clearedTxIds.size,
      notes: reconcileNotes,
      status: Math.abs(diff) < 0.01 ? "completed" : "pending",
      difference: diff
    }]);
    setAccounts(as => as.map(a => a.id===reconcileAccount.id ? {...a, reconciled:true, lastReconciled:reconcileDate} : a));
    setActiveTab("overview");
    setReconcileAccount(null);
  };

  if (selectedAccount) {
    const a = accounts.find(acc => acc.id === selectedAccount);
    if (!a) { setSelectedAccount(null); return null; }
    return <AccountDetail account={a} accounts={accounts} transactions={transactions} onBack={() => setSelectedAccount(null)} onEdit={() => { openEdit(a); setSelectedAccount(null); }} />;
  }

  const totalLiquid = accounts.reduce((s,a) => s+a.balance, 0);

  return (
    <Page title="Accounts & Wallets" subtitle="All your money accounts in one place"
      action={<Btn onClick={openNew} icon="ti-plus">New Account</Btn>}>

      {showForm && (
        <Modal title={editing ? "Edit Account" : "New Account"} onClose={cancel}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
            <Input label="Account Name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. BDO Savings" />
            <Select label="Type" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
              {ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
            </Select>
            <Input label="Current Balance (₱)" type="number" value={form.balance} onChange={e=>setForm(f=>({...f,balance:e.target.value}))} placeholder="0.00" />
            <Input label="Institution / Bank" value={form.institution} onChange={e=>setForm(f=>({...f,institution:e.target.value}))} placeholder="e.g. BPI" />
            <Input label="Account Number (masked)" value={form.accountNo} onChange={e=>setForm(f=>({...f,accountNo:e.target.value}))} placeholder="****1234" />
            <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)" }}>
              Card Color
              <input type="color" value={form.color} onChange={e=>setForm(f=>({...f,color:e.target.value}))} style={{ display:"block", marginTop:4, width:"100%", height:36, border:"1px solid var(--color-border)", borderRadius:"var(--radius-sm)", cursor:"pointer", padding:2 }} />
            </label>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={save} icon="ti-check">{editing ? "Update" : "Save Account"}</Btn>
            <Btn onClick={cancel} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Tab bar for reconcile mode */}
      {activeTab === "reconcile" && reconcileAccount && (
        <ReconcileView
          account={reconcileAccount}
          transactions={transactions.filter(t => t.account===reconcileAccount.id)}
          statementBalance={statementBalance} setStatementBalance={setStatementBalance}
          reconcileDate={reconcileDate} setReconcileDate={setReconcileDate}
          reconcileNotes={reconcileNotes} setReconcileNotes={setReconcileNotes}
          clearedTxIds={clearedTxIds} setClearedTxIds={setClearedTxIds}
          onFinish={finishReconcile} onCancel={() => { setActiveTab("overview"); setReconcileAccount(null); }}
        />
      )}

      {activeTab === "overview" && (
        <>
          {/* Summary strip */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
            <MetricCard label="Total Balance" value={fmt(totalLiquid)} color="var(--color-accent)" icon="ti-wallet" />
            <MetricCard label="Bank Accounts" value={fmt(accounts.filter(a=>["checking","savings"].includes(a.type)).reduce((s,a)=>s+a.balance,0))} color="var(--color-success)" icon="ti-building-bank" />
            <MetricCard label="E-Wallets & Cash" value={fmt(accounts.filter(a=>!["checking","savings"].includes(a.type)).reduce((s,a)=>s+a.balance,0))} color="var(--color-warning)" icon="ti-device-mobile" />
            <MetricCard label="Accounts" value={accounts.length} color="var(--color-purple)" icon="ti-list" />
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:16, marginBottom:24 }}>
            {accounts.map(a => {
              const accTx = transactions.filter(t => t.account===a.id);
              const deposits = accTx.filter(t=>isInflow(t)).reduce((s,t)=>s+t.amount,0);
              const withdrawals = accTx.filter(t=>!isInflow(t)).reduce((s,t)=>s+t.amount,0);
              const txCount = accTx.length;
              return (
                <div key={a.id} style={{ background:`linear-gradient(135deg, ${a.color}18 0%, ${a.color}08 100%)`, border:`1px solid ${a.color}30`, borderRadius:"var(--radius-lg)", padding:"20px", boxShadow:"var(--shadow-sm)", cursor:"pointer", transition:"var(--transition)" }}
                  onClick={() => setSelectedAccount(a.id)}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow="var(--shadow-md)"; e.currentTarget.style.transform="translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow="var(--shadow-sm)"; e.currentTarget.style.transform="translateY(0)"; }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ width:40, height:40, borderRadius:10, background:a.color+"33", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>
                        {getAccountIcon(a.type)}
                      </div>
                      <div>
                        <div style={{ fontSize:14, fontWeight:600 }}>{a.name}</div>
                        <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:1 }}>{a.institution || "—"} · {a.accountNo || "—"}</div>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:6 }}>
                      <badge style={{ background:a.reconciled?"var(--color-success-subtle)":"var(--color-warning-subtle)", color:a.reconciled?"var(--color-success)":"var(--color-warning)", fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:99 }}>{a.reconciled?"Reconciled":"Unreconciled"}</badge>
                    </div>
                  </div>
                  <div style={{ fontSize:26, fontWeight:800, color:"var(--color-text-primary)", letterSpacing:"-0.03em", marginBottom:12 }}>{fmt(a.balance)}</div>
                  <div style={{ display:"flex", gap:0, marginBottom:12 }}>
                    <div style={{ flex:1, borderRight:"1px solid var(--color-border)" }}>
                      <div style={{ fontSize:10, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.05em" }}>In</div>
                      <div style={{ fontSize:13, fontWeight:600, color:"var(--color-success)", marginTop:2 }}>+{fmt(deposits)}</div>
                    </div>
                    <div style={{ flex:1, paddingLeft:12 }}>
                      <div style={{ fontSize:10, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Out</div>
                      <div style={{ fontSize:13, fontWeight:600, color:"var(--color-danger)", marginTop:2 }}>-{fmt(withdrawals)}</div>
                    </div>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontSize:11, color:"var(--color-text-tertiary)" }}>{txCount} transactions</span>
                    <div style={{ display:"flex", gap:6 }}>
                      <Btn onClick={e=>{e.stopPropagation();startReconcile(a);}} variant="secondary" size="sm" icon="ti-check">Reconcile</Btn>
                      <Btn onClick={e=>{e.stopPropagation();openEdit(a);}} variant="secondary" size="sm" icon="ti-edit" />
                      <Btn onClick={e=>{e.stopPropagation();del(a.id);}} variant="danger" size="sm" icon="ti-trash" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reconciliation History */}
          {reconciliations.length > 0 && (
            <Card>
              <SectionTitle>Reconciliation History</SectionTitle>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:"1px solid var(--color-border)" }}>
                    {["Account","Date","Opening","Closing","Transactions","Status"].map(h=>(
                      <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reconciliations.map(rec => {
                    const acc = accounts.find(a=>a.id===rec.accountId);
                    return (
                      <tr key={rec.id} style={{ borderBottom:"1px solid var(--color-border-subtle)" }}>
                        <td style={{ padding:"10px 12px", fontWeight:500 }}>{acc?.name||"—"}</td>
                        <td style={{ padding:"10px 12px", color:"var(--color-text-secondary)" }}>{rec.date}</td>
                        <td style={{ padding:"10px 12px" }}>{fmt(rec.openingBalance)}</td>
                        <td style={{ padding:"10px 12px" }}>{fmt(rec.closingBalance)}</td>
                        <td style={{ padding:"10px 12px", color:"var(--color-text-secondary)" }}>{rec.clearedCount}</td>
                        <td style={{ padding:"10px 12px" }}><Badge color="green">Completed</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </Page>
  );
}

function AccountDetail({ account, accounts, transactions, onBack, onEdit }) {
  const accTx = useMemo(() => transactions.filter(t=>t.account===account.id).sort((a,b)=>b.date.localeCompare(a.date)), [transactions, account.id]);
  const deposits = accTx.filter(t=>isInflow(t)).reduce((s,t)=>s+t.amount,0);
  const withdrawals = accTx.filter(t=>!isInflow(t)).reduce((s,t)=>s+t.amount,0);

  // running balance
  const txWithBalance = useMemo(() => {
    let running = account.balance;
    return accTx.map(t => {
      const entry = { ...t, runningBalance: running };
      if (isInflow(t)) running -= t.amount; else running += t.amount;
      return entry;
    });
  }, [accTx, account.balance]);

  // monthly activity for mini chart
  const monthlyActivity = useMemo(() => {
    const m = {};
    accTx.forEach(t => {
      const mo = t.date.slice(0,7);
      if (!m[mo]) m[mo] = {month:mo, in:0, out:0};
      if (isInflow(t)) m[mo].in += t.amount; else m[mo].out += t.amount;
    });
    return Object.values(m).sort((a,b)=>a.month.localeCompare(b.month)).slice(-6);
  }, [accTx]);

  return (
    <div style={{ padding:"28px 32px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
        <Btn onClick={onBack} variant="ghost" icon="ti-arrow-left" size="sm">Back</Btn>
        <div style={{ display:"flex", alignItems:"center", gap:12, flex:1 }}>
          <div style={{ width:44, height:44, borderRadius:11, background:account.color+"33", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>
            {getAccountIcon(account.type)}
          </div>
          <div>
            <h1 style={{ fontSize:20, fontWeight:700, margin:0, letterSpacing:"-0.02em" }}>{account.name}</h1>
            <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginTop:2 }}>{account.institution} · {account.accountNo}</div>
          </div>
        </div>
        <Btn onClick={onEdit} variant="secondary" icon="ti-edit" size="sm">Edit</Btn>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Current Balance" value={fmt(account.balance)} color={account.color} icon="ti-wallet" />
        <MetricCard label="Total Deposits" value={fmt(deposits)} color="var(--color-success)" icon="ti-arrow-down" />
        <MetricCard label="Total Withdrawals" value={fmt(withdrawals)} color="var(--color-danger)" icon="ti-arrow-up" />
        <MetricCard label="Transactions" value={accTx.length} color="var(--color-purple)" icon="ti-transfer" />
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:16 }}>
        <Card>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <SectionTitle>Monthly Activity</SectionTitle>
            <div style={{ display:"flex", gap:12 }}>
              {[{l:"Deposits",c:"#059669"},{l:"Withdrawals",c:"#DC2626"}].map(x=>(
                <div key={x.l} style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:x.c }} />
                  <span style={{ fontSize:10, color:"var(--color-text-tertiary)", fontWeight:500 }}>{x.l}</span>
                </div>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={monthlyActivity} barGap={3}>
              <defs>
                <linearGradient id="depGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#059669" stopOpacity={0.9}/><stop offset="100%" stopColor="#059669" stopOpacity={0.5}/>
                </linearGradient>
                <linearGradient id="wthGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#DC2626" stopOpacity={0.9}/><stop offset="100%" stopColor="#DC2626" stopOpacity={0.5}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 6" stroke="var(--color-border)" vertical={false} />
              <XAxis dataKey="month" tick={{fontSize:10, fill:"var(--color-text-tertiary)"}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize:10, fill:"var(--color-text-tertiary)"}} tickFormatter={v=>"₱"+v/1000+"K"} axisLine={false} tickLine={false} width={42} />
              <Tooltip formatter={(v,n)=>[fmt(v),n]} contentStyle={{ borderRadius:12, border:"1px solid var(--color-border)", fontSize:12, boxShadow:"0 8px 24px rgba(15,17,23,0.10)", padding:"10px 14px" }} cursor={{ fill:"var(--color-bg-subtle)", radius:4 }} />
              <Bar dataKey="in" name="Deposits" fill="url(#depGrad)" radius={[5,5,0,0]} barSize={16} />
              <Bar dataKey="out" name="Withdrawals" fill="url(#wthGrad)" radius={[5,5,0,0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionTitle>Account Status</SectionTitle>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
              <span style={{ color:"var(--color-text-secondary)" }}>Currency</span>
              <span style={{ fontWeight:600 }}>{account.currency || "PHP"}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
              <span style={{ color:"var(--color-text-secondary)" }}>Reconciliation</span>
              <Badge color={account.reconciled?"green":"amber"}>{account.reconciled?"Reconciled":"Pending"}</Badge>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
              <span style={{ color:"var(--color-text-secondary)" }}>Net Cash Flow</span>
              <span style={{ fontWeight:700, color: deposits-withdrawals>=0?"var(--color-success)":"var(--color-danger)" }}>{deposits-withdrawals>=0?"+":""}{fmt(deposits-withdrawals)}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
              <span style={{ color:"var(--color-text-secondary)" }}>% of Portfolio</span>
              <span style={{ fontWeight:600 }}>{((account.balance / (accounts.reduce((s,a)=>s+a.balance,0) || 1))*100).toFixed(1)}%</span>
            </div>
          </div>
        </Card>
      </div>

      <Card style={{ padding:0 }}>
        <div style={{ padding:"16px 20px", borderBottom:"1px solid var(--color-border)" }}>
          <SectionTitle>Transaction History — Running Balance</SectionTitle>
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"var(--color-bg-subtle)", borderBottom:"1px solid var(--color-border)" }}>
              {["Date","Description","Category","Amount","Running Balance","Cleared"].map(h=>(
                <th key={h} style={{ padding:"10px 16px", textAlign: ["Amount","Running Balance"].includes(h)?"right":"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {txWithBalance.map(t => (
              <tr key={t.id} style={{ borderBottom:"1px solid var(--color-border-subtle)" }}>
                <td style={{ padding:"10px 16px", color:"var(--color-text-secondary)", fontSize:12 }}>{t.date}</td>
                <td style={{ padding:"10px 16px", fontWeight:500 }}>{t.desc}</td>
                <td style={{ padding:"10px 16px" }}><Badge color={t.type==="income"?"green":"gray"} size="xs">{t.category}</Badge></td>
                <td style={{ padding:"10px 16px", textAlign:"right", fontWeight:600, color: isInflow(t)?"var(--color-success)":"var(--color-danger)" }}>
                  {isInflow(t)?"+":"-"}{fmt(t.amount)}
                </td>
                <td style={{ padding:"10px 16px", textAlign:"right", fontWeight:500, fontFamily:"var(--font-mono)", fontSize:12 }}>{fmt(t.runningBalance)}</td>
                <td style={{ padding:"10px 16px" }}>
                  <Badge color={t.cleared?"green":"gray"} size="xs">{t.cleared?"Cleared":"Pending"}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {accTx.length===0 && <EmptyState icon="📋" title="No transactions yet" desc="Add transactions linked to this account." />}
      </Card>
    </div>
  );
}

function ReconcileView({ account, transactions, statementBalance, setStatementBalance, reconcileDate, setReconcileDate, reconcileNotes, setReconcileNotes, clearedTxIds, setClearedTxIds, onFinish, onCancel }) {
  const stmtBal = parseFloat(statementBalance)||0;
  const diff = stmtBal - account.balance;
  const cleared = clearedTxIds.size;
  const outstanding = transactions.length - cleared;
  const balanced = Math.abs(diff) < 0.01;

  const toggleCleared = (id) => setClearedTxIds(s => {
    const ns = new Set(s);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    return ns;
  });

  return (
    <div style={{ animation:"fadeUp 0.3s ease" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={onCancel} style={{ background:"none", border:"1px solid var(--color-border)", borderRadius:8, padding:"7px 10px", cursor:"pointer", color:"var(--color-text-secondary)", display:"flex", alignItems:"center", gap:5, fontSize:12, fontWeight:500 }}>
            <i className="ti ti-arrow-left" style={{ fontSize:14 }} /> Back
          </button>
          <div>
            <h2 style={{ fontSize:18, fontWeight:800, margin:0, letterSpacing:"-0.03em" }}>Reconcile — {account.name}</h2>
            <p style={{ margin:"3px 0 0", fontSize:12, color:"var(--color-text-secondary)" }}>Mark transactions as cleared to match your bank statement</p>
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <Btn onClick={()=>onFinish(false)} icon={balanced?"ti-check":"ti-device-floppy"} variant={balanced?"success":"primary"}>
            {balanced ? "Complete & Save" : "Save Reconciliation"}
          </Btn>
        </div>
      </div>

      {/* Setup Row — date, statement balance, notes */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:16 }}>
        <Card style={{ border:"1.5px solid var(--color-border)" }}>
          <div style={{ fontSize:10, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>Reconciliation Date</div>
          <input type="date" value={reconcileDate} onChange={e=>setReconcileDate(e.target.value)}
            style={{ fontSize:15, fontWeight:700, border:"none", background:"transparent", outline:"none", padding:0, color:"var(--color-text-primary)", width:"100%", cursor:"pointer" }} />
          <div style={{ fontSize:10, color:"var(--color-text-tertiary)", marginTop:6 }}>Statement end date</div>
        </Card>
        <Card style={{ border:"1.5px solid " + (balanced ? "var(--color-success)" : stmtBal > 0 ? "var(--color-warning)" : "var(--color-border)"), background: balanced ? "var(--color-success-subtle)" : undefined }}>
          <div style={{ fontSize:10, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Statement Balance</div>
          <div style={{ display:"flex", alignItems:"baseline", gap:3 }}>
            <span style={{ fontSize:15, fontWeight:700, color:"var(--color-text-tertiary)" }}>₱</span>
            <input type="number" value={statementBalance} onChange={e=>setStatementBalance(e.target.value)}
              placeholder="0.00"
              style={{ fontSize:20, fontWeight:800, border:"none", background:"transparent", outline:"none", padding:0, color:"var(--color-text-primary)", width:"100%", letterSpacing:"-0.03em" }} />
          </div>
          <div style={{ fontSize:10, color:"var(--color-text-tertiary)", marginTop:4 }}>From your bank/statement</div>
        </Card>
        <Card style={{ border:"1.5px solid var(--color-border)" }}>
          <div style={{ fontSize:10, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Notes (optional)</div>
          <input type="text" value={reconcileNotes} onChange={e=>setReconcileNotes(e.target.value)}
            placeholder="e.g. June 2025 statement…"
            style={{ fontSize:13, border:"none", background:"transparent", outline:"none", padding:0, color:"var(--color-text-primary)", width:"100%" }} />
        </Card>
      </div>

      {/* Status strip */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
        <div style={{ background:"var(--color-bg-card)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"14px 16px", boxShadow:"var(--shadow-sm)" }}>
          <div style={{ fontSize:10, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Book Balance</div>
          <div style={{ fontSize:18, fontWeight:800, letterSpacing:"-0.02em" }}>{fmt(account.balance)}</div>
        </div>
        <div style={{ background:"var(--color-bg-card)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"14px 16px", boxShadow:"var(--shadow-sm)" }}>
          <div style={{ fontSize:10, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Statement Balance</div>
          <div style={{ fontSize:18, fontWeight:800, letterSpacing:"-0.02em", color: stmtBal > 0 ? "var(--color-text-primary)" : "var(--color-text-tertiary)" }}>{stmtBal > 0 ? fmt(stmtBal) : "—"}</div>
        </div>
        <div style={{ background: balanced ? "var(--color-success-subtle)" : "var(--color-danger-subtle)", border:`1px solid ${balanced?"var(--color-success)":"var(--color-danger)"}30`, borderRadius:"var(--radius-md)", padding:"14px 16px" }}>
          <div style={{ fontSize:10, color: balanced?"var(--color-success)":"var(--color-danger)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>
            {balanced ? "✓ Balanced" : "Difference"}
          </div>
          <div style={{ fontSize:18, fontWeight:800, letterSpacing:"-0.02em", color: balanced?"var(--color-success)":"var(--color-danger)" }}>
            {balanced ? "₱0" : fmt(Math.abs(diff))}
          </div>
        </div>
        <div style={{ background:"var(--color-bg-card)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"14px 16px", boxShadow:"var(--shadow-sm)" }}>
          <div style={{ fontSize:10, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Cleared</div>
          <div style={{ fontSize:18, fontWeight:800, letterSpacing:"-0.02em" }}>{cleared} <span style={{ fontSize:13, color:"var(--color-text-tertiary)", fontWeight:500 }}>of {transactions.length}</span></div>
        </div>
      </div>

      {/* Transaction table */}
      <Card style={{ padding:0 }}>
        <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--color-border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <span style={{ fontSize:13, fontWeight:700 }}>Transactions</span>
            <span style={{ fontSize:12, color:"var(--color-text-secondary)", marginLeft:8 }}>Click to mark as cleared</span>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn size="sm" variant="ghost" onClick={()=>setClearedTxIds(new Set(transactions.map(t=>t.id)))}>Select All</Btn>
            <Btn size="sm" variant="ghost" onClick={()=>setClearedTxIds(new Set())}>Clear All</Btn>
          </div>
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"var(--color-bg-subtle)", borderBottom:"1px solid var(--color-border)" }}>
              {["","Date","Description","Amount","Status"].map(h=>(
                <th key={h} style={{ padding:"9px 14px", textAlign: h==="Amount"?"right":"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {transactions.sort((a,b)=>b.date.localeCompare(a.date)).map(t => {
              const isCleared = clearedTxIds.has(t.id);
              return (
                <tr key={t.id} onClick={()=>toggleCleared(t.id)}
                  style={{ borderBottom:"1px solid var(--color-border-subtle)", cursor:"pointer", background: isCleared?"var(--color-success-subtle)":undefined, transition:"background 0.12s" }}>
                  <td style={{ padding:"10px 14px 10px 18px", width:36 }}>
                    <div style={{ width:20, height:20, borderRadius:5, border:`2px solid ${isCleared?"var(--color-success)":"var(--color-border)"}`, background: isCleared?"var(--color-success)":"transparent", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s", flexShrink:0 }}>
                      {isCleared && <i className="ti ti-check" style={{ fontSize:11, color:"#fff" }} />}
                    </div>
                  </td>
                  <td style={{ padding:"10px 14px", color:"var(--color-text-secondary)", fontSize:12, whiteSpace:"nowrap" }}>{t.date}</td>
                  <td style={{ padding:"10px 14px", fontWeight:500 }}>{t.desc}</td>
                  <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:700, fontFamily:"var(--font-mono)", color: isInflow(t)?"var(--color-success)":"var(--color-danger)", letterSpacing:"-0.01em" }}>
                    {isInflow(t)?"+":"-"}{fmt(t.amount)}
                  </td>
                  <td style={{ padding:"10px 14px" }}>
                    <Badge color={isCleared?"green":"gray"}>{isCleared?"Cleared":"Outstanding"}</Badge>
                  </td>
                </tr>
              );
            })}
            {transactions.length === 0 && (
              <tr><td colSpan={5} style={{ padding:"32px", textAlign:"center", color:"var(--color-text-tertiary)", fontSize:13 }}>No transactions for this account</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ─── TRANSACTIONS ─────────────────────────────────────────────────────────────
function Transactions({ transactions, setTransactions, accounts, setAccounts, addTransaction, addTransfer, deleteTransaction, session }) {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterAccount, setFilterAccount] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [amtMin, setAmtMin] = useState("");
  const [amtMax, setAmtMax] = useState("");
  const [filterCleared, setFilterCleared] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [tagInput, setTagInput] = useState("");
  const [form, setForm] = useState({date:today(), desc:"", category:"", account:"a1", amount:"", type:"expense", notes:"", tags:[], cleared:false});

  const blankForm = {date:today(), desc:"", category:"", account:"a1", amount:"", type:"expense", notes:"", tags:[], cleared:false};

  const blankRow = () => ({id:uid(), date:today(), desc:"", category:"", account:accounts[0]?.id||"a1", toAccount:accounts[1]?.id||accounts[0]?.id||"a1", type:"expense", amount:""});
  const [batchRows, setBatchRows] = useState([blankRow()]);
  const addBatchRow = () => setBatchRows(rows => [...rows, {...blankRow(), date: rows[rows.length-1]?.date || today()}]);
  const updateBatchRow = (id, field, value) => setBatchRows(rows => rows.map(r => r.id===id ? {...r, [field]:value} : r));
  const removeBatchRow = (id) => setBatchRows(rows => rows.length>1 ? rows.filter(r=>r.id!==id) : rows);
  const isRowValid = (r) => r.type==="transfer"
    ? !!(r.amount && r.account && r.toAccount && r.account!==r.toAccount)
    : !!(r.desc && r.amount);
  const validBatchCount = batchRows.filter(isRowValid).length;
  const saveBatch = async () => {
    for (const row of batchRows) {
      if (!isRowValid(row)) continue;
      if (row.type === "transfer") {
        await addTransfer({ date:row.date, desc:row.desc, fromAccount:row.account, toAccount:row.toAccount, amount:parseFloat(row.amount) });
      } else {
        await addTransaction({ id:uid(), date:row.date, desc:row.desc, category:row.category, account:row.account, amount:parseFloat(row.amount), type:row.type, notes:"", tags:[], cleared:false });
      }
    }
    setShowForm(false);
    setBatchRows([blankRow()]);
  };

  const allCategories = useMemo(() => {
    const cats = new Set(transactions.map(t=>t.category).filter(Boolean));
    return [...cats];
  }, [transactions]);

  const filtered = useMemo(() => transactions.filter(t => {
    if (search && !t.desc.toLowerCase().includes(search.toLowerCase()) && !t.category.toLowerCase().includes(search.toLowerCase()) && !(t.notes||"").toLowerCase().includes(search.toLowerCase()) && !((t.tags||[]).join(" ")).toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType === "transfer" && t.type!=="transfer_out" && t.type!=="transfer_in") return false;
    if (filterType !== "all" && filterType !== "transfer" && t.type !== filterType) return false;
    if (filterAccount !== "all" && t.account !== filterAccount) return false;
    if (filterCategory !== "all" && t.category !== filterCategory) return false;
    if (dateFrom && t.date < dateFrom) return false;
    if (dateTo && t.date > dateTo) return false;
    if (amtMin && t.amount < parseFloat(amtMin)) return false;
    if (amtMax && t.amount > parseFloat(amtMax)) return false;
    if (filterCleared === "cleared" && !t.cleared) return false;
    if (filterCleared === "uncleared" && t.cleared) return false;
    if (filterStatus === "posted" && !t.category) return false;
    if (filterStatus === "pending" && t.category) return false;
    return true;
  }).sort((a,b) => b.date.localeCompare(a.date)), [transactions, search, filterType, filterAccount, filterCategory, dateFrom, dateTo, amtMin, amtMax, filterCleared, filterStatus]);

  const totalIn = filtered.filter(t=>t.type==="income" && t.category).reduce((s,t)=>s+t.amount,0);
  const totalOut = filtered.filter(t=>t.type==="expense" && t.category).reduce((s,t)=>s+t.amount,0);

const save = async () => {
    if (!form.desc || !form.amount || !editing) return;
    const amount = parseFloat(form.amount);
    const old = transactions.find(t=>t.id===editing);
    await supabase.from('transactions').update({
      date: form.date, description: form.desc, category: form.category,
      account: form.account, amount, type: form.type,
      notes: form.notes, tags: form.tags, cleared: form.cleared
    }).eq('id', editing);
    setTransactions(ts => ts.map(t => t.id===editing ? {...form, id:editing, amount} : t));
    if (old) {
      const acc = accounts.find(a => a.id === old.account);
      if (acc) {
        // Only Posted (categorized) transactions affect the account balance —
        // revert the old effect only if it was previously posted, apply the new effect only if it's now posted.
        const reverted = old.category ? (old.type==="income" ? acc.balance - old.amount : acc.balance + old.amount) : acc.balance;
        const newBalance = form.category ? (form.type==="income" ? reverted + amount : reverted - amount) : reverted;
        if (newBalance !== acc.balance) {
          await supabase.from('accounts').update({ balance: newBalance }).eq('id', old.account);
          setAccounts(as => as.map(a => a.id===old.account ? {...a, balance: newBalance} : a));
        }
      }
    }
    setShowForm(false); setEditing(null); setForm(blankForm);
  };

  const edit = (t) => { setForm({...t, amount:String(t.amount), tags:t.tags||[]}); setEditing(t.id); setShowForm(true); };

  const del = (id) => { if (window.confirm("Delete this transaction?")) deleteTransaction(id); };

  const bulkDelete = () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} transactions?`)) return;
    [...selected].forEach(id => deleteTransaction(id));
    setSelected(new Set());
  };

  const bulkClear = () => {
    setTransactions(ts => ts.map(t => selected.has(t.id) ? {...t, cleared:true} : t));
    setSelected(new Set());
  };

  const toggleSelect = (id) => setSelected(s => { const ns = new Set(s); if(ns.has(id)) ns.delete(id); else ns.add(id); return ns; });
  const toggleAll = () => setSelected(s => s.size===filtered.length ? new Set() : new Set(filtered.map(t=>t.id)));

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(f => ({...f, tags:[...f.tags, tagInput.trim()]}));
    }
    setTagInput("");
  };

  const activeFilters = [filterType!=="all",filterAccount!=="all",filterCategory!=="all",dateFrom,dateTo,amtMin,amtMax,filterCleared!=="all",filterStatus!=="all"].filter(Boolean).length;

  return (
    <Page title="Transactions" subtitle="All income, expense, and transfer entries"
      action={<Btn onClick={()=>{setShowForm(true);setEditing(null);setBatchRows([blankRow()]);}} icon="ti-plus">New Transaction</Btn>}>

      {showForm && !editing && (
        <Modal title="New Transactions" onClose={()=>setShowForm(false)} width={900}>
          <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:14, padding:"10px 14px", background:"var(--color-accent-subtle)", borderRadius:"var(--radius-md)" }}>
            Add as many rows as you need. Leave <strong>Category</strong> blank to save an Income/Expense row as <strong>Pending / To Review</strong> — it won't affect the account balance until you categorize it later. <strong>Transfer</strong> rows move money between two of your own accounts and post immediately.
          </div>
          <div style={{ overflowX:"auto", marginBottom:12 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr>
                  {["Date","Description","Type","Category / Accounts","Account","Amount (₱)",""].map(h=>(
                    <th key={h} style={{ padding:"4px 6px", textAlign:"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:10, textTransform:"uppercase", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {batchRows.map(row => (
                  <tr key={row.id}>
                    <td style={{ padding:4 }}><input type="date" value={row.date} onChange={e=>updateBatchRow(row.id,"date",e.target.value)} style={{ width:132 }} /></td>
                    <td style={{ padding:4 }}><input value={row.desc} onChange={e=>updateBatchRow(row.id,"desc",e.target.value)} placeholder={row.type==="transfer"?"Optional note":"Description"} style={{ minWidth:150 }} /></td>
                    <td style={{ padding:4 }}>
                      <select value={row.type} onChange={e=>updateBatchRow(row.id,"type",e.target.value)} style={{ width:90 }}>
                        <option value="expense">Expense</option>
                        <option value="income">Income</option>
                        <option value="transfer">Transfer</option>
                      </select>
                    </td>
                    {row.type==="transfer" ? (
                      <td style={{ padding:4 }} colSpan={2}>
                        <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                          <select value={row.account} onChange={e=>updateBatchRow(row.id,"account",e.target.value)} style={{ minWidth:110 }}>
                            {accounts.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
                          </select>
                          <i className="ti ti-arrow-right" style={{ fontSize:13, color:"var(--color-text-tertiary)", flexShrink:0 }} aria-hidden="true" />
                          <select value={row.toAccount} onChange={e=>updateBatchRow(row.id,"toAccount",e.target.value)} style={{ minWidth:110 }}>
                            {accounts.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
                          </select>
                        </div>
                        {row.account===row.toAccount && <div style={{ fontSize:10, color:"var(--color-danger)", marginTop:2 }}>Pick two different accounts</div>}
                      </td>
                    ) : (
                      <>
                        <td style={{ padding:4 }}>
                          <select value={row.category} onChange={e=>updateBatchRow(row.id,"category",e.target.value)} style={{ minWidth:160 }}>
                            <option value="">Uncategorized (Pending)</option>
                            {(row.type==="income"?incomeCategories:expenseCategories).map(c=><option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td style={{ padding:4 }}>
                          <select value={row.account} onChange={e=>updateBatchRow(row.id,"account",e.target.value)} style={{ minWidth:130 }}>
                            {accounts.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
                          </select>
                        </td>
                      </>
                    )}
                    <td style={{ padding:4 }}><input type="number" value={row.amount} onChange={e=>updateBatchRow(row.id,"amount",e.target.value)} placeholder="0.00" style={{ width:100, textAlign:"right" }} /></td>
                    <td style={{ padding:4 }}>
                      <button onClick={()=>removeBatchRow(row.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--color-danger)", display:"flex" }}><i className="ti ti-x" style={{ fontSize:15 }} aria-hidden="true" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:16 }}>
            <Btn onClick={addBatchRow} variant="secondary" size="sm" icon="ti-plus">Add Row</Btn>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={saveBatch} icon="ti-check" disabled={validBatchCount===0}>Save All ({validBatchCount})</Btn>
            <Btn onClick={()=>setShowForm(false)} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      {showForm && editing && (
        <Modal title="Edit Transaction" onClose={()=>{setShowForm(false);setEditing(null);}} width={640}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <Input label="Date" type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} />
            <Select label="Type" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value,category:""}))}>
              <option value="income">📥 Income</option>
              <option value="expense">📤 Expense</option>
            </Select>
            <Input label="Description" value={form.desc} onChange={e=>setForm(f=>({...f,desc:e.target.value}))} placeholder="e.g. Monthly Salary" />
            <Select label="Category" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
              <option value="">Uncategorized (Pending)</option>
              {(form.type==="income"?incomeCategories:expenseCategories).map(c=><option key={c} value={c}>{c}</option>)}
            </Select>
            <Select label="Account" value={form.account} onChange={e=>setForm(f=>({...f,account:e.target.value}))}>
              {accounts.map(a=><option key={a.id} value={a.id}>{getAccountIcon(a.type)} {a.name} ({fmt(a.balance)})</option>)}
            </Select>
            <Input label="Amount (₱)" type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} placeholder="0.00" />
          </div>
          <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", display:"block", marginBottom:12 }}>
            Notes
            <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} style={{ display:"block", marginTop:4, resize:"vertical", width:"100%" }} placeholder="Optional notes…" />
          </label>
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)" }}>Tags</label>
            <div style={{ display:"flex", gap:6, marginTop:4, flexWrap:"wrap" }}>
              {(form.tags||[]).map(tag => <Tag key={tag} label={tag} onRemove={()=>setForm(f=>({...f,tags:f.tags.filter(t=>t!==tag)}))} />)}
              <input value={tagInput} onChange={e=>setTagInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"||e.key===","){e.preventDefault();addTag();}}} placeholder="Add tag…" style={{ width:120, fontSize:11 }} />
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <input type="checkbox" id="cleared_cb" checked={form.cleared} onChange={e=>setForm(f=>({...f,cleared:e.target.checked}))} style={{ width:"auto", cursor:"pointer" }} />
            <label htmlFor="cleared_cb" style={{ fontSize:13, cursor:"pointer" }}>Mark as cleared</label>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={save} icon="ti-check">Update Transaction</Btn>
            <Btn onClick={()=>{setShowForm(false);setEditing(null);}} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Filter bar */}
      <div style={{ display:"flex", gap:10, marginBottom:16, alignItems:"center" }}>
        <div style={{ position:"relative", flex:1, maxWidth:340 }}>
          <i className="ti ti-search" style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"var(--color-text-tertiary)", fontSize:15 }} aria-hidden="true" />
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search description, category, tags…" style={{ paddingLeft:34 }} />
        </div>
        <Btn onClick={()=>setShowFilters(f=>!f)} variant="secondary" icon="ti-filter" size="sm">
          Filters {activeFilters>0 && <span style={{ background:"var(--color-accent)", color:"#fff", borderRadius:99, fontSize:10, padding:"0 5px", marginLeft:2 }}>{activeFilters}</span>}
        </Btn>
        {selected.size > 0 && (
          <div style={{ display:"flex", gap:8, marginLeft:"auto" }}>
            <span style={{ fontSize:12, color:"var(--color-text-secondary)", alignSelf:"center" }}>{selected.size} selected</span>
            <Btn onClick={bulkClear} variant="success" size="sm" icon="ti-check">Mark Cleared</Btn>
            <Btn onClick={bulkDelete} variant="danger" size="sm" icon="ti-trash">Delete</Btn>
          </div>
        )}
        <div style={{ display:"flex", gap:16, marginLeft:"auto", fontSize:13 }}>
          <span><span style={{ color:"var(--color-text-secondary)" }}>In: </span><span style={{ color:"var(--color-success)", fontWeight:700 }}>{fmt(totalIn)}</span></span>
          <span><span style={{ color:"var(--color-text-secondary)" }}>Out: </span><span style={{ color:"var(--color-danger)", fontWeight:700 }}>{fmt(totalOut)}</span></span>
          <span><span style={{ color:"var(--color-text-secondary)" }}>Net: </span><span style={{ color: totalIn-totalOut>=0?"var(--color-success)":"var(--color-danger)", fontWeight:700 }}>{fmt(totalIn-totalOut)}</span></span>
        </div>
      </div>

      {showFilters && (
        <Card style={{ marginBottom:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))", gap:12 }}>
            <Select label="Type" value={filterType} onChange={e=>setFilterType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
              <option value="transfer">Transfer</option>
            </Select>
            <Select label="Account" value={filterAccount} onChange={e=>setFilterAccount(e.target.value)}>
              <option value="all">All Accounts</option>
              {accounts.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
            <Select label="Category" value={filterCategory} onChange={e=>setFilterCategory(e.target.value)}>
              <option value="all">All Categories</option>
              {allCategories.map(c=><option key={c} value={c}>{c}</option>)}
            </Select>
            <Input label="Date From" type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
            <Input label="Date To" type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} />
            <Input label="Min Amount" type="number" value={amtMin} onChange={e=>setAmtMin(e.target.value)} placeholder="0" />
            <Input label="Max Amount" type="number" value={amtMax} onChange={e=>setAmtMax(e.target.value)} placeholder="999999" />
            <Select label="Cleared Status" value={filterCleared} onChange={e=>setFilterCleared(e.target.value)}>
              <option value="all">All</option>
              <option value="cleared">Cleared</option>
              <option value="uncleared">Uncleared</option>
            </Select>
            <Select label="Review Status" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="posted">Posted</option>
              <option value="pending">Pending / To Review</option>
            </Select>
          </div>
          <div style={{ marginTop:12 }}>
            <Btn size="sm" variant="ghost" onClick={()=>{setFilterType("all");setFilterAccount("all");setFilterCategory("all");setDateFrom("");setDateTo("");setAmtMin("");setAmtMax("");setFilterCleared("all");setFilterStatus("all");}}>Clear Filters</Btn>
          </div>
        </Card>
      )}

      <Card style={{ padding:0 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"var(--color-bg-subtle)", borderBottom:"1px solid var(--color-border)" }}>
              <th style={{ padding:"10px 14px", width:36 }}>
                <input type="checkbox" checked={selected.size===filtered.length && filtered.length>0} onChange={toggleAll} style={{ width:"auto", cursor:"pointer" }} />
              </th>
              {["Date","Description","Category","Status","Account","Tags","Amount","Cleared",""].map(h=>(
                <th key={h} style={{ padding:"10px 14px", textAlign: h==="Amount"?"right":"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} style={{ borderBottom:"1px solid var(--color-border-subtle)", background: selected.has(t.id) ? "var(--color-accent-subtle)" : undefined }}>
                <td style={{ padding:"10px 14px" }}>
                  <input type="checkbox" checked={selected.has(t.id)} onChange={()=>toggleSelect(t.id)} style={{ width:"auto", cursor:"pointer" }} />
                </td>
                <td style={{ padding:"10px 14px", color:"var(--color-text-secondary)", whiteSpace:"nowrap", fontSize:12 }}>{t.date}</td>
                <td style={{ padding:"10px 14px" }}>
                  <div style={{ fontWeight:500 }}>{t.desc}</div>
                  {t.notes && <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:1 }}>{t.notes}</div>}
                </td>
                <td style={{ padding:"10px 14px" }}>{t.category ? <Badge color={t.type==="income"?"green":"gray"} size="xs">{t.category}</Badge> : <span style={{ fontSize:11, color:"var(--color-text-tertiary)", fontStyle:"italic" }}>Uncategorized</span>}</td>
                <td style={{ padding:"10px 14px" }}><Badge color={t.category?"blue":"amber"} size="xs">{t.category?"Posted":"Pending"}</Badge></td>
                <td style={{ padding:"10px 14px", color:"var(--color-text-secondary)", fontSize:12 }}>
                  {accounts.find(a=>a.id===t.account)?.name||"—"}
                </td>
                <td style={{ padding:"10px 14px" }}>
                  <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                    {(t.tags||[]).slice(0,2).map(tag=><Tag key={tag} label={tag} />)}
                    {(t.tags||[]).length > 2 && <span style={{ fontSize:10, color:"var(--color-text-tertiary)" }}>+{t.tags.length-2}</span>}
                  </div>
                </td>
                <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:700, color: isInflow(t)?"var(--color-success)":"var(--color-danger)", whiteSpace:"nowrap" }}>
                  {isInflow(t)?"+":"-"}{fmt(t.amount)}
                </td>
                <td style={{ padding:"10px 14px" }}><Badge color={t.cleared?"green":"gray"} size="xs">{t.cleared?"Cleared":"Uncleared"}</Badge></td>
                <td style={{ padding:"10px 14px" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    {t.type!=="transfer_out" && t.type!=="transfer_in" && <button onClick={()=>edit(t)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--color-text-secondary)", padding:2 }}><i className="ti ti-edit" style={{ fontSize:15 }} aria-hidden="true" /></button>}
                    <button onClick={()=>del(t.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--color-danger)", padding:2 }}><i className="ti ti-trash" style={{ fontSize:15 }} aria-hidden="true" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length===0 && <EmptyState icon="🔍" title="No transactions found" desc="Try adjusting your search or filters." />}
      </Card>
    </Page>
  );
}

// ─── RECEIVABLES ──────────────────────────────────────────────────────────────
function Receivables({ receivables, setReceivables, accounts, setAccounts, session }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [payForm, setPayForm] = useState({principal:"", interest:"", account:"a1", paymentDate:today(), notes:"", selectedInstallments:[]});
  const [form, setForm] = useState({borrower:"",contact:"",loanDate:today(),principal:"",rate:"",dueDate:"",paid:"0",interestPaid:"0",notes:"",paymentMethod:"Cash",accountId:"a4",installments:[],repaymentType:"lump",installmentCount:"",installmentFrequency:"monthly"});

  const blankForm = {borrower:"",contact:"",loanDate:today(),principal:"",rate:"",dueDate:"",paid:"0",interestPaid:"0",notes:"",paymentMethod:"Cash",accountId:"a4",installments:[],repaymentType:"lump",installmentCount:"",installmentFrequency:"monthly"};

  const openNew = () => { setForm(blankForm); setEditing(null); setShowForm(true); };
  const openEdit = (r) => { setForm({...r, principal:String(r.principal), rate:String(r.rate), paid:String(r.paid), interestPaid:String(r.interestPaid), installments:r.installments||[], repaymentType:(r.installments&&r.installments.length>0)?"installments":"lump", installmentCount:String((r.installments||[]).length||""), installmentFrequency:"monthly"}); setEditing(r.id); setShowForm(true); };
const cancel = () => { setShowForm(false); setEditing(null); };
  const generateInstallments = () => {
    const principal = parseFloat(form.principal||0);
    if (!principal || !form.installmentCount) return;
    const rows = generateInstallmentPlan({ principal, count: form.installmentCount, frequency: form.installmentFrequency, startDate: form.loanDate||today() });
    setForm(f => ({...f, installments: rows}));
  };
  const addInstallmentRow = () => setForm(f => ({...f, installments:[...f.installments, {id:uid(), dueDate:today(), amount:0, paid:false, paidDate:null}]}));
  const updateInstallmentRow = (id, field, value) => setForm(f => ({...f, installments: f.installments.map(i => i.id===id ? {...i, [field]: field==="amount"?parseFloat(value)||0:value} : i)}));
  const removeInstallmentRow = (id) => setForm(f => ({...f, installments: f.installments.filter(i => i.id!==id)}));
  const del = async (id) => { 
    if (window.confirm("Delete this receivable?")) {
      await supabase.from('receivables').delete().eq('id', id);
      setReceivables(rs => rs.filter(r => r.id!==id)); 
    }
  };
  const openPayment = (id) => { setPayForm({principal:"", interest:"", account:"a1", paymentDate:today(), notes:"", selectedInstallments:[]}); setPaymentId(id); };

  const toggleSelectedInstallment = (ins) => {
    setPayForm(f => {
      const selected = f.selectedInstallments.includes(ins.id) ? f.selectedInstallments.filter(id=>id!==ins.id) : [...f.selectedInstallments, ins.id];
      const rec = receivables.find(r => r.id===paymentId);
      const sum = (rec.installments||[]).filter(i => selected.includes(i.id)).reduce((s,i)=>s+i.amount, 0);
      return {...f, selectedInstallments: selected, principal: sum>0 ? String(sum) : f.principal};
    });
  };

  const savePayment = async () => {
    const principalAmt = parseFloat(payForm.principal||0);
    const interestAmt = parseFloat(payForm.interest||0);
    const total = principalAmt + interestAmt;
    const rec = receivables.find(r => r.id===paymentId);
    const newPaid = rec.paid + principalAmt;
    const newInterest = rec.interestPaid + interestAmt;
    const updatedInstallments = (rec.installments||[]).map(i =>
      payForm.selectedInstallments.includes(i.id) ? {...i, paid:true, paidDate: payForm.paymentDate} : i
    );
    await supabase.from('receivables').update({ paid: newPaid, interest_paid: newInterest, installments: updatedInstallments }).eq('id', paymentId);
    setReceivables(rs => rs.map(r => r.id===paymentId ? {...r, paid: newPaid, interestPaid: newInterest, installments: updatedInstallments} : r));
    if (total > 0 && payForm.account) {
      const acc = accounts.find(a => a.id===payForm.account);
      const newBalance = acc.balance + total;
      await supabase.from('accounts').update({ balance: newBalance }).eq('id', payForm.account);
      setAccounts(as => as.map(a => a.id===payForm.account ? {...a, balance: newBalance} : a));
    }
    setPaymentId(null);
  };

  const toggleReceivableInstallmentPaid = async (recId, insId) => {
    const rec = receivables.find(r => r.id===recId);
    if (!rec) return;
    const inst = (rec.installments||[]).find(i => i.id===insId);
    if (!inst) return;
    const willBePaid = !inst.paid;
    const updatedInstallments = rec.installments.map(i => i.id===insId ? {...i, paid: willBePaid, paidDate: willBePaid ? today() : null} : i);
    const newPaid = Math.max(0, rec.paid + (willBePaid ? 1 : -1) * inst.amount);
    await supabase.from('receivables').update({ installments: updatedInstallments, paid: newPaid }).eq('id', recId);
    setReceivables(rs => rs.map(r => r.id===recId ? {...r, installments: updatedInstallments, paid: newPaid} : r));
  };

  const save = async () => {
    if (!form.borrower||!form.principal) return;
    const installments = form.repaymentType==="installments" ? form.installments : [];
    const parsed = {...form, principal:parseFloat(form.principal), rate:parseFloat(form.rate||0), paid:parseFloat(form.paid||0), interestPaid:parseFloat(form.interestPaid||0), installments};
    if (editing) {
      await supabase.from('receivables').update({
        borrower: parsed.borrower, contact: parsed.contact,
        loan_date: parsed.loanDate, principal: parsed.principal,
        rate: parsed.rate, due_date: parsed.dueDate, paid: parsed.paid,
        interest_paid: parsed.interestPaid, notes: parsed.notes,
        payment_method: parsed.paymentMethod, account_id: parsed.accountId,
        installments: parsed.installments
      }).eq('id', editing);
      setReceivables(rs => rs.map(r => r.id===editing ? {...parsed, id:editing} : r));
    } else {
      const newRec = {id:uid(), ...parsed};
      await supabase.from('receivables').insert([{
        id: newRec.id, borrower: newRec.borrower, contact: newRec.contact,
        loan_date: newRec.loanDate, principal: newRec.principal, rate: newRec.rate,
        due_date: newRec.dueDate, paid: newRec.paid, interest_paid: newRec.interestPaid,
        notes: newRec.notes, payment_method: newRec.paymentMethod,
        account_id: newRec.accountId, installments: newRec.installments,
        user_id: session.user.id
      }]);
      setReceivables(rs => [...rs, newRec]);
    }
    cancel();
  };

  const totalPrincipal = receivables.reduce((s,r)=>s+(r.principal-r.paid),0);
  const totalInterest = receivables.reduce((s,r)=>s+r.interestPaid,0);

  const agingColors = {current:"green","1-30":"amber","31-60":"amber","61-90":"red","91-120":"red","120+":"red",paid:"gray"};

  if (selectedReceivable) {
    const r = receivables.find(rx=>rx.id===selectedReceivable);
    if (!r) { setSelectedReceivable(null); return null; }
    return <ReceivableDetail receivable={r} onBack={()=>setSelectedReceivable(null)} onPay={()=>{ openPayment(r.id); setSelectedReceivable(null); }} onToggleInstallment={(insId)=>toggleReceivableInstallmentPaid(r.id, insId)} />;
  }

  return (
    <Page title="Receivables" subtitle="Loans extended — track collections and interest"
      action={<Btn onClick={openNew} icon="ti-plus">New Loan</Btn>}>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Principal Outstanding" value={fmt(totalPrincipal)} color="var(--color-purple)" icon="ti-cash" />
        <MetricCard label="Total Interest Earned" value={fmt(totalInterest)} color="var(--color-success)" icon="ti-coins" />
        <MetricCard label="Active Loans" value={receivables.filter(r=>r.paid<r.principal).length} color="var(--color-accent)" icon="ti-users" />
        <MetricCard label="Total Deployed" value={fmt(receivables.reduce((s,r)=>s+r.principal,0))} color="var(--color-warning)" icon="ti-report-money" />
      </div>

      {showForm && (
        <Modal title={editing?"Edit Loan":"New Loan Receivable"} onClose={cancel} width={680}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
            <Input label="Borrower Name" value={form.borrower} onChange={e=>setForm(f=>({...f,borrower:e.target.value}))} placeholder="Full name" />
            <Input label="Contact Number" value={form.contact} onChange={e=>setForm(f=>({...f,contact:e.target.value}))} placeholder="09XX XXX XXXX" />
            <Input label="Loan Date" type="date" value={form.loanDate} onChange={e=>setForm(f=>({...f,loanDate:e.target.value}))} />
            <Input label="Principal Amount (₱)" type="number" value={form.principal} onChange={e=>setForm(f=>({...f,principal:e.target.value}))} placeholder="0" />
            <Input label="Monthly Rate (%)" type="number" step="0.5" value={form.rate} onChange={e=>setForm(f=>({...f,rate:e.target.value}))} placeholder="3" />
            <Input label="Due Date" type="date" value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))} />
            <Select label="Payment Account" value={form.accountId} onChange={e=>setForm(f=>({...f,accountId:e.target.value}))}>
              {accounts.map(a=><option key={a.id} value={a.id}>{getAccountIcon(a.type)} {a.name}</option>)}
            </Select>
            <Input label="Already Paid (₱)" type="number" value={form.paid} onChange={e=>setForm(f=>({...f,paid:e.target.value}))} />
            <Input label="Interest Collected (₱)" type="number" value={form.interestPaid} onChange={e=>setForm(f=>({...f,interestPaid:e.target.value}))} />
          </div>
          <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", display:"block", marginBottom:16 }}>
            Notes
            <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} style={{ display:"block", marginTop:4, width:"100%", resize:"vertical" }} />
          </label>

          <Divider />
          <SectionTitle>Repayment</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
            <Select label="Repayment Type" value={form.repaymentType} onChange={e=>setForm(f=>({...f,repaymentType:e.target.value}))}>
              <option value="lump">Lump Sum</option>
              <option value="installments">Installments</option>
            </Select>
            {form.repaymentType==="installments" && (
              <>
                <Input label="# of Installments" type="number" value={form.installmentCount} onChange={e=>setForm(f=>({...f,installmentCount:e.target.value}))} placeholder="5" />
                <Select label="Frequency" value={form.installmentFrequency} onChange={e=>setForm(f=>({...f,installmentFrequency:e.target.value}))}>
                  <option value="monthly">Monthly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="weekly">Weekly</option>
                </Select>
              </>
            )}
          </div>

          {form.repaymentType==="installments" && (
            <div style={{ marginBottom:16 }}>
              <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                <Btn onClick={generateInstallments} variant="secondary" size="sm" icon="ti-wand">Generate Schedule</Btn>
                <Btn onClick={addInstallmentRow} variant="secondary" size="sm" icon="ti-plus">Add Installment</Btn>
              </div>
              {form.installments.length > 0 && (
                <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:220, overflow:"auto" }}>
                  {form.installments.map((ins,i) => (
                    <div key={ins.id} style={{ display:"grid", gridTemplateColumns:"24px 1fr 1fr 32px", gap:8, alignItems:"center" }}>
                      <div style={{ fontSize:11, color:"var(--color-text-tertiary)", textAlign:"right" }}>{i+1}</div>
                      <input type="date" value={ins.dueDate} onChange={e=>updateInstallmentRow(ins.id,"dueDate",e.target.value)} />
                      <input type="number" value={ins.amount} onChange={e=>updateInstallmentRow(ins.id,"amount",e.target.value)} placeholder="Amount" />
                      <button onClick={()=>removeInstallmentRow(ins.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--color-danger)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <i className="ti ti-x" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={save} icon="ti-check">{editing?"Update":"Save"} Loan</Btn>
            <Btn onClick={cancel} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      {paymentId && (
        <Modal title={`Record Payment — ${receivables.find(r=>r.id===paymentId)?.borrower}`} onClose={()=>setPaymentId(null)}>
          <div style={{ marginBottom:16, padding:"12px 16px", background:"var(--color-warning-subtle)", borderRadius:"var(--radius-md)", fontSize:13 }}>
            <strong>Where did the money go?</strong> — Select the account to receive this payment
          </div>
          {(receivables.find(r=>r.id===paymentId)?.installments||[]).filter(i=>!i.paid).length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", marginBottom:6 }}>Which installments does this cover?</div>
              <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:150, overflow:"auto" }}>
                {receivables.find(r=>r.id===paymentId).installments.filter(i=>!i.paid).map(ins => (
                  <label key={ins.id} style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, fontWeight:400, color:"var(--color-text-primary)" }}>
                    <input type="checkbox" checked={payForm.selectedInstallments.includes(ins.id)} onChange={()=>toggleSelectedInstallment(ins)} style={{ width:"auto" }} />
                    {ins.dueDate} — {fmt(ins.amount)}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <Input label="Principal Payment (₱)" type="number" value={payForm.principal} onChange={e=>setPayForm(f=>({...f,principal:e.target.value}))} placeholder="0" />
            <Input label="Interest Collected (₱)" type="number" value={payForm.interest} onChange={e=>setPayForm(f=>({...f,interest:e.target.value}))} placeholder="0" />
            <Select label="Deposit To Account" value={payForm.account} onChange={e=>setPayForm(f=>({...f,account:e.target.value}))}>
              {accounts.map(a=><option key={a.id} value={a.id}>{getAccountIcon(a.type)} {a.name} ({fmt(a.balance)})</option>)}
            </Select>
            <Input label="Payment Date" type="date" value={payForm.paymentDate} onChange={e=>setPayForm(f=>({...f,paymentDate:e.target.value}))} />
          </div>
          <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", display:"block", marginBottom:16 }}>
            Notes
            <textarea value={payForm.notes} onChange={e=>setPayForm(f=>({...f,notes:e.target.value}))} rows={2} style={{ display:"block", marginTop:4, width:"100%", resize:"vertical" }} />
          </label>
          <div style={{ padding:"12px 16px", background:"var(--color-bg-subtle)", borderRadius:"var(--radius-md)", marginBottom:16, fontSize:13 }}>
            <div style={{ display:"flex", justifyContent:"space-between" }}>
              <span>Total collection</span>
              <span style={{ fontWeight:700, color:"var(--color-success)" }}>{fmt((parseFloat(payForm.principal)||0) + (parseFloat(payForm.interest)||0))}</span>
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={savePayment} variant="success" icon="ti-check">Record Payment</Btn>
            <Btn onClick={()=>setPaymentId(null)} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      <Card style={{ padding:0, marginBottom:16 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"var(--color-bg-subtle)", borderBottom:"1px solid var(--color-border)" }}>
              {["Borrower","Loan Date","Principal","Rate","Due Date","Paid","Outstanding","Interest","Aging",""].map(h=>(
                <th key={h} style={{ padding:"10px 14px", textAlign:["Principal","Paid","Outstanding","Interest","Rate"].includes(h)?"right":"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {receivables.map(r => {
              const outstanding = r.principal - r.paid;
              const aging = getAgingBucket(r);
              const collectionRate = r.principal > 0 ? (r.paid/r.principal)*100 : 0;
              return (
                <tr key={r.id} style={{ borderBottom:"1px solid var(--color-border-subtle)", cursor:"pointer" }} onClick={()=>setSelectedReceivable(r.id)}>
                  <td style={{ padding:"12px 14px" }}>
                    <div style={{ fontWeight:600 }}>{r.borrower}</div>
                    <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:1 }}>{r.contact}</div>
                    {r.notes && <div style={{ fontSize:10, color:"var(--color-text-tertiary)", marginTop:1, fontStyle:"italic" }}>{r.notes.slice(0,40)}{r.notes.length>40?"…":""}</div>}
                  </td>
                  <td style={{ padding:"12px 14px", color:"var(--color-text-secondary)", fontSize:12 }}>{r.loanDate}</td>
                  <td style={{ padding:"12px 14px", textAlign:"right", fontWeight:600 }}>{fmt(r.principal)}</td>
                  <td style={{ padding:"12px 14px", textAlign:"right" }}><Badge color="purple" size="xs">{r.rate}%/mo</Badge></td>
                  <td style={{ padding:"12px 14px", color:"var(--color-text-secondary)", fontSize:12 }}>{r.dueDate}</td>
                  <td style={{ padding:"12px 14px", textAlign:"right" }}>
                    <div style={{ fontSize:13, color:"var(--color-success)", fontWeight:600 }}>{fmt(r.paid)}</div>
                    <div style={{ height:3, borderRadius:99, background:"var(--color-border)", marginTop:4, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${Math.min(100,collectionRate)}%`, background:"var(--color-success)", borderRadius:99 }} />
                    </div>
                  </td>
                  <td style={{ padding:"12px 14px", textAlign:"right", fontWeight:700, color: outstanding>0?"var(--color-danger)":"var(--color-success)" }}>{fmt(outstanding)}</td>
                  <td style={{ padding:"12px 14px", textAlign:"right", color:"var(--color-purple)", fontWeight:500 }}>{fmt(r.interestPaid)}</td>
                  <td style={{ padding:"12px 14px" }}><Badge color={agingColors[aging]||"gray"} size="xs">{aging==="paid"?"Paid":aging==="current"?"Current":aging+" days"}</Badge></td>
                  <td style={{ padding:"12px 14px" }}>
                    <div style={{ display:"flex", gap:5 }} onClick={e=>e.stopPropagation()}>
                      {r.paid < r.principal && <Btn onClick={()=>openPayment(r.id)} variant="success" size="sm">Pay</Btn>}
                      <Btn onClick={()=>openEdit(r)} variant="ghost" size="sm" icon="ti-edit" />
                      <Btn onClick={()=>del(r.id)} variant="danger" size="sm" icon="ti-trash" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {receivables.length===0 && <EmptyState icon="🤝" title="No receivables yet" desc="Track loans you've extended to others." action={<Btn onClick={openNew} icon="ti-plus">New Loan</Btn>} />}
      </Card>

      {/* Aging Summary */}
      <Card className="chart-card">
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
          <div>
            <SectionTitle>Receivables Aging</SectionTitle>
            <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:2 }}>Outstanding by days overdue</div>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(6, 1fr)", gap:10 }}>
          {[
            {bucket:"current", label:"Current", color:"#059669", bg:"#ECFDF5", border:"#D1FAE5"},
            {bucket:"1-30", label:"1–30 days", color:"#D97706", bg:"#FFFBEB", border:"#FDE68A"},
            {bucket:"31-60", label:"31–60 days", color:"#EA580C", bg:"#FFF7ED", border:"#FDBA74"},
            {bucket:"61-90", label:"61–90 days", color:"#DC2626", bg:"#FEF2F2", border:"#FECACA"},
            {bucket:"91-120", label:"91–120 days", color:"#B91C1C", bg:"#FEF2F2", border:"#FCA5A5"},
            {bucket:"120+", label:"120+ days", color:"#7F1D1D", bg:"#FEF2F2", border:"#F87171"},
          ].map(({bucket,label,color,bg,border}) => {
            const total = receivables.filter(r => getAgingBucket(r)===bucket).reduce((s,r)=>s+(r.principal-r.paid),0);
            const count = receivables.filter(r => getAgingBucket(r)===bucket).length;
            const maxTotal = Math.max(1, ...["current","1-30","31-60","61-90","91-120","120+"].map(b=>receivables.filter(r=>getAgingBucket(r)===b).reduce((s,r)=>s+(r.principal-r.paid),0)));
            const barH = Math.round((total/maxTotal)*52);
            return (
              <div key={bucket} style={{ background:bg, border:`1.5px solid ${border}`, borderRadius:"var(--radius-md)", padding:"12px 10px", textAlign:"center", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div style={{ fontSize:9, fontWeight:700, color, textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</div>
                <div style={{ height:56, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
                  <div style={{ width:24, height:Math.max(4, barH), background:color, borderRadius:4, opacity: count===0?0.2:0.9, transition:"height 0.4s ease" }} />
                </div>
                <div style={{ fontSize:15, fontWeight:800, color, letterSpacing:"-0.02em", lineHeight:1 }}>{total>0?fmtShort(total):"—"}</div>
                <div style={{ fontSize:10, color, opacity:0.65 }}>{count} loan{count!==1?"s":""}</div>
              </div>
            );
          })}
        </div>
      </Card>
    </Page>
  );
}

function ReceivableDetail({ receivable: r, onBack, onPay, onToggleInstallment }) {
  const outstanding = r.principal - r.paid;
  const collectionRate = r.principal > 0 ? (r.paid/r.principal)*100 : 0;
  const aging = getAgingBucket(r);
  const monthlyInterest = r.principal * (r.rate/100);
  const expectedTotal = r.principal + (r.principal * (r.rate/100) * 12);
  const installments = r.installments || [];
  const paidInstallments = installments.filter(i=>i.paid).length;

  return (
    <div style={{ padding:"28px 32px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
        <Btn onClick={onBack} variant="ghost" icon="ti-arrow-left" size="sm">Back</Btn>
        <div style={{ flex:1 }}>
          <h1 style={{ fontSize:20, fontWeight:700, margin:0 }}>{r.borrower}</h1>
          <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginTop:2 }}>{r.contact} · Loan since {r.loanDate}</div>
        </div>
        {outstanding > 0 && <Btn onClick={onPay} icon="ti-cash" variant="success">Record Payment</Btn>}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Principal" value={fmt(r.principal)} color="var(--color-accent)" icon="ti-cash" />
        <MetricCard label="Collected" value={fmt(r.paid)} color="var(--color-success)" icon="ti-check" />
        <MetricCard label="Outstanding" value={fmt(outstanding)} color={outstanding>0?"var(--color-danger)":"var(--color-success)"} icon="ti-clock" />
        <MetricCard label="Interest Earned" value={fmt(r.interestPaid)} color="var(--color-purple)" icon="ti-coins" />
      </div>

      <div style={{ display:"grid", gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "2fr 1fr", gap:16, marginBottom:16 }}>
        <Card>
          <SectionTitle>Collection Progress</SectionTitle>
          <div style={{ marginBottom:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:8 }}>
              <span style={{ color:"var(--color-text-secondary)" }}>Collection Rate</span>
              <span style={{ fontWeight:700, color:"var(--color-success)" }}>{collectionRate.toFixed(1)}%</span>
            </div>
            <ProgressBar value={r.paid} max={r.principal} color="var(--color-success)" height={10} />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginTop:16 }}>
            <div style={{ textAlign:"center", padding:"12px", background:"var(--color-bg-subtle)", borderRadius:"var(--radius-md)" }}>
              <div style={{ fontSize:10, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Monthly Interest</div>
              <div style={{ fontSize:16, fontWeight:700, color:"var(--color-purple)", marginTop:4 }}>{fmt(monthlyInterest)}</div>
            </div>
            <div style={{ textAlign:"center", padding:"12px", background:"var(--color-bg-subtle)", borderRadius:"var(--radius-md)" }}>
              <div style={{ fontSize:10, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Rate</div>
              <div style={{ fontSize:16, fontWeight:700, color:"var(--color-accent)", marginTop:4 }}>{r.rate}%/mo</div>
            </div>
            <div style={{ textAlign:"center", padding:"12px", background:"var(--color-bg-subtle)", borderRadius:"var(--radius-md)" }}>
              <div style={{ fontSize:10, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Aging</div>
              <div style={{ fontSize:16, fontWeight:700, color:"var(--color-danger)", marginTop:4, textTransform:"uppercase" }}>{aging}</div>
            </div>
          </div>
        </Card>
        <Card>
          <SectionTitle>Loan Details</SectionTitle>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {[
              ["Due Date", r.dueDate],
              ["Loan Date", r.loanDate],
              ["Notes", r.notes || "—"],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:13, borderBottom:"1px solid var(--color-border-subtle)", paddingBottom:8 }}>
                <span style={{ color:"var(--color-text-secondary)" }}>{k}</span>
                <span style={{ fontWeight:500, maxWidth:160, textAlign:"right", wordBreak:"break-word" }}>{v}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {installments.length > 0 && (
        <Card>
          <SectionTitle>Installment Schedule</SectionTitle>
          <div style={{ marginBottom:12 }}>
            <ProgressBar value={paidInstallments} max={installments.length} color="var(--color-success)" height={8} showLabel />
          </div>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:"var(--color-bg-subtle)", borderBottom:"1px solid var(--color-border)" }}>
                {["#","Due Date","Amount","Status","Paid Date",""].map(h=>(
                  <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {installments.map((ins,i) => (
                <tr key={ins.id} style={{ borderBottom:"1px solid var(--color-border-subtle)", background: ins.paid ? "var(--color-success-subtle)" : getDaysOverdue(ins.dueDate)>0?"var(--color-danger-subtle)":undefined }}>
                  <td style={{ padding:"9px 12px", color:"var(--color-text-tertiary)", fontWeight:600 }}>{i+1}</td>
                  <td style={{ padding:"9px 12px" }}>{ins.dueDate}</td>
                  <td style={{ padding:"9px 12px", fontWeight:600 }}>{fmt(ins.amount)}</td>
                  <td style={{ padding:"9px 12px" }}>
                    <Badge color={ins.paid?"green":getDaysOverdue(ins.dueDate)>0?"red":"gray"} size="xs">
                      {ins.paid?"Paid":getDaysOverdue(ins.dueDate)>0?"Overdue":"Pending"}
                    </Badge>
                  </td>
                  <td style={{ padding:"9px 12px", color:"var(--color-text-secondary)", fontSize:12 }}>{ins.paidDate||"—"}</td>
                  <td style={{ padding:"9px 12px" }}>
                    <Btn onClick={()=>onToggleInstallment(ins.id)} variant={ins.paid?"secondary":"success"} size="sm">{ins.paid?"Undo":"Mark Paid"}</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ─── DEBTS ────────────────────────────────────────────────────────────────────
function Debts({ debts, setDebts, session }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [form, setForm] = useState({lender:"",amount:"",rate:"",dueDate:"",paid:"0",type:"personal_loan",monthlyPayment:"",notes:"",termMonths:"",installments:[]});

  const blankForm = {lender:"",amount:"",rate:"",dueDate:"",paid:"0",type:"personal_loan",monthlyPayment:"",notes:"",termMonths:"",installments:[]};
  const openNew = () => { setForm(blankForm); setEditing(null); setShowForm(true); };
  const openEdit = (d) => { setForm({...d, amount:String(d.amount), rate:String(d.rate), paid:String(d.paid), monthlyPayment:String(d.monthlyPayment||""), notes:d.notes||"", termMonths:String(d.termMonths||""), installments:d.installments||[]}); setEditing(d.id); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); };
  const generateSchedule = () => {
    const principal = parseFloat(form.amount||0);
    const termMonths = parseInt(form.termMonths||0);
    if (!principal || !termMonths) return;
    if ((form.installments||[]).length > 0 && !window.confirm("This will replace the current payment schedule. Continue?")) return;
    const rows = generateAmortizationSchedule({ principal, annualRatePct: parseFloat(form.rate||0), termMonths, startDate: today() });
    setForm(f => ({...f, installments: rows}));
  };
  const addInstallmentRow = () => setForm(f => ({...f, installments:[...(f.installments||[]), {id:uid(), dueDate:today(), amount:0, paid:false, paidDate:null}]}));
  const updateInstallmentRow = (id, field, value) => setForm(f => ({...f, installments: f.installments.map(i => i.id===id ? {...i, [field]: field==="amount"?parseFloat(value)||0:value} : i)}));
  const removeInstallmentRow = (id) => setForm(f => ({...f, installments: f.installments.filter(i => i.id!==id)}));
  const del = async (id) => { 
    if (window.confirm("Delete this debt?")) {
      await supabase.from('debts').delete().eq('id', id);
      setDebts(ds => ds.filter(d => d.id!==id)); 
    }
  };
  const openPayment = (id) => { setPayAmount(""); setPaymentId(id); };
  const savePayment = async () => {
    const debt = debts.find(d => d.id===paymentId);
    const newPaid = debt.paid + parseFloat(payAmount||0);
    await supabase.from('debts').update({ paid: newPaid }).eq('id', paymentId);
    setDebts(ds => ds.map(d => d.id===paymentId ? {...d, paid: newPaid} : d));
    setPaymentId(null);
  };
  const save = async () => {
    if (!form.lender||!form.amount) return;
    const parsed = {...form, amount:parseFloat(form.amount), rate:parseFloat(form.rate||0), paid:parseFloat(form.paid||0), monthlyPayment:parseFloat(form.monthlyPayment||0), termMonths:parseInt(form.termMonths||0)||0};
    const installments = recalcDebtInstallments(form.installments||[], parsed.amount, parsed.rate);
    if (editing) {
      await supabase.from('debts').update({
        lender: parsed.lender, amount: parsed.amount, rate: parsed.rate,
        due_date: parsed.dueDate, paid: parsed.paid, type: parsed.type,
        monthly_payment: parsed.monthlyPayment, notes: parsed.notes,
        term_months: parsed.termMonths || null, installments
      }).eq('id', editing);
      setDebts(ds => ds.map(d => d.id===editing ? {...parsed, id:editing, installments} : d));
    } else {
      const newDebt = {id:uid(), ...parsed};
      await supabase.from('debts').insert([{
        id: newDebt.id, lender: newDebt.lender, amount: newDebt.amount,
        rate: newDebt.rate, due_date: newDebt.dueDate, paid: newDebt.paid,
        type: newDebt.type, monthly_payment: newDebt.monthlyPayment, notes: newDebt.notes,
        term_months: newDebt.termMonths || null, installments,
        user_id: session.user.id
      }]);
      setDebts(ds => [...ds, {...newDebt, installments}]);
    }
    cancel();
  };

  const toggleInstallmentPaid = async (debtId, insId) => {
    const debt = debts.find(d => d.id===debtId);
    if (!debt) return;
    const inst = (debt.installments||[]).find(i => i.id===insId);
    if (!inst) return;
    const willBePaid = !inst.paid;
    const updatedInstallments = debt.installments.map(i => i.id===insId ? {...i, paid: willBePaid, paidDate: willBePaid ? today() : null} : i);
    const newPaid = Math.max(0, debt.paid + (willBePaid ? 1 : -1) * inst.principal);
    await supabase.from('debts').update({ installments: updatedInstallments, paid: newPaid }).eq('id', debtId);
    setDebts(ds => ds.map(d => d.id===debtId ? {...d, installments: updatedInstallments, paid: newPaid} : d));
  };

  const totalDebt = debts.reduce((s,d)=>s+(d.amount-d.paid),0);
  const totalPaid = debts.reduce((s,d)=>s+d.paid,0);
  const totalMonthly = debts.reduce((s,d)=>s+(d.monthlyPayment||0),0);

  const typeLabels = {credit_card:"Credit Card", personal_loan:"Personal Loan", car_loan:"Car Loan", mortgage:"Mortgage", student_loan:"Student Loan"};
  const typeColors = {credit_card:"red", personal_loan:"amber", car_loan:"blue", mortgage:"purple", student_loan:"orange"};

  if (selectedDebt) {
    const d = debts.find(x=>x.id===selectedDebt);
    if (!d) { setSelectedDebt(null); return null; }
    return <DebtDetail debt={d} typeLabels={typeLabels} typeColors={typeColors} onBack={()=>setSelectedDebt(null)} onPay={()=>{ openPayment(d.id); setSelectedDebt(null); }} onToggleInstallment={(insId)=>toggleInstallmentPaid(d.id, insId)} />;
  }

  return (
    <Page title="Debts & Liabilities" subtitle="Loans, credit cards, and financial obligations"
      action={<Btn onClick={openNew} icon="ti-plus">Add Debt</Btn>}>

      <div style={{ display:"grid", gridTemplateColumns: window.innerWidth <= 768 ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Total Outstanding" value={fmt(totalDebt)} color="var(--color-danger)" icon="ti-credit-card" />
        <MetricCard label="Total Paid" value={fmt(totalPaid)} color="var(--color-success)" icon="ti-check" />
        <MetricCard label="Monthly Obligations" value={fmt(totalMonthly)} color="var(--color-orange)" icon="ti-calendar" />
        <MetricCard label="Avg. Interest Rate" value={debts.length>0?(debts.reduce((s,d)=>s+d.rate,0)/debts.length).toFixed(1)+"%":"—"} color="var(--color-purple)" icon="ti-percentage" />
      </div>

      {showForm && (
        <Modal title={editing?"Edit Debt":"Add Debt"} onClose={cancel} width={680}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <Input label="Lender / Creditor" value={form.lender} onChange={e=>setForm(f=>({...f,lender:e.target.value}))} placeholder="BPI Credit Card" />
            <Select label="Type" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
              {Object.entries(typeLabels).map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </Select>
            <Input label="Original Amount (₱)" type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} />
            <Input label="Annual Interest Rate (%)" type="number" step="0.1" value={form.rate} onChange={e=>setForm(f=>({...f,rate:e.target.value}))} />
            <Input label="Due / End Date" type="date" value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))} />
            <Input label="Monthly Payment (₱)" type="number" value={form.monthlyPayment} onChange={e=>setForm(f=>({...f,monthlyPayment:e.target.value}))} />
            <Input label="Amount Already Paid (₱)" type="number" value={form.paid} onChange={e=>setForm(f=>({...f,paid:e.target.value}))} />
            <Input label="Loan Term (months)" type="number" value={form.termMonths} onChange={e=>setForm(f=>({...f,termMonths:e.target.value}))} placeholder="e.g. 24" />
          </div>
          <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", display:"block", marginBottom:16 }}>
            Notes
            <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} style={{ display:"block", marginTop:4, width:"100%", resize:"vertical" }} />
          </label>

          <Divider />
          <SectionTitle>Payment Schedule</SectionTitle>
          <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginBottom:10 }}>
            Generate a schedule from the term above, then edit any row's amount or date — handy for credit cards where the payment changes each month (e.g. ₱800, then ₱900, then ₱400).
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:10 }}>
            <Btn onClick={generateSchedule} variant="secondary" size="sm" icon="ti-wand">Generate Schedule</Btn>
            <Btn onClick={addInstallmentRow} variant="secondary" size="sm" icon="ti-plus">Add Installment</Btn>
          </div>
          {(form.installments||[]).length > 0 && (
            <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:220, overflow:"auto", marginBottom:16 }}>
              {form.installments.map((ins,i) => (
                <div key={ins.id} style={{ display:"grid", gridTemplateColumns:"24px 1fr 1fr 32px", gap:8, alignItems:"center" }}>
                  <div style={{ fontSize:11, color:"var(--color-text-tertiary)", textAlign:"right" }}>{i+1}</div>
                  <input type="date" value={ins.dueDate} onChange={e=>updateInstallmentRow(ins.id,"dueDate",e.target.value)} />
                  <input type="number" value={ins.amount} onChange={e=>updateInstallmentRow(ins.id,"amount",e.target.value)} placeholder="Amount" />
                  <button onClick={()=>removeInstallmentRow(ins.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--color-danger)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <i className="ti ti-x" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={save} icon="ti-check">{editing?"Update":"Save"} Debt</Btn>
            <Btn onClick={cancel} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      {paymentId && (
        <Modal title={`Record Payment — ${debts.find(d=>d.id===paymentId)?.lender}`} onClose={()=>setPaymentId(null)}>
          <Input label="Payment Amount (₱)" type="number" value={payAmount} onChange={e=>setPayAmount(e.target.value)} placeholder="0.00" style={{ marginBottom:16 }} />
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={savePayment} variant="danger" icon="ti-check">Record Payment</Btn>
            <Btn onClick={()=>setPaymentId(null)} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        {debts.map(d => {
          const outstanding = d.amount - d.paid;
          const pct = Math.round((d.paid/d.amount)*100);
          const daysToMaturity = getDaysOverdue(d.dueDate);
          const monthlyRate = d.rate/100/12;
          const months = 12;
          const payment = outstanding > 0 && monthlyRate > 0 ? outstanding * monthlyRate / (1 - Math.pow(1+monthlyRate,-months)) : 0;

          return (
            <Card key={d.id}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, marginBottom:4 }}>{d.lender}</div>
                  <Badge color={typeColors[d.type]||"gray"}>{typeLabels[d.type]||d.type}</Badge>
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <Btn onClick={()=>setSelectedDebt(d.id)} variant="secondary" size="sm" icon="ti-calendar-due">Schedule</Btn>
                  <Btn onClick={()=>openPayment(d.id)} variant="danger" size="sm">Pay</Btn>
                  <Btn onClick={()=>openEdit(d)} variant="ghost" size="sm" icon="ti-edit" />
                  <Btn onClick={()=>del(d.id)} variant="danger" size="sm" icon="ti-trash" />
                </div>
              </div>
              <div style={{ fontSize:24, fontWeight:800, color:"var(--color-danger)", marginBottom:4, letterSpacing:"-0.03em" }}>{fmt(outstanding)}</div>
              <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:12 }}>outstanding of {fmt(d.amount)}</div>
              <ProgressBar value={d.paid} max={d.amount} color="var(--color-success)" height={8} />
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"var(--color-text-tertiary)", marginTop:6, marginBottom:12 }}>
                <span>Paid: {fmt(d.paid)}</span>
                <span style={{ fontWeight:600, color:"var(--color-success)" }}>{pct}% paid off</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                <div style={{ background:"var(--color-bg-subtle)", borderRadius:"var(--radius-sm)", padding:"8px 10px" }}>
                  <div style={{ fontSize:9, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Rate</div>
                  <div style={{ fontSize:13, fontWeight:600, marginTop:2 }}>{d.rate}% pa</div>
                </div>
                <div style={{ background:"var(--color-bg-subtle)", borderRadius:"var(--radius-sm)", padding:"8px 10px" }}>
                  <div style={{ fontSize:9, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Monthly</div>
                  <div style={{ fontSize:13, fontWeight:600, marginTop:2 }}>{fmt(d.monthlyPayment||payment)}</div>
                </div>
                <div style={{ background:"var(--color-bg-subtle)", borderRadius:"var(--radius-sm)", padding:"8px 10px" }}>
                  <div style={{ fontSize:9, color:"var(--color-text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Maturity</div>
                  <div style={{ fontSize:13, fontWeight:600, marginTop:2, color: daysToMaturity>0?"var(--color-danger)":"var(--color-success)" }}>
                    {daysToMaturity > 0 ? `${Math.floor(daysToMaturity/30)}mo` : `${Math.abs(Math.floor(daysToMaturity/30))}mo left`}
                  </div>
                </div>
              </div>
              {d.notes && <div style={{ marginTop:10, fontSize:11, color:"var(--color-text-tertiary)", fontStyle:"italic" }}>{d.notes}</div>}
            </Card>
          );
        })}
      </div>

    </Page>
  );
}

function DebtDetail({ debt: d, typeLabels, typeColors, onBack, onPay, onToggleInstallment }) {
  const outstanding = d.amount - d.paid;
  const installments = d.installments || [];
  const paidInstallments = installments.filter(i=>i.paid).length;

  return (
    <div style={{ padding:"28px 32px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
        <Btn onClick={onBack} variant="ghost" icon="ti-arrow-left" size="sm">Back</Btn>
        <div style={{ flex:1 }}>
          <h1 style={{ fontSize:20, fontWeight:700, margin:0 }}>{d.lender}</h1>
          <div style={{ marginTop:4 }}><Badge color={typeColors[d.type]||"gray"} size="xs">{typeLabels[d.type]||d.type}</Badge></div>
        </div>
        {outstanding > 0 && <Btn onClick={onPay} icon="ti-cash" variant="danger">Record Payment</Btn>}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Original Amount" value={fmt(d.amount)} color="var(--color-accent)" icon="ti-cash" />
        <MetricCard label="Paid" value={fmt(d.paid)} color="var(--color-success)" icon="ti-check" />
        <MetricCard label="Outstanding" value={fmt(outstanding)} color={outstanding>0?"var(--color-danger)":"var(--color-success)"} icon="ti-clock" />
        <MetricCard label="Rate" value={`${d.rate}% pa`} color="var(--color-purple)" icon="ti-percentage" />
      </div>

      <Card style={{ marginBottom:16 }}>
        <SectionTitle>Payoff Progress</SectionTitle>
        <ProgressBar value={d.paid} max={d.amount} color="var(--color-success)" height={10} showLabel />
      </Card>

      {installments.length > 0 ? (
        <Card style={{ padding:0 }}>
          <div style={{ padding:"20px 24px 0" }}>
            <SectionTitle>Payment Schedule</SectionTitle>
            <div style={{ marginBottom:12 }}>
              <ProgressBar value={paidInstallments} max={installments.length} color="var(--color-success)" height={8} showLabel />
            </div>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:"var(--color-bg-subtle)", borderBottom:"1px solid var(--color-border)" }}>
                  {["Month","Due Date","Payment","Principal","Interest","Balance","Status",""].map(h=>(
                    <th key={h} style={{ padding:"8px 12px", textAlign: ["Due Date","Status",""].includes(h) ? "left" : "right", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:10, textTransform:"uppercase", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {installments.map(ins => (
                  <tr key={ins.id} style={{ borderBottom:"1px solid var(--color-border-subtle)", background: ins.paid ? "var(--color-success-subtle)" : getDaysOverdue(ins.dueDate)>0?"var(--color-danger-subtle)":undefined }}>
                    <td style={{ padding:"9px 12px", textAlign:"right", color:"var(--color-text-tertiary)", fontWeight:600 }}>{ins.month}</td>
                    <td style={{ padding:"9px 12px" }}>{ins.dueDate}</td>
                    <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:600 }}>{fmt(ins.amount)}</td>
                    <td style={{ padding:"9px 12px", textAlign:"right", color:"var(--color-success)" }}>{fmt(ins.principal)}</td>
                    <td style={{ padding:"9px 12px", textAlign:"right", color:"var(--color-danger)" }}>{fmt(ins.interest)}</td>
                    <td style={{ padding:"9px 12px", textAlign:"right", fontFamily:"var(--font-mono)" }}>{fmt(ins.balance)}</td>
                    <td style={{ padding:"9px 12px" }}>
                      <Badge color={ins.paid?"green":getDaysOverdue(ins.dueDate)>0?"red":"gray"} size="xs">
                        {ins.paid?"Paid":getDaysOverdue(ins.dueDate)>0?"Overdue":"Pending"}
                      </Badge>
                    </td>
                    <td style={{ padding:"9px 12px" }}>
                      <Btn onClick={()=>onToggleInstallment(ins.id)} variant={ins.paid?"secondary":"success"} size="sm">{ins.paid?"Undo":"Mark Paid"}</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <EmptyState icon="📅" title="No payment schedule" desc="Edit this loan and set a term (in months) to generate a full monthly payment schedule you can track month by month." />
      )}
    </div>
  );
}

// ─── BILLS ───────────────────────────────────────────────────────────────────
function Bills({ bills, setBills, accounts, session }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [form, setForm] = useState({name:"",category:"Electricity",amount:"",dueDay:"1",status:"unpaid",account:"a1",recurring:true,installments:[]});

  const blankForm = {name:"",category:"Electricity",amount:"",dueDay:"1",status:"unpaid",account:"a1",recurring:true,installments:[]};
  const openNew = () => { setForm(blankForm); setEditing(null); setShowForm(true); };
  const openEdit = (b) => { setForm({...b, amount:String(b.amount), dueDay:String(b.dueDay), account:b.account||"a1", installments:b.installments||[]}); setEditing(b.id); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); };
  const generateSchedule = () => {
    const amount = parseFloat(form.amount||0);
    const dueDay = parseInt(form.dueDay||0);
    if (!amount || !dueDay) return;
    if ((form.installments||[]).length > 0 && !window.confirm("This will replace the current payment schedule. Continue?")) return;
    const rows = generateBillSchedule({ dueDay, amount, monthsAhead: 12, startDate: today() });
    setForm(f => ({...f, installments: rows}));
  };
  const addInstallmentRow = () => setForm(f => ({...f, installments:[...(f.installments||[]), {id:uid(), dueDate:today(), amount:parseFloat(f.amount)||0, paid:false, paidDate:null}]}));
  const updateInstallmentRow = (id, field, value) => setForm(f => ({...f, installments: f.installments.map(i => i.id===id ? {...i, [field]: field==="amount"?parseFloat(value)||0:value} : i)}));
  const removeInstallmentRow = (id) => setForm(f => ({...f, installments: f.installments.filter(i => i.id!==id)}));
  const del = async (id) => { 
    if (window.confirm("Delete this bill?")) {
      await supabase.from('bills').delete().eq('id', id);
      setBills(bs => bs.filter(b => b.id!==id)); 
    }
  };
  const toggle = async (id) => {
    const bill = bills.find(b => b.id===id);
    const newStatus = bill.status==="paid" ? "unpaid" : "paid";
    await supabase.from('bills').update({ status: newStatus }).eq('id', id);
    setBills(bs => bs.map(b => b.id===id ? {...b, status: newStatus} : b));
  };
  const save = async () => {
    if (!form.name||!form.amount) return;
    const parsed = {...form, amount:parseFloat(form.amount), dueDay:parseInt(form.dueDay)};
    let installments = parsed.recurring ? (parsed.installments||[]) : [];
    if (parsed.recurring && installments.length===0) {
      installments = generateBillSchedule({ dueDay: parsed.dueDay, amount: parsed.amount, monthsAhead: 12, startDate: today() });
    }
    parsed.installments = installments;
    if (editing) {
      await supabase.from('bills').update(parsed).eq('id', editing);
      setBills(bs => bs.map(b => b.id===editing ? {...parsed, id:editing} : b));
    } else {
      const newBill = {id:uid(), ...parsed};
      await supabase.from('bills').insert([{...newBill, user_id: session.user.id}]);
      setBills(bs => [...bs, newBill]);
    }
    cancel();
  };

  const toggleBillInstallmentPaid = async (billId, insId) => {
    const bill = bills.find(b => b.id===billId);
    if (!bill) return;
    const inst = (bill.installments||[]).find(i => i.id===insId);
    if (!inst) return;
    const willBePaid = !inst.paid;
    const updatedInstallments = bill.installments.map(i => i.id===insId ? {...i, paid: willBePaid, paidDate: willBePaid ? today() : null} : i);
    await supabase.from('bills').update({ installments: updatedInstallments }).eq('id', billId);
    setBills(bs => bs.map(b => b.id===billId ? {...b, installments: updatedInstallments} : b));
  };

  const totalUnpaid = bills.filter(b=>b.status==="unpaid").reduce((s,b)=>s+b.amount,0);
  const totalPaid = bills.filter(b=>b.status==="paid").reduce((s,b)=>s+b.amount,0);
  const totalMonthly = bills.reduce((s,b)=>s+b.amount,0);

  if (selectedBill) {
    const b = bills.find(x=>x.id===selectedBill);
    if (!b) { setSelectedBill(null); return null; }
    return <BillDetail bill={b} onBack={()=>setSelectedBill(null)} onToggleInstallment={(insId)=>toggleBillInstallmentPaid(b.id, insId)} />;
  }

  return (
    <Page title="Bills & Subscriptions" subtitle="Recurring payments and monthly obligations"
      action={<Btn onClick={openNew} icon="ti-plus">Add Bill</Btn>}>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Total Monthly" value={fmt(totalMonthly)} color="var(--color-accent)" icon="ti-calendar-repeat" />
        <MetricCard label="Paid This Month" value={fmt(totalPaid)} color="var(--color-success)" icon="ti-check" />
        <MetricCard label="Still Due" value={fmt(totalUnpaid)} color="var(--color-warning)" icon="ti-clock" />
        <MetricCard label="Bills Count" value={bills.length} color="var(--color-purple)" icon="ti-receipt-2" />
      </div>

      {showForm && (
        <Modal title={editing?"Edit Bill":"Add Bill"} onClose={cancel} width={680}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <Input label="Bill Name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Netflix" />
            <Select label="Category" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
              {expenseCategories.map(c=><option key={c}>{c}</option>)}
            </Select>
            <Input label="Amount (₱)" type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} />
            <Input label="Due Day of Month" type="number" min="1" max="31" value={form.dueDay} onChange={e=>setForm(f=>({...f,dueDay:e.target.value}))} />
            <Select label="Payment Account" value={form.account} onChange={e=>setForm(f=>({...f,account:e.target.value}))}>
              {accounts.map(a=><option key={a.id} value={a.id}>{getAccountIcon(a.type)} {a.name}</option>)}
            </Select>
            <Select label="Status" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
            </Select>
          </div>

          {form.recurring && (
            <>
              <Divider />
              <SectionTitle>Payment Schedule</SectionTitle>
              <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginBottom:10 }}>
                Generate the next 12 months, then edit any row's amount or date — handy when the bill isn't the same every month (e.g. ₱800, then ₱900, then ₱400).
              </div>
              <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                <Btn onClick={generateSchedule} variant="secondary" size="sm" icon="ti-wand">Generate Schedule</Btn>
                <Btn onClick={addInstallmentRow} variant="secondary" size="sm" icon="ti-plus">Add Installment</Btn>
              </div>
              {(form.installments||[]).length > 0 && (
                <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:220, overflow:"auto", marginBottom:16 }}>
                  {form.installments.map((ins,i) => (
                    <div key={ins.id} style={{ display:"grid", gridTemplateColumns:"24px 1fr 1fr 32px", gap:8, alignItems:"center" }}>
                      <div style={{ fontSize:11, color:"var(--color-text-tertiary)", textAlign:"right" }}>{i+1}</div>
                      <input type="date" value={ins.dueDate} onChange={e=>updateInstallmentRow(ins.id,"dueDate",e.target.value)} />
                      <input type="number" value={ins.amount} onChange={e=>updateInstallmentRow(ins.id,"amount",e.target.value)} placeholder="Amount" />
                      <button onClick={()=>removeInstallmentRow(ins.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--color-danger)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <i className="ti ti-x" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={save} icon="ti-check">{editing?"Update":"Save"} Bill</Btn>
            <Btn onClick={cancel} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:14 }}>
        {[...bills].sort((a,b)=>a.dueDay-b.dueDay).map(b => {
          const daysLeft = b.dueDay - now.getDate();
          const isOverdue = daysLeft < 0 && b.status==="unpaid";
          const acct = accounts.find(a=>a.id===b.account);
          return (
            <div key={b.id} style={{
              background:"var(--color-bg-card)",
              border:`1px solid ${b.status==="paid"?"var(--color-success)":isOverdue?"var(--color-danger)":"var(--color-border)"}`,
              borderLeft:`4px solid ${b.status==="paid"?"var(--color-success)":isOverdue?"var(--color-danger)":"var(--color-warning)"}`,
              borderRadius:"var(--radius-lg)", padding:"18px 20px",
              boxShadow:"var(--shadow-sm)", transition:"var(--transition)"
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, marginBottom:4 }}>{b.name}</div>
                  <Badge color="gray" size="xs">{b.category}</Badge>
                  {acct && <div style={{ fontSize:10, color:"var(--color-text-tertiary)", marginTop:4 }}>via {acct.name}</div>}
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:20, fontWeight:800, letterSpacing:"-0.02em" }}>{fmt(b.amount)}</div>
                  <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:1 }}>day {b.dueDay}</div>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:12, fontWeight:500, color: b.status==="paid"?"var(--color-success)":isOverdue?"var(--color-danger)":"var(--color-warning)" }}>
                  {b.status==="paid" ? "✓ Paid" : isOverdue ? `${Math.abs(daysLeft)}d overdue` : `Due in ${daysLeft}d`}
                </span>
                <div style={{ display:"flex", gap:6 }}>
                  {(b.installments||[]).length > 0 && <Btn onClick={()=>setSelectedBill(b.id)} variant="secondary" size="sm" icon="ti-calendar-due" />}
                  <Btn onClick={()=>toggle(b.id)} variant={b.status==="paid"?"danger":"success"} size="sm">
                    {b.status==="paid"?"Mark Unpaid":"Mark Paid"}
                  </Btn>
                  <Btn onClick={()=>openEdit(b)} variant="ghost" size="sm" icon="ti-edit" />
                  <Btn onClick={()=>del(b.id)} variant="danger" size="sm" icon="ti-trash" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Page>
  );
}

function BillDetail({ bill: b, onBack, onToggleInstallment }) {
  const installments = b.installments || [];
  const paidCount = installments.filter(i=>i.paid).length;

  return (
    <div style={{ padding:"28px 32px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
        <Btn onClick={onBack} variant="ghost" icon="ti-arrow-left" size="sm">Back</Btn>
        <div style={{ flex:1 }}>
          <h1 style={{ fontSize:20, fontWeight:700, margin:0 }}>{b.name}</h1>
          <div style={{ marginTop:4 }}><Badge color="gray" size="xs">{b.category}</Badge></div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Amount" value={fmt(b.amount)} color="var(--color-accent)" icon="ti-cash" />
        <MetricCard label="Due Day" value={`Day ${b.dueDay}`} color="var(--color-warning)" icon="ti-calendar" />
        <MetricCard label="Paid Instances" value={`${paidCount} / ${installments.length}`} color="var(--color-success)" icon="ti-check" />
      </div>

      <Card style={{ padding:0 }}>
        <div style={{ padding:"20px 24px 0" }}>
          <SectionTitle>Upcoming Schedule</SectionTitle>
          <div style={{ marginBottom:12 }}>
            <ProgressBar value={paidCount} max={installments.length} color="var(--color-success)" height={8} showLabel />
          </div>
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"var(--color-bg-subtle)", borderBottom:"1px solid var(--color-border)" }}>
              {["#","Due Date","Amount","Status","Paid Date",""].map(h=>(
                <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {installments.map((ins,i) => (
              <tr key={ins.id} style={{ borderBottom:"1px solid var(--color-border-subtle)", background: ins.paid ? "var(--color-success-subtle)" : getDaysOverdue(ins.dueDate)>0?"var(--color-danger-subtle)":undefined }}>
                <td style={{ padding:"9px 12px", color:"var(--color-text-tertiary)", fontWeight:600 }}>{i+1}</td>
                <td style={{ padding:"9px 12px" }}>{ins.dueDate}</td>
                <td style={{ padding:"9px 12px", fontWeight:600 }}>{fmt(ins.amount)}</td>
                <td style={{ padding:"9px 12px" }}>
                  <Badge color={ins.paid?"green":getDaysOverdue(ins.dueDate)>0?"red":"gray"} size="xs">
                    {ins.paid?"Paid":getDaysOverdue(ins.dueDate)>0?"Overdue":"Pending"}
                  </Badge>
                </td>
                <td style={{ padding:"9px 12px", color:"var(--color-text-secondary)", fontSize:12 }}>{ins.paidDate||"—"}</td>
                <td style={{ padding:"9px 12px" }}>
                  <Btn onClick={()=>onToggleInstallment(ins.id)} variant={ins.paid?"secondary":"success"} size="sm">{ins.paid?"Undo":"Mark Paid"}</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ─── BUDGETS ──────────────────────────────────────────────────────────────────
function Budgets({ budgets, setBudgets, transactions, session }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({category:"Groceries", budget:""});
  const [drillCategory, setDrillCategory] = useState(null);

  const openNew = () => { setForm({category:"Groceries", budget:""}); setEditing(null); setShowForm(true); };
  const openEdit = (b) => { setForm({category:b.category, budget:String(b.budget)}); setEditing(b.id); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); };
 const del = async (id) => { 
    if (window.confirm("Delete this budget?")) {
      await supabase.from('budgets').delete().eq('id', id);
      setBudgets(bs => bs.filter(b => b.id!==id)); 
    }
  };

  const save = async () => {
    if (!form.budget) return;
    if (editing) {
      await supabase.from('budgets').update({ category: form.category, budget: parseFloat(form.budget) }).eq('id', editing);
      setBudgets(bs => bs.map(b => b.id===editing ? {...b, category:form.category, budget:parseFloat(form.budget)} : b));
    } else {
      const existing = budgets.find(b=>b.category===form.category);
      if (existing) {
        await supabase.from('budgets').update({ budget: parseFloat(form.budget) }).eq('id', existing.id);
        setBudgets(bs => bs.map(b => b.category===form.category ? {...b, budget:parseFloat(form.budget)} : b));
      } else {
        const newBudget = {id:uid(), category:form.category, budget:parseFloat(form.budget)};
        await supabase.from('budgets').insert([{...newBudget, user_id: session.user.id}]);
        setBudgets(bs => [...bs, newBudget]);
      }
    }
    cancel();
  };

  const actuals = useMemo(() => {
    const m = {};
    transactions.filter(t=>t.type==="expense" && t.category).forEach(t => { m[t.category]=(m[t.category]||0)+t.amount; });
    return m;
  }, [transactions]);

  const totalBudget = budgets.reduce((s,b)=>s+b.budget,0);
  const totalActual = budgets.reduce((s,b)=>s+(actuals[b.category]||0),0);
  const totalVariance = totalBudget - totalActual;

  const drillTx = drillCategory ? transactions.filter(t=>t.type==="expense" && t.category===drillCategory) : [];

  const barData = budgets.map(b => ({
    name: b.category.length > 12 ? b.category.slice(0,12)+"…" : b.category,
    fullName: b.category,
    budget: b.budget,
    actual: actuals[b.category]||0,
    variance: b.budget - (actuals[b.category]||0),
  }));

  return (
    <Page title="Budgets" subtitle={`${thisMonth} ${thisYear} — Planned vs Actual`}
      action={<Btn onClick={openNew} icon="ti-plus">Set Budget</Btn>}>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Total Budget" value={fmt(totalBudget)} color="var(--color-accent)" icon="ti-target" />
        <MetricCard label="Total Spent" value={fmt(totalActual)} color="var(--color-orange)" icon="ti-receipt" />
        <MetricCard label="Total Variance" value={fmt(Math.abs(totalVariance))} color={totalVariance>=0?"var(--color-success)":"var(--color-danger)"} icon="ti-arrows-diff" />
        <MetricCard label="Budget Utilization" value={totalBudget>0?((totalActual/totalBudget)*100).toFixed(1)+"%":"—"} color="var(--color-purple)" icon="ti-percentage" />
      </div>

      {showForm && (
        <Modal title={editing?"Edit Budget":"Set Budget"} onClose={cancel}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
            <Select label="Category" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} disabled={!!editing}>
              {expenseCategories.map(c=><option key={c}>{c}</option>)}
            </Select>
            <Input label="Monthly Budget (₱)" type="number" value={form.budget} onChange={e=>setForm(f=>({...f,budget:e.target.value}))} placeholder="0" />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={save} icon="ti-check">{editing?"Update":"Save"} Budget</Btn>
            <Btn onClick={cancel} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:20 }}>
        <Card className="chart-card">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
            <div>
              <SectionTitle>Budget vs Actual</SectionTitle>
              <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:2 }}>Click any bar to inspect transactions</div>
            </div>
            <div style={{ display:"flex", gap:12 }}>
              {[{label:"Budget",color:"#2563EB"},{label:"Actual",color:"#EA580C"}].map(l=>(
                <div key={l.label} style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{ width:10, height:10, borderRadius:3, background:l.color }} />
                  <span style={{ fontSize:11, color:"var(--color-text-secondary)", fontWeight:500 }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={290}>
            <BarChart data={barData} barGap={3} onClick={d=>d?.activePayload?.[0] && setDrillCategory(d.activePayload[0].payload.fullName)}>
              <defs>
                <linearGradient id="budgetGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0.3}/>
                </linearGradient>
                <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EA580C" stopOpacity={0.9}/>
                  <stop offset="100%" stopColor="#EA580C" stopOpacity={0.5}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 6" stroke="var(--color-border)" vertical={false} />
              <XAxis dataKey="name" tick={{fontSize:10, fill:"var(--color-text-tertiary)"}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize:10, fill:"var(--color-text-tertiary)"}} tickFormatter={v=>"₱"+v/1000+"K"} axisLine={false} tickLine={false} width={44} />
              <Tooltip
                formatter={(v,n)=>[fmt(v),n]}
                contentStyle={{ borderRadius:12, border:"1px solid var(--color-border)", fontSize:12, boxShadow:"0 8px 24px rgba(15,17,23,0.10)", padding:"10px 14px" }}
                cursor={{ fill:"var(--color-bg-subtle)", radius:4 }}
              />
              <Bar dataKey="budget" name="Budget" fill="url(#budgetGrad)" radius={[5,5,0,0]} barSize={18} />
              <Bar dataKey="actual" name="Actual" fill="url(#actualGrad)" radius={[5,5,0,0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionTitle>Variance Summary</SectionTitle>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {budgets.map(b => {
              const actual = actuals[b.category]||0;
              const variance = b.budget - actual;
              const over = variance < 0;
              return (
                <div key={b.id} style={{ padding:"8px 0", borderBottom:"1px solid var(--color-border-subtle)" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                    <span style={{ fontSize:12, fontWeight:500 }}>{b.category}</span>
                    <span style={{ fontSize:12, fontWeight:700, color: over?"var(--color-danger)":"var(--color-success)" }}>
                      {over?"-":"+"}{ fmt(Math.abs(variance))}
                    </span>
                  </div>
                  <ProgressBar value={actual} max={b.budget} color={over?"var(--color-danger)":"var(--color-success)"} height={4} />
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {drillCategory && (
        <Card style={{ marginBottom:20, borderLeft:"4px solid var(--color-accent)" }}>
          <SectionTitle action={<Btn size="sm" variant="ghost" onClick={()=>setDrillCategory(null)}>× Close</Btn>}>
            Drill-down: {drillCategory}
          </SectionTitle>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"1px solid var(--color-border)" }}>
                {["Date","Description","Amount"].map(h=>(
                  <th key={h} style={{ padding:"8px 12px", textAlign: h==="Amount"?"right":"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:11, textTransform:"uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drillTx.map(t=>(
                <tr key={t.id} style={{ borderBottom:"1px solid var(--color-border-subtle)" }}>
                  <td style={{ padding:"8px 12px", color:"var(--color-text-secondary)", fontSize:12 }}>{t.date}</td>
                  <td style={{ padding:"8px 12px", fontWeight:500 }}>{t.desc}</td>
                  <td style={{ padding:"8px 12px", textAlign:"right", fontWeight:700, color:"var(--color-danger)" }}>-{fmt(t.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding:"10px 12px", background:"var(--color-bg-subtle)", borderRadius:"var(--radius-sm)", marginTop:8, display:"flex", justifyContent:"space-between", fontSize:13 }}>
            <span style={{ fontWeight:600 }}>Total Spent</span>
            <span style={{ fontWeight:700, color:"var(--color-danger)" }}>-{fmt(drillTx.reduce((s,t)=>s+t.amount,0))}</span>
          </div>
        </Card>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:14 }}>
        {budgets.map(b => {
          const actual = actuals[b.category] || 0;
          const pct = Math.min(120, Math.round((actual/b.budget)*100));
          const over = actual > b.budget;
          const variance = b.budget - actual;
          return (
            <Card key={b.id} style={{ cursor:"pointer", borderLeft:`4px solid ${over?"var(--color-danger)":"var(--color-success)"}` }}
              onClick={()=>setDrillCategory(b.category)}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700 }}>{b.category}</div>
                  <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:2 }}>Budget: {fmt(b.budget)}/mo</div>
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <Badge color={over?"red":"green"} size="xs">{over?"Over budget":"On track"}</Badge>
                  <Btn onClick={e=>{e.stopPropagation();openEdit(b);}} variant="ghost" size="sm" icon="ti-edit" />
                  <Btn onClick={e=>{e.stopPropagation();del(b.id);}} variant="danger" size="sm" icon="ti-trash" />
                </div>
              </div>
              <div style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
                  <span style={{ fontWeight:600, color: over?"var(--color-danger)":"var(--color-text-primary)" }}>Spent: {fmt(actual)}</span>
                  <span style={{ fontWeight:700, color: over?"var(--color-danger)":"var(--color-success)" }}>
                    {pct}%
                  </span>
                </div>
                <ProgressBar value={actual} max={b.budget} color={over?"var(--color-danger)":pct>75?"var(--color-warning)":"var(--color-success)"} height={8} />
              </div>
              <div style={{ fontSize:12, color: over?"var(--color-danger)":"var(--color-success)", fontWeight:600 }}>
                {over ? `${fmt(Math.abs(variance))} over budget` : `${fmt(variance)} remaining`}
              </div>
            </Card>
          );
        })}
      </div>
    </Page>
  );
}

// ─── GOALS ───────────────────────────────────────────────────────────────────
function Goals({ goals, setGoals, monthlySavings, session }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({name:"",targetAmount:"",savedAmount:"",targetDate:"",icon:"🎯",color:"#2563EB"});

  const blankForm = {name:"",targetAmount:"",savedAmount:"",targetDate:"",icon:"🎯",color:"#2563EB"};
  const openNew = () => { setForm(blankForm); setEditing(null); setShowForm(true); };
  const openEdit = (g) => { setForm({...g, targetAmount:String(g.targetAmount), savedAmount:String(g.savedAmount)}); setEditing(g.id); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); };
  const del = async (id) => { 
    if (window.confirm("Delete this goal?")) {
      await supabase.from('goals').delete().eq('id', id);
      setGoals(gs => gs.filter(g => g.id!==id)); 
    }
  };
  const save = async () => {
    if (!form.name||!form.targetAmount) return;
    const parsed = {...form, targetAmount:parseFloat(form.targetAmount), savedAmount:parseFloat(form.savedAmount||0)};
    if (editing) {
      await supabase.from('goals').update({
        name: parsed.name, target_amount: parsed.targetAmount,
        saved_amount: parsed.savedAmount, target_date: parsed.targetDate,
        icon: parsed.icon, color: parsed.color
      }).eq('id', editing);
      setGoals(gs => gs.map(g => g.id===editing ? {...parsed, id:editing} : g));
    } else {
      const newGoal = {id:uid(), ...parsed};
      await supabase.from('goals').insert([{
        id: newGoal.id, name: newGoal.name, target_amount: newGoal.targetAmount,
        saved_amount: newGoal.savedAmount, target_date: newGoal.targetDate,
        icon: newGoal.icon, color: newGoal.color, user_id: session.user.id
      }]);
      setGoals(gs => [...gs, newGoal]);
    }
    cancel();
  };

  const emojiOptions = ["🎯","✈️","🏠","💻","🚗","💰","🛡️","🎓","💍","🌴","📱","🏥","📈"];

  return (
    <Page title="Financial Goals" subtitle="Save with intention — track your progress"
      action={<Btn onClick={openNew} icon="ti-plus">New Goal</Btn>}>

      {showForm && (
        <Modal title={editing?"Edit Goal":"New Goal"} onClose={cancel}>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", marginBottom:6 }}>Icon</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {emojiOptions.map(e=>(
                <button key={e} onClick={()=>setForm(f=>({...f,icon:e}))} style={{ fontSize:20, background:form.icon===e?"var(--color-accent-subtle)":"var(--color-bg-subtle)", border:`1px solid ${form.icon===e?"var(--color-accent)":"var(--color-border)"}`, borderRadius:"var(--radius-sm)", padding:"6px 8px", cursor:"pointer" }}>{e}</button>
              ))}
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
            <Input label="Goal Name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Emergency Fund" />
            <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)" }}>
              Color
              <input type="color" value={form.color} onChange={e=>setForm(f=>({...f,color:e.target.value}))} style={{ display:"block", marginTop:4, width:"100%", height:36, border:"1px solid var(--color-border)", borderRadius:"var(--radius-sm)", cursor:"pointer", padding:2 }} />
            </label>
            <Input label="Target Amount (₱)" type="number" value={form.targetAmount} onChange={e=>setForm(f=>({...f,targetAmount:e.target.value}))} />
            <Input label="Amount Saved (₱)" type="number" value={form.savedAmount} onChange={e=>setForm(f=>({...f,savedAmount:e.target.value}))} />
            <Input label="Target Date" type="date" value={form.targetDate} onChange={e=>setForm(f=>({...f,targetDate:e.target.value}))} />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={save} icon="ti-check">{editing?"Update":"Create"} Goal</Btn>
            <Btn onClick={cancel} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:16 }}>
        {goals.map(g => {
          const pct = Math.min(100, g.targetAmount > 0 ? (g.savedAmount/g.targetAmount)*100 : 0);
          const remaining = g.targetAmount - g.savedAmount;
          const monthsToTarget = monthlySavings > 0 && remaining > 0 ? Math.ceil(remaining/monthlySavings) : null;
          const daysLeft = g.targetDate ? Math.floor((new Date(g.targetDate) - new Date()) / 86400000) : null;
          const onTrack = monthsToTarget !== null && daysLeft !== null && (monthsToTarget * 30) <= daysLeft;
          // SVG arc progress ring
          const R = 44, C = 2*Math.PI*R, filled = C * (pct/100);
          return (
            <div key={g.id} className="chart-card" style={{
              background:"var(--color-bg-card)",
              border:"1px solid var(--color-border)",
              borderRadius:"var(--radius-xl)",
              padding:"22px 22px 18px",
              boxShadow:"var(--shadow-md)",
              overflow:"hidden", position:"relative"
            }}>
              {/* Subtle color wash */}
              <div style={{ position:"absolute", top:0, left:0, right:0, height:4, background:`linear-gradient(90deg,${g.color},${g.color}66)` }} />
              <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
                {/* Ring */}
                <div style={{ position:"relative", flexShrink:0, width:100, height:100 }}>
                  <svg width={100} height={100} style={{ transform:"rotate(-90deg)" }}>
                    <circle cx={50} cy={50} r={R} fill="none" stroke={g.color+"18"} strokeWidth={9} />
                    <circle cx={50} cy={50} r={R} fill="none" stroke={g.color} strokeWidth={9}
                      strokeDasharray={`${filled} ${C}`} strokeLinecap="round"
                      style={{ transition:"stroke-dasharray 0.8s ease" }} />
                  </svg>
                  <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                    <div style={{ fontSize:20 }}>{g.icon}</div>
                    <div style={{ fontSize:11, fontWeight:800, color:g.color, marginTop:2 }}>{pct.toFixed(0)}%</div>
                  </div>
                </div>
                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                    <div style={{ fontSize:15, fontWeight:800, letterSpacing:"-0.02em" }}>{g.name}</div>
                    <div style={{ display:"flex", gap:4 }}>
                      <Btn onClick={()=>openEdit(g)} variant="ghost" size="sm" icon="ti-edit" />
                      <Btn onClick={()=>del(g.id)} variant="danger" size="sm" icon="ti-trash" />
                    </div>
                  </div>
                  <div style={{ fontSize:22, fontWeight:800, color:g.color, letterSpacing:"-0.03em", lineHeight:1 }}>{fmt(g.savedAmount)}</div>
                  <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:2, marginBottom:10 }}>of {fmt(g.targetAmount)}</div>
                  <div style={{ height:6, background:g.color+"18", borderRadius:99, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${g.color},${g.color}CC)`, borderRadius:99, transition:"width 0.8s ease" }} />
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
                    <span style={{ fontSize:10, color:"var(--color-text-tertiary)" }}>{fmt(remaining)} left</span>
                    {g.targetDate && <span style={{ fontSize:10, color:"var(--color-text-tertiary)" }}>{daysLeft}d remaining</span>}
                  </div>
                </div>
              </div>
              {monthsToTarget !== null && (
                <div style={{ marginTop:14, padding:"9px 12px", background:g.color+"0E", borderRadius:"var(--radius-md)", borderLeft:`3px solid ${g.color}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:11, color:"var(--color-text-secondary)" }}>At current savings rate</span>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <strong style={{ fontSize:13, color:g.color, fontWeight:800 }}>{monthsToTarget} mo</strong>
                    {daysLeft !== null && <Badge color={onTrack?"green":"amber"} size="xs">{onTrack?"On Track":"Behind"}</Badge>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {goals.length===0 && <EmptyState icon="🎯" title="No goals yet" desc="Set financial targets and track your progress toward them." action={<Btn onClick={openNew} icon="ti-plus">Create First Goal</Btn>} />}
      </div>
    </Page>
  );
}

// ─── ASSETS ──────────────────────────────────────────────────────────────────
function Assets({ assets, setAssets, session }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({name:"",category:"Vehicle",value:"",notes:""});

  const blankForm = {name:"",category:"Vehicle",value:"",notes:""};
  const openNew = () => { setForm(blankForm); setEditing(null); setShowForm(true); };
  const openEdit = (a) => { setForm({...a, value:String(a.value)}); setEditing(a.id); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); };
  const del = async (id) => { 
    if (window.confirm("Delete this asset?")) {
      await supabase.from('assets').delete().eq('id', id);
      setAssets(as => as.filter(a => a.id!==id)); 
    }
  };
  const save = async () => {
    if (!form.name||!form.value) return;
    const parsed = {...form, value:parseFloat(form.value)};
    if (editing) {
      await supabase.from('assets').update({
        name: parsed.name, category: parsed.category,
        value: parsed.value, notes: parsed.notes
      }).eq('id', editing);
      setAssets(as => as.map(a => a.id===editing ? {...parsed, id:editing} : a));
    } else {
      const newAsset = {id:uid(), ...parsed};
      await supabase.from('assets').insert([{...newAsset, user_id: session.user.id}]);
      setAssets(as => [...as, newAsset]);
    }
    cancel();
  };

  const totalValue = assets.reduce((s,a)=>s+a.value,0);
  const byCategory = useMemo(() => {
    const m = {};
    assets.forEach(a => { m[a.category]=(m[a.category]||0)+a.value; });
    return Object.entries(m).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value);
  }, [assets]);

  const assetCategories = ["Vehicle","Laptop","Phone","Mutual Funds","Stocks","Crypto","Real Estate","Jewelry","Equipment","Other"];

  return (
    <Page title="Assets" subtitle="Track the value of everything you own"
      action={<Btn onClick={openNew} icon="ti-plus">Add Asset</Btn>}>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        <MetricCard label="Total Asset Value" value={fmt(totalValue)} color="var(--color-success)" icon="ti-building" />
        <MetricCard label="Asset Count" value={assets.length} color="var(--color-accent)" icon="ti-list" />
        <MetricCard label="Highest Value" value={assets.length?fmt(Math.max(...assets.map(a=>a.value))):"—"} color="var(--color-purple)" icon="ti-crown" />
        <MetricCard label="Categories" value={byCategory.length} color="var(--color-warning)" icon="ti-category" />
      </div>

      {showForm && (
        <Modal title={editing?"Edit Asset":"Add Asset"} onClose={cancel}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <Input label="Asset Name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Toyota Vios 2022" />
            <Select label="Category" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
              {assetCategories.map(c=><option key={c}>{c}</option>)}
            </Select>
            <Input label="Current Value (₱)" type="number" value={form.value} onChange={e=>setForm(f=>({...f,value:e.target.value}))} placeholder="0" />
          </div>
          <label style={{ fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", display:"block", marginBottom:16 }}>
            Notes
            <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} style={{ display:"block", marginTop:4, width:"100%", resize:"vertical" }} />
          </label>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={save} icon="ti-check">{editing?"Update":"Save"} Asset</Btn>
            <Btn onClick={cancel} variant="secondary">Cancel</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:20 }}>
        <Card style={{ padding:0 }}>
          <div style={{ padding:"16px 20px", borderBottom:"1px solid var(--color-border)" }}>
            <SectionTitle>Asset Inventory</SectionTitle>
          </div>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:"var(--color-bg-subtle)", borderBottom:"1px solid var(--color-border)" }}>
                {["Asset","Category","Value","% of Total",""].map(h=>(
                  <th key={h} style={{ padding:"10px 16px", textAlign: h==="Value"||h==="% of Total"?"right":"left", fontWeight:600, color:"var(--color-text-tertiary)", fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...assets].sort((a,b)=>b.value-a.value).map(a => (
                <tr key={a.id} style={{ borderBottom:"1px solid var(--color-border-subtle)" }}>
                  <td style={{ padding:"12px 16px" }}>
                    <div style={{ fontWeight:600 }}>{a.name}</div>
                    {a.notes && <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:1 }}>{a.notes}</div>}
                  </td>
                  <td style={{ padding:"12px 16px" }}><Badge color="gray" size="xs">{a.category}</Badge></td>
                  <td style={{ padding:"12px 16px", textAlign:"right", fontWeight:700, fontSize:14 }}>{fmt(a.value)}</td>
                  <td style={{ padding:"12px 16px", textAlign:"right" }}>
                    <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:12, color:"var(--color-text-secondary)" }}>{((a.value/totalValue)*100).toFixed(1)}%</span>
                      <div style={{ width:60, height:4, background:"var(--color-border)", borderRadius:99, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${(a.value/totalValue)*100}%`, background:"var(--color-success)", borderRadius:99 }} />
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:"12px 16px" }}>
                    <div style={{ display:"flex", gap:6 }}>
                      <Btn onClick={()=>openEdit(a)} variant="ghost" size="sm" icon="ti-edit" />
                      <Btn onClick={()=>del(a.id)} variant="danger" size="sm" icon="ti-trash" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="chart-card">
          <SectionTitle>By Category</SectionTitle>
          <div style={{ position:"relative" }}>
            <ResponsiveContainer width="100%" height={210}>
              <PieChart>
                <Pie data={byCategory} cx="50%" cy="50%" innerRadius={55} outerRadius={82} dataKey="value" paddingAngle={3}>
                  {byCategory.map((e,i) => <Cell key={i} fill={PALETTE[i%PALETTE.length]} stroke="none" />)}
                </Pie>
                <Tooltip formatter={v=>fmt(v)} contentStyle={{ borderRadius:12, border:"1px solid var(--color-border)", fontSize:12, boxShadow:"0 8px 24px rgba(15,17,23,0.10)", padding:"10px 14px" }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", textAlign:"center", pointerEvents:"none" }}>
              <div style={{ fontSize:9, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em" }}>Total</div>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--color-text-primary)", letterSpacing:"-0.03em" }}>{fmt(totalValue)}</div>
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:1, marginTop:8 }}>
            {byCategory.map((e,i) => {
              const pct = totalValue>0?((e.value/totalValue)*100).toFixed(1):0;
              return (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 6px", borderRadius:7 }}>
                  <div style={{ width:9, height:9, borderRadius:"50%", background:PALETTE[i%PALETTE.length], flexShrink:0 }} />
                  <span style={{ flex:1, fontSize:11, color:"var(--color-text-secondary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{e.name}</span>
                  <span style={{ fontSize:11, fontWeight:700 }}>{fmt(e.value)}</span>
                  <span style={{ fontSize:10, color:"var(--color-text-tertiary)", minWidth:30, textAlign:"right" }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </Page>
  );
}

// ─── REPORTS ─────────────────────────────────────────────────────────────────
function Reports({ transactions, receivables, debts, accounts, assets, totalAssets, totalDebts, netWorth, totalLiquid, totalReceivables, totalAssetValues, monthlyIncome, monthlyExpense }) {
  const [activeReport, setActiveReport] = useState("pnl");

  const totalIncome = transactions.filter(t=>t.type==="income" && t.category).reduce((s,t)=>s+t.amount,0);
  const totalExpense = transactions.filter(t=>t.type==="expense" && t.category).reduce((s,t)=>s+t.amount,0);
  const netIncome = totalIncome - totalExpense;

  const expByCategory = useMemo(() => {
    const m = {};
    transactions.filter(t=>t.type==="expense" && t.category).forEach(t=>{m[t.category]=(m[t.category]||0)+t.amount;});
    return Object.entries(m).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value);
  }, [transactions]);

  const incByCategory = useMemo(() => {
    const m = {};
    transactions.filter(t=>t.type==="income" && t.category).forEach(t=>{m[t.category]=(m[t.category]||0)+t.amount;});
    return Object.entries(m).map(([name,value])=>({name,value}));
  }, [transactions]);

  const reportTabs = [
    {id:"pnl", label:"P&L Statement", icon:"ti-report-analytics"},
    {id:"cashflow", label:"Cash Flow", icon:"ti-arrows-exchange"},
    {id:"networth", label:"Net Worth", icon:"ti-trending-up"},
    {id:"balancesheet", label:"Balance Sheet", icon:"ti-scale"},
    {id:"spending", label:"Spending Analysis", icon:"ti-chart-donut"},
  ];

  return (
    <Page title="Financial Reports" subtitle="Professional accounting reports and analytics">
      {/* Report Tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:24, background:"var(--color-bg-subtle)", borderRadius:"var(--radius-lg)", padding:4 }}>
        {reportTabs.map(r => (
          <button key={r.id} onClick={()=>setActiveReport(r.id)} style={{
            display:"flex", alignItems:"center", gap:6, flex:1,
            padding:"8px 14px", borderRadius:"var(--radius-md)",
            border:"none", background: activeReport===r.id ? "var(--color-bg-card)" : "transparent",
            color: activeReport===r.id ? "var(--color-text-primary)" : "var(--color-text-secondary)",
            cursor:"pointer", fontSize:12, fontWeight: activeReport===r.id ? 600 : 400,
            boxShadow: activeReport===r.id ? "var(--shadow-sm)" : "none",
            transition:"var(--transition)", justifyContent:"center", whiteSpace:"nowrap"
          }}>
            <i className={`ti ${r.icon}`} style={{ fontSize:14 }} aria-hidden="true" />{r.label}
          </button>
        ))}
      </div>

      {activeReport==="pnl" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <Card>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h2 style={{ fontSize:16, fontWeight:700, margin:0 }}>Income Statement</h2>
              <Badge color="blue" size="xs">{thisMonth} {thisYear}</Badge>
            </div>
            <div style={{ marginBottom:16 }}>
              <SectionTitle>Income</SectionTitle>
              {incByCategory.map(i=><ReportLine key={i.name} label={i.name} value={i.value} indent />)}
              <ReportLine label="Total Income" value={totalIncome} bold />
            </div>
            <Divider />
            <div style={{ marginBottom:16 }}>
              <SectionTitle>Expenses</SectionTitle>
              {expByCategory.map(e=><ReportLine key={e.name} label={e.name} value={e.value} indent negative />)}
              <ReportLine label="Total Expenses" value={totalExpense} bold negative />
            </div>
            <Divider />
            <ReportLine label="Net Income (Savings)" value={netIncome} bold large />
          </Card>
          <Card className="chart-card">
            <SectionTitle>Income vs Expense Breakdown</SectionTitle>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={[{name:"Income",value:totalIncome},{name:"Expense",value:totalExpense},{name:"Net Savings",value:Math.abs(netIncome)}]} barSize={52}>
                <defs>
                  <linearGradient id="incG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#059669" stopOpacity={1}/><stop offset="100%" stopColor="#059669" stopOpacity={0.5}/></linearGradient>
                  <linearGradient id="expG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#DC2626" stopOpacity={1}/><stop offset="100%" stopColor="#DC2626" stopOpacity={0.5}/></linearGradient>
                  <linearGradient id="netG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={netIncome>=0?"#2563EB":"#EA580C"} stopOpacity={1}/><stop offset="100%" stopColor={netIncome>=0?"#2563EB":"#EA580C"} stopOpacity={0.5}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 6" stroke="var(--color-border)" vertical={false} />
                <XAxis dataKey="name" tick={{fontSize:11, fill:"var(--color-text-tertiary)"}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize:10, fill:"var(--color-text-tertiary)"}} tickFormatter={v=>"₱"+v/1000+"K"} axisLine={false} tickLine={false} width={44} />
                <Tooltip formatter={(v,n)=>[fmt(v),n]} contentStyle={{ borderRadius:12, border:"1px solid var(--color-border)", fontSize:12, boxShadow:"0 8px 24px rgba(15,17,23,0.10)", padding:"10px 14px" }} cursor={{ fill:"var(--color-bg-subtle)", radius:4 }} />
                <Bar dataKey="value" radius={[7,7,0,0]}>
                  <Cell fill="url(#incG)" />
                  <Cell fill="url(#expG)" />
                  <Cell fill="url(#netG)" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ marginTop:16 }}>
              <SectionTitle>Top Expense Categories</SectionTitle>
              {expByCategory.slice(0,5).map((e,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid var(--color-border-subtle)" }}>
                  <div style={{ width:22, height:22, borderRadius:6, background:PALETTE[i%PALETTE.length], display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:"#fff", flexShrink:0 }}>{i+1}</div>
                  <span style={{ flex:1, fontSize:12, fontWeight:500 }}>{e.name}</span>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:13, fontWeight:700 }}>{fmt(e.value)}</div>
                    <div style={{ fontSize:10, color:"var(--color-text-tertiary)" }}>{totalExpense>0?((e.value/totalExpense)*100).toFixed(1):0}%</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {activeReport==="balancesheet" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <Card>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h2 style={{ fontSize:16, fontWeight:700, margin:0 }}>Balance Sheet</h2>
              <Badge color="blue" size="xs">as of {thisMonth} {thisYear}</Badge>
            </div>
            <div style={{ marginBottom:16 }}>
              <SectionTitle>Assets</SectionTitle>
              <div style={{ paddingLeft:8, marginBottom:8 }}>
                <div style={{ fontSize:11, fontWeight:600, color:"var(--color-text-tertiary)", textTransform:"uppercase", marginBottom:6 }}>Current Assets</div>
                {accounts.map(a=><ReportLine key={a.id} label={a.name} value={a.balance} indent />)}
                <ReportLine label="Total Liquid Assets" value={totalLiquid} bold />
              </div>
              <div style={{ paddingLeft:8, marginBottom:8 }}>
                <div style={{ fontSize:11, fontWeight:600, color:"var(--color-text-tertiary)", textTransform:"uppercase", marginBottom:6 }}>Receivables</div>
                {receivables.map(r=><ReportLine key={r.id} label={r.borrower} value={r.principal-r.paid} indent />)}
                <ReportLine label="Total Receivables" value={totalReceivables} bold />
              </div>
              <div style={{ paddingLeft:8, marginBottom:8 }}>
                <div style={{ fontSize:11, fontWeight:600, color:"var(--color-text-tertiary)", textTransform:"uppercase", marginBottom:6 }}>Non-Current Assets</div>
                {assets.map(a=><ReportLine key={a.id} label={a.name} value={a.value} indent />)}
                <ReportLine label="Total Fixed Assets" value={totalAssetValues} bold />
              </div>
              <Divider />
              <ReportLine label="TOTAL ASSETS" value={totalAssets} bold large />
            </div>
          </Card>
          <Card>
            <div style={{ height:16 }} />
            <div style={{ marginBottom:16 }}>
              <SectionTitle>Liabilities & Equity</SectionTitle>
              <div style={{ paddingLeft:8, marginBottom:8 }}>
                <div style={{ fontSize:11, fontWeight:600, color:"var(--color-text-tertiary)", textTransform:"uppercase", marginBottom:6 }}>Liabilities</div>
                {debts.map(d=><ReportLine key={d.id} label={d.lender} value={d.amount-d.paid} indent negative />)}
                <ReportLine label="Total Liabilities" value={totalDebts} bold negative />
              </div>
              <Divider />
              <ReportLine label="NET WORTH (Equity)" value={netWorth} bold large />
            </div>
            <div style={{ marginTop:24 }}>
              <SectionTitle>Asset Composition</SectionTitle>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={[{name:"Liquid",value:totalLiquid},{name:"Receivables",value:totalReceivables},{name:"Fixed Assets",value:totalAssetValues}]} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                    <Cell fill="var(--chart-1)" /><Cell fill="var(--chart-4)" /><Cell fill="var(--chart-2)" />
                  </Pie>
                  <Tooltip formatter={v=>fmt(v)} contentStyle={{borderRadius:10, fontSize:12, background:"var(--color-bg-card)", border:"1px solid var(--color-border)", color:"var(--color-text-primary)"}} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}

      {activeReport==="cashflow" && (
        <Card>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
            <h2 style={{ fontSize:16, fontWeight:700, margin:0 }}>Cash Flow Statement</h2>
            <Badge color="blue" size="xs">{thisMonth} {thisYear}</Badge>
          </div>
          <div style={{ marginBottom:20 }}>
            <SectionTitle>Operating Activities</SectionTitle>
            <ReportLine label="Salary & Active Income" value={transactions.filter(t=>["Salary Income","Bonus Income","Commission Income"].includes(t.category)).reduce((s,t)=>s+t.amount,0)} indent />
            <ReportLine label="Side & Business Income" value={transactions.filter(t=>["Side Hustle Income","Business Income"].includes(t.category)).reduce((s,t)=>s+t.amount,0)} indent />
            <ReportLine label="Living Expenses" value={transactions.filter(t=>["Groceries","Dining Out","Electricity","Water","Internet","Rent","Mobile Phone","Coffee","Food Delivery"].includes(t.category)).reduce((s,t)=>s+t.amount,0)} indent negative />
            <ReportLine label="Transportation" value={transactions.filter(t=>["Fuel","Parking","Toll Fees","Vehicle Maintenance","Public Transportation"].includes(t.category)).reduce((s,t)=>s+t.amount,0)} indent negative />
            <ReportLine label="Subscriptions & Entertainment" value={transactions.filter(t=>["Subscriptions","Entertainment","Streaming"].includes(t.category)).reduce((s,t)=>s+t.amount,0)} indent negative />
            <Divider />
            <ReportLine label="Net Operating Cash Flow" value={totalIncome - totalExpense} bold />
          </div>
          <div style={{ marginBottom:20 }}>
            <SectionTitle>Investing Activities</SectionTitle>
            <ReportLine label="Interest from Loans" value={transactions.filter(t=>t.category==="Interest Income from Loans").reduce((s,t)=>s+t.amount,0)} indent />
            <ReportLine label="Dividend Income" value={transactions.filter(t=>t.category==="Dividend Income").reduce((s,t)=>s+t.amount,0)} indent />
            <Divider />
            <ReportLine label="Net Investing Cash Flow" value={transactions.filter(t=>["Interest Income from Loans","Dividend Income","Investment Income"].includes(t.category)).reduce((s,t)=>s+t.amount,0)} bold />
          </div>
          <div style={{ marginBottom:20 }}>
            <SectionTitle>Financing Activities</SectionTitle>
            <ReportLine label="Loan Repayments" value={debts.reduce((s,d)=>s+d.paid,0)} indent negative />
            <Divider />
            <ReportLine label="Net Financing Cash Flow" value={-debts.reduce((s,d)=>s+d.paid,0)} bold />
          </div>
          <div style={{ background:"var(--color-accent-subtle)", borderRadius:"var(--radius-md)", padding:"14px 18px" }}>
            <ReportLine label="NET CHANGE IN CASH" value={netIncome} bold large />
          </div>
        </Card>
      )}

      {activeReport==="networth" && (
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16 }}>
          <Card className="chart-card" style={{ background:"linear-gradient(160deg,#0F1117,#1A1F2E)", border:"none" }}>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.10em", marginBottom:4 }}>Net Worth Trend</div>
              <div style={{ fontSize:26, fontWeight:800, color:"#60A5FA", letterSpacing:"-0.03em" }}>{fmt(netWorth)}</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:3, display:"flex", gap:16 }}>
                {[{l:"Assets",c:"#34D399"},{l:"Liabilities",c:"#F87171"},{l:"Net Worth",c:"#60A5FA"}].map(x=>(
                  <span key={x.l} style={{ display:"flex", alignItems:"center", gap:4 }}>
                    <span style={{ width:8, height:8, background:x.c, borderRadius:"50%", display:"inline-block" }} />{x.l}
                  </span>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={[
                {month:"Jan",nw:380000,assets:720000,liabilities:340000},
                {month:"Feb",nw:415000,assets:750000,liabilities:335000},
                {month:"Mar",nw:455000,assets:785000,liabilities:330000},
                {month:"Apr",nw:490000,assets:815000,liabilities:325000},
                {month:"May",nw:520000,assets:840000,liabilities:320000},
                {month:"Jun",nw:netWorth,assets:totalAssets,liabilities:totalDebts},
              ]} margin={{top:4,right:4,left:0,bottom:0}}>
                <defs>
                  <linearGradient id="nwFill3" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.25}/><stop offset="100%" stopColor="#60A5FA" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 6" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="month" tick={{fontSize:10, fill:"rgba(255,255,255,0.3)"}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize:10, fill:"rgba(255,255,255,0.25)"}} tickFormatter={v=>"₱"+v/1000+"K"} axisLine={false} tickLine={false} width={46} />
                <Tooltip contentStyle={{ background:"#1E2435", border:"1px solid rgba(255,255,255,0.10)", borderRadius:12, fontSize:12, color:"#fff", padding:"10px 14px" }} formatter={(v,n)=>[fmt(v),n]} labelStyle={{ color:"rgba(255,255,255,0.5)", marginBottom:4 }} cursor={{ stroke:"rgba(255,255,255,0.1)" }} />
                <Area type="monotone" dataKey="assets" stroke="#34D399" fill="none" strokeWidth={2} strokeDasharray="5 3" name="Total Assets" dot={false} />
                <Area type="monotone" dataKey="liabilities" stroke="#F87171" fill="none" strokeWidth={2} strokeDasharray="5 3" name="Liabilities" dot={false} />
                <Area type="monotone" dataKey="nw" stroke="#60A5FA" fill="url(#nwFill3)" strokeWidth={2.5} name="Net Worth" dot={false} activeDot={{ r:5, fill:"#60A5FA", stroke:"#0F1117", strokeWidth:2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
          <Card>
            <SectionTitle>Net Worth Breakdown</SectionTitle>
            <ReportLine label="Total Assets" value={totalAssets} bold />
            <ReportLine label="Total Liabilities" value={totalDebts} negative />
            <div style={{ borderTop:"2px solid var(--color-border)", marginTop:10, paddingTop:12 }}>
              <ReportLine label="Net Worth" value={netWorth} bold large />
            </div>
            <div style={{ background:"var(--color-success-subtle)", borderRadius:"var(--radius-md)", padding:"14px 16px", marginTop:20 }}>
              <div style={{ fontSize:11, color:"var(--color-success)", textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:600 }}>YTD Growth</div>
              <div style={{ fontSize:24, fontWeight:800, color:"var(--color-success)", marginTop:4, letterSpacing:"-0.03em" }}>+{fmt(netWorth-380000)}</div>
              <div style={{ fontSize:12, color:"var(--color-success)", opacity:0.8, marginTop:4 }}>{(((netWorth-380000)/380000)*100).toFixed(1)}% since Jan {thisYear}</div>
            </div>
          </Card>
        </div>
      )}

      {activeReport==="spending" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <Card className="chart-card">
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
              <div>
                <SectionTitle>Spending Map</SectionTitle>
                <div style={{ fontSize:11, color:"var(--color-text-tertiary)", marginTop:2 }}>Tile size = share of total spending</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"var(--color-text-tertiary)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em" }}>Total Spent</div>
                <div style={{ fontSize:18, fontWeight:800, color:"var(--color-danger)", letterSpacing:"-0.03em" }}>{fmt(totalExpense)}</div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <Treemap
                data={expByCategory.slice(0,10).map((e,i)=>({...e,fill:PALETTE[i%PALETTE.length]}))}
                dataKey="value"
                aspectRatio={4/3}
                content={({x,y,width,height,name,value,fill})=>(
                  width > 40 && height > 28 ? (
                    <g>
                      <rect x={x+1} y={y+1} width={width-2} height={height-2} fill={fill} rx={6} />
                      <rect x={x+1} y={y+1} width={width-2} height={height-2} fill="rgba(0,0,0,0.08)" rx={6} />
                      {height > 44 && <text x={x+width/2} y={y+height/2-(width>80?8:4)} textAnchor="middle" fill="#fff" fontSize={width>100?11:9} fontWeight={700} style={{textShadow:"0 1px 3px rgba(0,0,0,0.5)"}}>{name?.length>14?name.slice(0,13)+"…":name}</text>}
                      {height > 56 && width > 60 && <text x={x+width/2} y={y+height/2+10} textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize={9} fontWeight={500}>{fmt(value)}</text>}
                    </g>
                  ) : width > 12 ? <g><rect x={x+1} y={y+1} width={width-2} height={height-2} fill={fill} rx={4} /></g> : <g/>
                )}
              />
            </ResponsiveContainer>
          </Card>
          <Card className="chart-card">
            <SectionTitle>Category Breakdown</SectionTitle>
            <div style={{ display:"flex", flexDirection:"column", gap:2, marginTop:6 }}>
              {expByCategory.slice(0,9).map((e,i)=>{
                const pct = totalExpense > 0 ? ((e.value/totalExpense)*100) : 0;
                return (
                  <div key={i} style={{ padding:"7px 0" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
                      <span style={{ display:"flex", alignItems:"center", gap:7, fontSize:12, fontWeight:500 }}>
                        <span style={{ width:10, height:10, borderRadius:3, background:PALETTE[i%PALETTE.length], display:"inline-block", flexShrink:0 }} />
                        {e.name}
                      </span>
                      <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                        <span style={{ fontSize:10, color:"var(--color-text-tertiary)" }}>{pct.toFixed(1)}%</span>
                        <span style={{ fontSize:12, fontWeight:700, fontFamily:"var(--font-mono)" }}>{fmt(e.value)}</span>
                      </div>
                    </div>
                    <div style={{ height:5, background:"var(--color-bg-subtle)", borderRadius:99, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${Math.min(100,pct)}%`, background:PALETTE[i%PALETTE.length], borderRadius:99, transition:"width 0.6s ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop:12, paddingTop:12, borderTop:"1.5px solid var(--color-border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:12, fontWeight:600, color:"var(--color-text-secondary)" }}>Total Expenses</span>
              <span style={{ fontSize:15, fontWeight:800, color:"var(--color-danger)", fontFamily:"var(--font-mono)" }}>{fmt(totalExpense)}</span>
            </div>
          </Card>
        </div>
      )}
    </Page>
  );
}

function ReportLine({ label, value, indent, negative, bold, large }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", paddingLeft: indent ? 16 : 0 }}>
      <span style={{ fontSize: large?15:13, fontWeight: bold?700:400, color: bold?"var(--color-text-primary)":"var(--color-text-secondary)" }}>{label}</span>
      <span style={{ fontSize: large?16:13, fontWeight: bold?700:500, fontFamily:"var(--font-mono)", color: negative ? (value>0?"var(--color-danger)":"var(--color-text-primary)") : value<0?"var(--color-danger)":"var(--color-text-primary)" }}>
        {negative && value>0 ? "(":""}{fmt(Math.abs(value))}{negative && value>0 ? ")":""}
      </span>
    </div>
  );
}